import React, { useRef, useEffect, useState } from "react";
import * as tf from "@tensorflow/tfjs";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import * as blazeface from "@tensorflow-models/blazeface";
import * as poseDetection from "@tensorflow-models/pose-detection";
import * as faceLandmarksDetection from "@tensorflow-models/face-landmarks-detection";
import "@tensorflow/tfjs-backend-webgl";

const UNIVERSAL_MOLD_SIZE = 800;
const UNIVERSAL_DECODE_EDGE = 2400; // browser may pre-shrink only above this

function srgb2d(canvas) {
  try {
    return canvas.getContext("2d", {
      colorSpace: "srgb",
      willReadFrequently: true,
    });
  } catch (err) {
    return canvas.getContext("2d", { willReadFrequently: true });
  }
}

// Integer area-average. Same input bytes -> same output bytes on every device.
function areaAverage(src, dst, sw, sh, dw, dh) {
  const s = src.data;
  const d = dst.data;
  for (let y = 0; y < dh; y++) {
    let y0 = Math.floor((y * sh) / dh);
    let y1 = Math.floor(((y + 1) * sh) / dh);
    if (y1 <= y0) y1 = y0 + 1;
    if (y0 >= sh) y0 = sh - 1;
    if (y1 > sh) y1 = sh;

    for (let x = 0; x < dw; x++) {
      let x0 = Math.floor((x * sw) / dw);
      let x1 = Math.floor(((x + 1) * sw) / dw);
      if (x1 <= x0) x1 = x0 + 1;
      if (x0 >= sw) x0 = sw - 1;
      if (x1 > sw) x1 = sw;

      let r = 0,
        g = 0,
        b = 0,
        n = 0;
      for (let yy = y0; yy < y1; yy++) {
        let row = (yy * sw + x0) * 4;
        for (let xx = x0; xx < x1; xx++) {
          r += s[row];
          g += s[row + 1];
          b += s[row + 2];
          n++;
          row += 4;
        }
      }
      const i = (y * dw + x) * 4;
      d[i] = (r / n + 0.5) | 0;
      d[i + 1] = (g / n + 0.5) | 0;
      d[i + 2] = (b / n + 0.5) | 0;
      d[i + 3] = 255;
    }
  }
}

async function createOrientedBitmap(source, extra) {
  try {
    return await createImageBitmap(source, {
      imageOrientation: "from-image",
      premultiplyAlpha: "none",
      colorSpaceConversion: "default",
      ...(extra || {}),
    });
  } catch (err) {
    return await createImageBitmap(source);
  }
}

async function decodeUniversalFrame(blob) {
  let bitmap = await createOrientedBitmap(blob);
  const longEdge = Math.max(bitmap.width, bitmap.height);

  // Only used to stay under the iOS canvas memory cap. Final size is still ours.
  if (longEdge > UNIVERSAL_DECODE_EDGE) {
    const pre = UNIVERSAL_DECODE_EDGE / longEdge;
    const shrunk = await createOrientedBitmap(bitmap, {
      resizeWidth: Math.max(1, Math.round(bitmap.width * pre)),
      resizeHeight: Math.max(1, Math.round(bitmap.height * pre)),
      resizeQuality: "high",
    });
    bitmap.close();
    bitmap = shrunk;
  }

  const scale = UNIVERSAL_MOLD_SIZE / Math.max(bitmap.width, bitmap.height);
  const targetW = Math.max(1, Math.round(bitmap.width * scale));
  const targetH = Math.max(1, Math.round(bitmap.height * scale));

  const srcCanvas = document.createElement("canvas");
  srcCanvas.width = bitmap.width;
  srcCanvas.height = bitmap.height;
  srgb2d(srcCanvas).drawImage(bitmap, 0, 0);
  const srcData = srgb2d(srcCanvas).getImageData(
    0,
    0,
    bitmap.width,
    bitmap.height
  );
  bitmap.close();

  const frame = document.createElement("canvas");
  frame.width = targetW;
  frame.height = targetH;
  const fctx = srgb2d(frame);
  const dstData = fctx.createImageData(targetW, targetH);
  areaAverage(
    srcData,
    dstData,
    srcCanvas.width,
    srcCanvas.height,
    targetW,
    targetH
  );
  fctx.putImageData(dstData, 0, 0);

  // Kits ask for img.naturalWidth. A canvas can stand in for the <img>.
  frame.naturalWidth = targetW;
  frame.naturalHeight = targetH;
  return frame;
}

let universalBackendLock = null;
function lockUniversalBackend() {
  if (!universalBackendLock) {
    universalBackendLock = (async () => {
      tf.env().set("WEBGL_FORCE_F16_TEXTURES", false);
      tf.env().set("WEBGL_RENDER_FLOAT32_CAPABLE", true);
      tf.env().set("WEBGL_PACK", false);
      await tf.setBackend("cpu");
      await tf.ready();
      if (tf.getBackend() !== "cpu") {
        throw new Error("CPU backend did not lock, got " + tf.getBackend());
      }
    })();
  }
  return universalBackendLock;
}

const BubbleDisplayImg = ({
  src,
  alt = "",
  showOcancer = false,
  activeKit = "none",
  onProcessed,
  connectedImages = [],
}) => {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [imageError, setImageError] = useState(false);

  // NEW STATE: Holds the final merged image for mobile saving
  const [mergedImgUrl, setMergedImgUrl] = useState(null);

  // const imgRef = useRef(null);
  // const canvasRef = useRef(null);
  // const [imageLoaded, setImageLoaded] = useState(false);
  // const [isDetecting, setIsDetecting] = useState(false);
  // const [imageError, setImageError] = useState(false);

  // // Holds the final merged image for mobile saving
  // const [mergedImgUrl, setMergedImgUrl] = useState(null);

  // NEW: State to hold the secure Blob URL
  const [secureBlobUrl, setSecureBlobUrl] = useState(null);

  // NEW: Fetch the image as raw data to bypass the CORS cache
  const frameRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    const load = async () => {
      setFrameReady(false);
      setImageError(false);
      setMergedImgUrl(null);
      frameRef.current = null;
      setSecureBlobUrl(null);
      if (!src) return;

      const response = await fetch(src, {
        cache: "no-store",
        headers: { Accept: "image/jpeg,image/png;q=0.8,*/*;q=0.1" },
      });
      if (!response.ok)
        throw new Error("Image fetch failed: " + response.status);

      const frame = await decodeUniversalFrame(await response.blob());
      const png = await new Promise((resolve) =>
        frame.toBlob(resolve, "image/png")
      );
      objectUrl = URL.createObjectURL(png);

      if (cancelled) {
        URL.revokeObjectURL(objectUrl);
        return;
      }
      frameRef.current = frame;
      setSecureBlobUrl(objectUrl);
      setFrameReady(true);
    };

    load().catch((err) => {
      console.error("Universal frame failed:", err);
      if (!cancelled) setImageError(true);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const drawCanvas = async () => {
    const img = frameRef.current;
    const canvas = canvasRef.current;

    if (!img || !canvas || !frameReady || imageError || !img.width) {
      setIsDetecting(false);
      return;
    }

    const ctx = srgb2d(canvas);
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = false;

    if (activeKit === "none") {
      setMergedImgUrl(null);
      if (showOcancer) {
        ctx.strokeStyle = "#3400ff";
        ctx.lineWidth = Math.max(12, canvas.width * 0.035);
        const padding = canvas.width * 0.12;
        ctx.beginPath();
        ctx.moveTo(padding, canvas.height - padding * 0.6);
        ctx.lineTo(padding, padding);
        ctx.lineTo(canvas.width - padding, padding);
        ctx.lineTo(canvas.width - padding, canvas.height - padding * 0.6);
        ctx.stroke();
        onProcessed?.(canvas.toDataURL("image/png"));
      }
      return;
    }

    setIsDetecting(true);
    try {
      await lockUniversalBackend();
      // kits continue here, unchanged
      // Force Apple WebGL to stop downgrading our 32-bit math into 16-bit
      tf.env().set("WEBGL_FORCE_F16_TEXTURES", false);
      tf.env().set("WEBGL_RENDER_FLOAT32_CAPABLE", true);
      tf.env().set("WEBGL_PACK", false);
      await tf.ready();

      // ==========================================
      // 1. HUMAN KIT
      // ==========================================
      if (activeKit === "human") {
        const poseModel = poseDetection.SupportedModels.MoveNet;
        const poseDetector = await poseDetection.createDetector(poseModel);
        const poses = await poseDetector.estimatePoses(img);

        ctx.strokeStyle = "#4d00ff";
        ctx.lineWidth = 3;

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(img);
        ctx.fillStyle = "#4d00ff";
        const { TRIANGULATION } = faceLandmarksDetection.util;

        faces.forEach((face) => {
          const keypoints = face.keypoints;
          const scaledKeypoints = keypoints.map((kp) => ({
            x: kp.x * (canvas.width / img.naturalWidth),
            y: kp.y * (canvas.height / img.naturalHeight),
          }));

          ctx.strokeStyle = "#4d00ff";
          ctx.lineWidth = 0.5;
          for (let i = 0; i < TRIANGULATION.length; i += 3) {
            const point1 = scaledKeypoints[TRIANGULATION[i]];
            const point2 = scaledKeypoints[TRIANGULATION[i + 1]];
            const point3 = scaledKeypoints[TRIANGULATION[i + 2]];

            ctx.beginPath();
            ctx.moveTo(point1.x, point1.y);
            ctx.lineTo(point2.x, point2.y);
            ctx.lineTo(point3.x, point3.y);
            ctx.closePath();
            ctx.stroke();
          }
        });
      }

      // ==========================================
      // 2. HOME KIT
      // ==========================================
      if (activeKit === "home") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        let minX = canvas.width,
          maxX = 0,
          minY = canvas.height,
          maxY = 0;
        let foundDoor = false;

        for (let y = canvas.height * 0.1; y < canvas.height * 0.9; y += 15) {
          for (let x = canvas.width * 0.1; x < canvas.width * 0.9; x += 15) {
            const idx = (Math.floor(y) * canvas.width + Math.floor(x)) * 4;
            const r = imageData.data[idx];
            const g = imageData.data[idx + 1];
            const b = imageData.data[idx + 2];

            if ((b > r + 15 && b > g + 15) || (r > 35 && g > 35 && b < 25)) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
              foundDoor = true;
            }
          }
        }

        if (foundDoor) {
          ctx.strokeStyle = "#9932CC";
          ctx.lineWidth = Math.max(canvas.width / 100, 8);
          ctx.beginPath();
          ctx.moveTo(minX, maxY);
          ctx.lineTo(minX, minY);
          ctx.lineTo(maxX, minY);
          ctx.lineTo(maxX, maxY);
          ctx.stroke();
          ctx.fillStyle = "#00FF00";
          ctx.font = "bold 24px Arial";
        }
      }

      // ==========================================
      // 3. GEO KIT (Tree)
      // ==========================================
      if (activeKit === "tree" || activeKit === "geo") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);
        const numSections = 5;
        const sectionWidth = Math.floor(canvas.width / numSections);

        ctx.strokeStyle = "#9932CC";
        ctx.lineWidth = Math.max(canvas.width / 160, 5);

        for (let s = 0; s < numSections; s++) {
          const startX = s * sectionWidth;
          tf.tidy(() => {
            const sectionTensor = baseTensor.slice(
              [0, startX, 0],
              [canvas.height, sectionWidth, 3]
            );
            const normalized = sectionTensor.toFloat().div(tf.scalar(255));
            const [r, g, b] = tf.split(normalized, 3, 2);

            const treeEssence = g
              .add(r)
              .sub(b.mul(tf.scalar(1.4)))
              .squeeze();
            const reluActivated = tf.relu(treeEssence);
            const sigmoidCurvature = tf.sigmoid(
              reluActivated.sub(tf.scalar(0.4)).mul(tf.scalar(10))
            );
            const maskValues = sigmoidCurvature.dataSync();

            const leftPoints = [];
            const rightPoints = [];
            const verticalStride = Math.max(2, Math.floor(canvas.height / 25));

            for (let y = 0; y < canvas.height; y += verticalStride) {
              let firstEdge = null,
                lastEdge = null;
              for (let x = 0; x < sectionWidth; x++) {
                if (maskValues[y * sectionWidth + x] > 0.65) {
                  if (firstEdge === null) firstEdge = x;
                  lastEdge = x;
                }
              }
              if (firstEdge !== null) {
                leftPoints.push({ x: startX + firstEdge, y: y });
                rightPoints.push({ x: startX + lastEdge, y: y });
              }
            }

            if (leftPoints.length > 1) {
              ctx.beginPath();
              ctx.moveTo(leftPoints[0].x, leftPoints[0].y);
              leftPoints.forEach((p) => ctx.lineTo(p.x, p.y));
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(rightPoints[0].x, rightPoints[0].y);
              rightPoints.forEach((p) => ctx.lineTo(p.x, p.y));
              ctx.stroke();
            }
          });
        }
        baseTensor.dispose();
      }

      // ==========================================
      // 4. FIREWORKS KIT 🎆
      // ==========================================
      if (activeKit === "fireworks") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const intensity = r.add(g).add(b).div(tf.scalar(3.0)).squeeze();
          const reluActivated = tf.relu(intensity.sub(tf.scalar(0.4)));
          const sigmoidCurvature = tf.sigmoid(reluActivated.mul(tf.scalar(15)));
          const maskValues = sigmoidCurvature.dataSync();

          ctx.fillStyle = "#1100ed";
          ctx.strokeStyle = "#1100ed";
          ctx.lineWidth = 1.5;
          const stride = Math.max(3, Math.floor(canvas.width / 120));
          const brightPoints = [];

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              if (maskValues[y * canvas.width + x] > 0.75) {
                brightPoints.push({ x, y });
              }
            }
          }

          brightPoints.forEach((p) => {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 1.5, 0, 2 * Math.PI);
            ctx.fill();
          });

          if (brightPoints.length > 0) {
            let sumX = 0,
              sumY = 0;
            brightPoints.forEach((p) => {
              sumX += p.x;
              sumY += p.y;
            });
            const centerX = sumX / brightPoints.length;
            const centerY = sumY / brightPoints.length;

            ctx.beginPath();
            ctx.arc(centerX, centerY, 6, 0, 2 * Math.PI);
            ctx.fillStyle = "#1100ed";
            ctx.fill();

            brightPoints.forEach((p, index) => {
              if (index % 15 === 0) {
                ctx.beginPath();
                ctx.moveTo(centerX, centerY);
                ctx.lineTo(p.x, p.y);
                ctx.stroke();
              }
            });
          }
        });

        baseTensor.dispose();

        ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
        ctx.fillRect(30, 30, 380, 80);
        ctx.strokeStyle = "#1100ed";
        ctx.strokeRect(30, 30, 380, 80);
        ctx.fillStyle = "#1100ed";
        ctx.font = "bold 18px monospace";
        ctx.fillText(`💙 TempleGrid`, 260, 90);
      }

      // ==========================================
      // 5. CYBER / EDGE KIT (Convolutional Tensor Filter)
      // ==========================================
      if (activeKit === "cyber" || activeKit === "edge") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          // 1. Normalize pixel values [0-255] -> [0, 1]
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));

          // 2. Convert RGB -> Grayscale tensor: Shape [1, height, width, 1]
          const grayscale = normalized.mean(2).expandDims(-1).expandDims(0);

          // 3. Define a 3x3 Laplacian Edge Kernel: Shape [3, 3, 1, 1]
          // [kernelHeight, kernelWidth, inputChannels, outputChannels]
          const edgeKernel = tf.tensor4d(
            [-1, -1, -1, -1, 8, -1, -1, -1, -1],
            [3, 3, 1, 1]
          );

          // 4. Perform 2D Convolution
          // stride = 1, padding = 'same' keeps input and output dimensions identical
          const convOutput = tf.conv2d(grayscale, edgeKernel, 1, "same");

          // 5. Apply ReLU activation to throw away negative edge gradients
          const reluActivated = tf.relu(convOutput);

          // 6. Pass through Sigmoid activation with temperature scaling to sharpen edge contrast
          const edgeProbability = tf.sigmoid(
            reluActivated.sub(tf.scalar(0.2)).mul(tf.scalar(8.0))
          );

          // Squeeze back down to 2D tensor for drawing
          const mask2D = edgeProbability.squeeze();
          const maskData = mask2D.dataSync(); // Or await mask2D.data()

          // 7. Draw cyan neon vector traces over detected structural edges
          ctx.strokeStyle = "#00ffff";
          ctx.lineWidth = 1;
          const stride = 4; // Skip pixels to keep render fast

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              const index = y * canvas.width + x;
              if (maskData[index] > 0.6) {
                ctx.beginPath();
                ctx.arc(x, y, 1, 0, 2 * Math.PI);
                ctx.stroke();
              }
            }
          }
        });

        baseTensor.dispose();
      }

      ////
      // ==========================================
      // 6. LUMINA KIT (Luminance Depth & Constellations)
      // ==========================================
      if (activeKit === "lumina") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Darken the background so the light constellations "pop"
        ctx.fillStyle = "rgba(0, 0, 0, 0.85)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          // Normalize [0-255] to [0-1]
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // Calculate Perceived Luminance Tensor
          // Formula: 0.299*R + 0.587*G + 0.114*B
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // =====================================
          // TENSOR MASKS: Slicing the light
          // =====================================

          // Layer 1: Bright highlights (Luma > 0.65)
          const l1Mask = tf.cast(luma.greater(tf.scalar(0.65)), "float32");

          // Layer 2: Mid-dark features (0.25 < Luma <= 0.45)
          const l2Mask = tf.cast(
            luma
              .greater(tf.scalar(0.25))
              .logicalAnd(luma.lessEqual(tf.scalar(0.45))),
            "float32"
          );

          // Layer 3: Deep darks prep (0.05 < Luma <= 0.15)
          const l3Mask = tf.cast(
            luma
              .greater(tf.scalar(0.05))
              .logicalAnd(luma.lessEqual(tf.scalar(0.15))),
            "float32"
          );

          const l1Data = l1Mask.dataSync();
          const l2Data = l2Mask.dataSync();
          const l3Data = l3Mask.dataSync();

          // Gather coordinates safely based on stride
          const stride = Math.max(8, Math.floor(canvas.width / 45));
          const layer1Points = [];
          const layer2Points = [];
          const layer3Points = [];

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              const idx = y * canvas.width + x;
              if (l1Data[idx] === 1) layer1Points.push({ x, y });
              if (l2Data[idx] === 1) layer2Points.push({ x, y });
              if (l3Data[idx] === 1) layer3Points.push({ x, y });
            }
          }

          // =====================================
          // DRAWING: Connect the dots
          // =====================================
          const connectDots = (points, color, maxDist) => {
            ctx.fillStyle = color;
            ctx.strokeStyle = color;
            ctx.lineWidth = 1;

            for (let i = 0; i < points.length; i++) {
              // Draw the node
              ctx.beginPath();
              ctx.arc(points[i].x, points[i].y, 2, 0, 2 * Math.PI);
              ctx.fill();

              // Connect to nearby nodes in the same layer
              for (let j = i + 1; j < points.length; j++) {
                const dx = points[i].x - points[j].x;
                const dy = points[i].y - points[j].y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < maxDist) {
                  ctx.beginPath();
                  ctx.moveTo(points[i].x, points[i].y);
                  ctx.lineTo(points[j].x, points[j].y);
                  ctx.stroke();
                }
              }
            }
          };

          const maxDist = canvas.width * 0.12; // Snap distance based on image size

          // Draw Layer 3 (Deep Dark) - Faint Blue
          connectDots(layer3Points, "rgba(0, 150, 255, 0.3)", maxDist * 0.8);

          // Draw Layer 2 (Mid Dark) - Vibrant Magenta
          connectDots(layer2Points, "rgba(255, 0, 255, 0.6)", maxDist);

          // Draw Layer 1 (Bright Highlights) - Glowing Yellow
          connectDots(layer1Points, "rgba(255, 255, 0, 1.0)", maxDist * 1.2);
        });

        baseTensor.dispose();
      }
      // ==========================================
      // 7. RADIANCE KIT (Magenta Wireframe: Shadow -> Light)
      // ==========================================
      if (activeKit === "radiance") {
        // 1. Draw the image temporarily just so tf.browser.fromPixels can grab the data
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // 2. NOW paint the canvas solid black for a pure wireframe aesthetic
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // Luma Formula
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // Relaxed thresholds (0.4 and 0.6) so it finds more points to connect
          const shadowMask = tf.cast(luma.lessEqual(tf.scalar(0.4)), "float32");
          const lightMask = tf.cast(luma.greater(tf.scalar(0.6)), "float32");

          const shadowData = shadowMask.dataSync();
          const lightData = lightMask.dataSync();

          // Tighter stride for a denser wireframe
          const stride = Math.max(10, Math.floor(canvas.width / 40));
          const shadowPoints = [];
          const lightPoints = [];

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              const idx = y * canvas.width + x;
              // Add a bit of randomization to coordinates for an organic feel
              const jitterX = x + (Math.random() * 4 - 2);
              const jitterY = y + (Math.random() * 4 - 2);

              if (shadowData[idx] === 1)
                shadowPoints.push({ x: jitterX, y: jitterY });
              if (lightData[idx] === 1)
                lightPoints.push({ x: jitterX, y: jitterY });
            }
          }

          // Draw tiny magenta stars at the light sources
          ctx.fillStyle = "rgba(255, 0, 255, 0.8)";
          lightPoints.forEach((lp) => {
            ctx.beginPath();
            ctx.arc(lp.x, lp.y, 1.2, 0, 2 * Math.PI);
            ctx.fill();
          });

          // Ray tracing: Interpolate lines from dark to light
          ctx.lineWidth = 0.8; // Bumped up slightly to ensure visibility against the black

          shadowPoints.forEach((sp) => {
            let nearestLp = null;
            let minDist = Infinity;

            // Find the closest light source for this shadow point
            lightPoints.forEach((lp) => {
              const dx = lp.x - sp.x;
              const dy = lp.y - sp.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < minDist) {
                minDist = dist;
                nearestLp = lp;
              }
            });

            // If a light source is found, draw the skewed ray
            if (nearestLp) {
              // Pure Magenta Gradient: Faint at the shadow, bright at the light
              const grad = ctx.createLinearGradient(
                sp.x,
                sp.y,
                nearestLp.x,
                nearestLp.y
              );
              grad.addColorStop(0, "rgba(255, 0, 255, 0.15)"); // Shadow origin
              grad.addColorStop(1, "rgba(255, 0, 255, 0.95)"); // Light destination

              ctx.strokeStyle = grad;

              // Calculate a perpendicular offset to skew the ray into a curve
              const dx = nearestLp.x - sp.x;
              const dy = nearestLp.y - sp.y;

              const controlX = (sp.x + nearestLp.x) / 2 - dy * 0.25;
              const controlY = (sp.y + nearestLp.y) / 2 + dx * 0.25;

              ctx.beginPath();
              ctx.moveTo(sp.x, sp.y);
              ctx.quadraticCurveTo(
                controlX,
                controlY,
                nearestLp.x,
                nearestLp.y
              );
              ctx.stroke();
            }
          });
        });

        baseTensor.dispose();
      }
      // ==========================================
      // 8. AURA KIT (Image Visible + Magenta Rays)
      // ==========================================
      if (activeKit === "aura") {
        // 1. Draw the actual image to the canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // 2. Add a very subtle dark overlay.
        // This keeps the image totally visible but gives the neon magenta enough contrast to glow.
        ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // Luma Formula
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // Keep the exact same relaxed thresholds from the Radiance kit
          const shadowMask = tf.cast(luma.lessEqual(tf.scalar(0.4)), "float32");
          const lightMask = tf.cast(luma.greater(tf.scalar(0.6)), "float32");

          const shadowData = shadowMask.dataSync();
          const lightData = lightMask.dataSync();

          const shortEdge = Math.min(canvas.width, canvas.height);
          const stride = Math.max(10, Math.floor(shortEdge / 40));
          // Also apply shortEdge to your nodeRadius and maxDistAllowed calculations!
          const shadowPoints = [];
          const lightPoints = [];

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              const idx = y * canvas.width + x;
              const jitterX = x + (Math.random() * 4 - 2);
              const jitterY = y + (Math.random() * 4 - 2);

              if (shadowData[idx] === 1)
                shadowPoints.push({ x: jitterX, y: jitterY });
              if (lightData[idx] === 1)
                lightPoints.push({ x: jitterX, y: jitterY });
            }
          }

          // Draw the magenta nodes
          ctx.fillStyle = "rgba(255, 0, 255, 0.9)";
          lightPoints.forEach((lp) => {
            ctx.beginPath();
            ctx.arc(lp.x, lp.y, 1.5, 0, 2 * Math.PI);
            ctx.fill();
          });

          ctx.lineWidth = 0.8;

          // Ray tracing
          shadowPoints.forEach((sp) => {
            let nearestLp = null;
            let minDist = Infinity;

            lightPoints.forEach((lp) => {
              const dx = lp.x - sp.x;
              const dy = lp.y - sp.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < minDist) {
                minDist = dist;
                nearestLp = lp;
              }
            });

            if (nearestLp) {
              // Same gradient, ensuring the light destinations are fully opaque
              const grad = ctx.createLinearGradient(
                sp.x,
                sp.y,
                nearestLp.x,
                nearestLp.y
              );
              grad.addColorStop(0, "rgba(255, 0, 255, 0.2)");
              grad.addColorStop(1, "rgba(255, 0, 255, 1.0)");

              ctx.strokeStyle = grad;

              const dx = nearestLp.x - sp.x;
              const dy = nearestLp.y - sp.y;

              const controlX = (sp.x + nearestLp.x) / 2 - dy * 0.25;
              const controlY = (sp.y + nearestLp.y) / 2 + dx * 0.25;

              ctx.beginPath();
              ctx.moveTo(sp.x, sp.y);
              ctx.quadraticCurveTo(
                controlX,
                controlY,
                nearestLp.x,
                nearestLp.y
              );
              ctx.stroke();
            }
          });
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 8. AURA KIT (Full Brightness + Magenta Rays + Organic Chaos)
      // ==========================================
      if (activeKit === "aura") {
        // Draw the image to the canvas at full brightness (No dark overlay)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // STRICTER THRESHOLDS: Prevents iPhone HDR from turning the whole wall into a grid
          const shadowMask = tf.cast(luma.lessEqual(tf.scalar(0.3)), "float32");
          const lightMask = tf.cast(luma.greater(tf.scalar(0.7)), "float32");

          const shadowData = shadowMask.dataSync();
          const lightData = lightMask.dataSync();

          const shortEdge = Math.min(canvas.width, canvas.height);
          const stride = Math.max(12, Math.floor(shortEdge / 35));

          // MASSIVE JITTER: Shatters the rigid grid layout to look organic
          const jitterAmt = stride * 1.2;

          const shadowPoints = [];
          const lightPoints = [];

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              const idx = y * canvas.width + x;
              // Randomizes the dot placement heavily
              const jitterX = x + (Math.random() * (jitterAmt * 2) - jitterAmt);
              const jitterY = y + (Math.random() * (jitterAmt * 2) - jitterAmt);

              if (shadowData[idx] === 1)
                shadowPoints.push({ x: jitterX, y: jitterY });
              if (lightData[idx] === 1)
                lightPoints.push({ x: jitterX, y: jitterY });
            }
          }

          const nodeRadius = Math.max(1.5, shortEdge * 0.0025);
          ctx.lineWidth = Math.max(1.0, shortEdge * 0.002);
          ctx.fillStyle = "rgba(255, 0, 255, 0.9)";

          lightPoints.forEach((lp) => {
            ctx.beginPath();
            ctx.arc(lp.x, lp.y, nodeRadius, 0, 2 * Math.PI);
            ctx.fill();
          });

          // TIGHTER DISTANCE: Stops the rays from sweeping across the entire screen
          const maxDistAllowed = shortEdge * 0.25;

          shadowPoints.forEach((sp) => {
            let nearestLp = null;
            let minDist = Infinity;

            lightPoints.forEach((lp) => {
              const dx = lp.x - sp.x;
              const dy = lp.y - sp.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < minDist) {
                minDist = dist;
                nearestLp = lp;
              }
            });

            // ONLY draw the ray if the light point is close enough
            if (nearestLp && minDist < maxDistAllowed) {
              const grad = ctx.createLinearGradient(
                sp.x,
                sp.y,
                nearestLp.x,
                nearestLp.y
              );
              grad.addColorStop(0, "rgba(255, 0, 255, 0.4)");
              grad.addColorStop(1, "rgba(255, 0, 255, 1.0)");
              ctx.strokeStyle = grad;

              const dx = nearestLp.x - sp.x;
              const dy = nearestLp.y - sp.y;
              const controlX = (sp.x + nearestLp.x) / 2 - dy * 0.25;
              const controlY = (sp.y + nearestLp.y) / 2 + dx * 0.25;

              ctx.beginPath();
              ctx.moveTo(sp.x, sp.y);
              ctx.quadraticCurveTo(
                controlX,
                controlY,
                nearestLp.x,
                nearestLp.y
              );
              ctx.stroke();
            }
          });
        });
        baseTensor.dispose();
      }

      // ==========================================
      // 9. LUCID KIT (Universal Mold + Organic Chaos)
      // ==========================================
      // ==========================================
      // 9. LUCID KIT (Full Brightness + Magenta Rays + Organic Chaos)
      // ==========================================
      if (activeKit === "aura") {
        // Draw the image to the canvas at full brightness (No dark overlay)
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // STRICTER THRESHOLDS: Prevents iPhone HDR from turning the whole wall into a grid
          const shadowMask = tf.cast(luma.lessEqual(tf.scalar(0.3)), "float32");
          const lightMask = tf.cast(luma.greater(tf.scalar(0.7)), "float32");

          const shadowData = shadowMask.dataSync();
          const lightData = lightMask.dataSync();

          // PROPORTIONAL MATH: Uses shortEdge so narrow photos don't get squished
          const shortEdge = Math.min(canvas.width, canvas.height);
          const stride = Math.max(12, Math.floor(shortEdge / 35));

          // MASSIVE JITTER: Shatters the rigid grid layout to look organic
          const jitterAmt = stride * 1.2;

          const shadowPoints = [];
          const lightPoints = [];

          for (let y = 0; y < canvas.height; y += stride) {
            for (let x = 0; x < canvas.width; x += stride) {
              const idx = y * canvas.width + x;
              // Randomizes the dot placement heavily
              const jitterX = x + (Math.random() * (jitterAmt * 2) - jitterAmt);
              const jitterY = y + (Math.random() * (jitterAmt * 2) - jitterAmt);

              if (shadowData[idx] === 1)
                shadowPoints.push({ x: jitterX, y: jitterY });
              if (lightData[idx] === 1)
                lightPoints.push({ x: jitterX, y: jitterY });
            }
          }

          const nodeRadius = Math.max(1.5, shortEdge * 0.0025);
          ctx.lineWidth = Math.max(1.0, shortEdge * 0.002);
          ctx.fillStyle = "rgba(255, 0, 255, 0.9)";

          lightPoints.forEach((lp) => {
            ctx.beginPath();
            ctx.arc(lp.x, lp.y, nodeRadius, 0, 2 * Math.PI);
            ctx.fill();
          });

          // TIGHTER DISTANCE: Stops the rays from sweeping across the entire screen
          const maxDistAllowed = shortEdge * 0.25;

          shadowPoints.forEach((sp) => {
            let nearestLp = null;
            let minDist = Infinity;

            lightPoints.forEach((lp) => {
              const dx = lp.x - sp.x;
              const dy = lp.y - sp.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < minDist) {
                minDist = dist;
                nearestLp = lp;
              }
            });

            // ONLY draw the ray if the light point is close enough
            if (nearestLp && minDist < maxDistAllowed) {
              const grad = ctx.createLinearGradient(
                sp.x,
                sp.y,
                nearestLp.x,
                nearestLp.y
              );
              grad.addColorStop(0, "rgba(255, 0, 255, 0.4)");
              grad.addColorStop(1, "rgba(255, 0, 255, 1.0)");
              ctx.strokeStyle = grad;

              const dx = nearestLp.x - sp.x;
              const dy = nearestLp.y - sp.y;
              const controlX = (sp.x + nearestLp.x) / 2 - dy * 0.25;
              const controlY = (sp.y + nearestLp.y) / 2 + dx * 0.25;

              ctx.beginPath();
              ctx.moveTo(sp.x, sp.y);
              ctx.quadraticCurveTo(
                controlX,
                controlY,
                nearestLp.x,
                nearestLp.y
              );
              ctx.stroke();
            }
          });
        });
        baseTensor.dispose();
      }
      // ==========================================
      // 10. ALPINE KIT (Bulletproof Trail Lattice)
      // ==========================================
      if (activeKit === "radarone") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // ctx.fillStyle = "rgba(0, 15, 5, 0.4)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          // 1. Downsample for the geometric grid
          const gridHeight = 70;
          const gridWidth = Math.floor(
            (canvas.width / canvas.height) * gridHeight
          );

          const resizedImage = tf.image.resizeBilinear(baseTensor, [
            gridHeight,
            gridWidth,
          ]);
          const normalized = resizedImage.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // 2. Use pure Luminance.
          // Sky = Bright (>0.6). Dark Forest = Dark (<0.15). Trails = the Mid-tones!
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // Highly forgiving threshold to guarantee we catch the mountain slopes
          const isTrail = luma
            .greater(tf.scalar(0.15))
            .logicalAnd(luma.lessEqual(tf.scalar(0.55)));

          const maskData = tf.cast(isTrail, "float32").dataSync();

          // 3. Draw the Green Lattice
          const scaleX = canvas.width / gridWidth;
          const scaleY = canvas.height / gridHeight;

          ctx.strokeStyle = "rgba(0, 255, 120, 0.9)"; // Neon Alpine Green
          ctx.lineWidth = 1.5;

          for (let y = 0; y < gridHeight - 1; y++) {
            for (let x = 0; x < gridWidth - 1; x++) {
              if (maskData[y * gridWidth + x] === 1) {
                const right = maskData[y * gridWidth + (x + 1)];
                const down = maskData[(y + 1) * gridWidth + x];
                const diagonal = maskData[(y + 1) * gridWidth + (x + 1)];

                const px = x * scaleX;
                const py = y * scaleY;
                const nextX = (x + 1) * scaleX;
                const nextY = (y + 1) * scaleY;

                ctx.beginPath();
                if (right === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(nextX, py);
                }
                if (down === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(px, nextY);
                }
                if (diagonal === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(nextX, nextY);
                }
                ctx.stroke();
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 11. OCEAN KIT (Bulletproof Oceanic Lattice)
      // ==========================================
      if (activeKit === "radartwo") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          const gridHeight = 70;
          const gridWidth = Math.floor(
            (canvas.width / canvas.height) * gridHeight
          );

          const resizedImage = tf.image.resizeBilinear(baseTensor, [
            gridHeight,
            gridWidth,
          ]);
          const normalized = resizedImage.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // 2. Extremely forgiving Ocean Check: Just look for areas where Blue is higher than Red
          // This catches dark blue, teal, gray-blue, and bright blue.
          const isOcean = b.greater(r.add(tf.scalar(0.02))).squeeze();

          const maskData = tf.cast(isOcean, "float32").dataSync();

          // 3. Draw the Blue Lattice
          const scaleX = canvas.width / gridWidth;
          const scaleY = canvas.height / gridHeight;

          ctx.strokeStyle = "rgba(0, 200, 255, 0.9)"; // Deep Cyan
          ctx.lineWidth = 1.5;

          for (let y = 0; y < gridHeight - 1; y++) {
            for (let x = 0; x < gridWidth - 1; x++) {
              if (maskData[y * gridWidth + x] === 1) {
                const right = maskData[y * gridWidth + (x + 1)];
                const down = maskData[(y + 1) * gridWidth + x];
                const diagonal = maskData[(y + 1) * gridWidth + (x + 1)];

                const px = x * scaleX;
                const py = y * scaleY;
                const nextX = (x + 1) * scaleX;
                const nextY = (y + 1) * scaleY;

                ctx.beginPath();
                if (right === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(nextX, py);
                }
                if (down === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(px, nextY);
                }
                if (diagonal === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(nextX, nextY);
                }
                ctx.stroke();
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 12. PORTRAIT 3D KIT (Pure Purple Theme)
      // ==========================================
      if (activeKit === "portrait3d") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(canvas);

        if (faces.length > 0) {
          faces.forEach((face) => {
            const jawline = [
              234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400,
              378, 379, 365, 397, 288, 361, 323, 454,
            ];
            const lipsOuter = [
              61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
              267, 0, 37, 39, 40, 185,
            ];
            const rightEye = [
              33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
              161, 246,
            ];
            const leftEye = [
              263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
              387, 388, 466,
            ];
            const rightBrow = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
            const leftBrow = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];

            const drawFeature = (indices, color, closePath = false) => {
              ctx.beginPath();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.5;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";

              indices.forEach((index, i) => {
                const pt = face.keypoints[index];
                if (i === 0) ctx.moveTo(pt.x, pt.y);
                else ctx.lineTo(pt.x, pt.y);
              });

              if (closePath) ctx.closePath();
              ctx.stroke();
            };

            // All features mapped to varying shades of purple
            const purplePrimary = "rgba(93, 0, 232, 1)";
            const purpleSecondary = "rgba(93, 0, 232, 1)";

            drawFeature(jawline, purplePrimary);
            drawFeature(lipsOuter, purpleSecondary, true);
            drawFeature(rightEye, purpleSecondary, true);
            drawFeature(leftEye, purpleSecondary, true);
            drawFeature(rightBrow, purplePrimary);
            drawFeature(leftBrow, purplePrimary);

            let minX = Infinity,
              maxX = -Infinity,
              minY = Infinity,
              maxY = -Infinity;
            face.keypoints.forEach((kp) => {
              if (kp.x < minX) minX = kp.x;
              if (kp.x > maxX) maxX = kp.x;
              if (kp.y < minY) minY = kp.y;
              if (kp.y > maxY) maxY = kp.y;
            });

            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;

            const boxX = maxX + faceWidth * 0.15;
            const boxY = maxY - faceHeight * 0.2;
            const bW = faceWidth * 0.6;
            const bH = faceHeight * 0.6;
            const depth = canvas.width * 0.05;

            // Purple 3D Box
            ctx.strokeStyle = "rgba(93, 0, 232, 1)";
            ctx.lineWidth = 2.0;

            ctx.strokeRect(boxX + depth, boxY - depth, bW, bH);
            ctx.strokeRect(boxX, boxY, bW, bH);

            ctx.beginPath();
            ctx.moveTo(boxX, boxY);
            ctx.lineTo(boxX + depth, boxY - depth);
            ctx.moveTo(boxX + bW, boxY);
            ctx.lineTo(boxX + bW + depth, boxY - depth);
            ctx.moveTo(boxX, boxY + bH);
            ctx.lineTo(boxX + depth, boxY + bH - depth);
            ctx.moveTo(boxX + bW, boxY + bH);
            ctx.lineTo(boxX + bW + depth, boxY + bH - depth);
            ctx.stroke();

            // Purple glass pane fill
            ctx.fillStyle = "rgba(180, 50, 255, 0.15)";
            ctx.fillRect(boxX, boxY, bW, bH);
          });
        }
      }

      // ==========================================
      // 13. TRAPEZOID KIT (Face with Inward-Facing Trapezoids)
      // ==========================================
      if (activeKit === "portraittrap") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(canvas);

        if (faces.length > 0) {
          faces.forEach((face) => {
            // Mapping the face (using same purple theme)
            const jawline = [
              234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400,
              378, 379, 365, 397, 288, 361, 323, 454,
            ];
            const lipsOuter = [
              61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
              267, 0, 37, 39, 40, 185,
            ];
            const rightEye = [
              33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
              161, 246,
            ];
            const leftEye = [
              263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
              387, 388, 466,
            ];
            const rightBrow = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
            const leftBrow = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];

            const drawFeature = (indices, color, closePath = false) => {
              ctx.beginPath();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.0;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";
              indices.forEach((index, i) => {
                const pt = face.keypoints[index];
                if (i === 0) ctx.moveTo(pt.x, pt.y);
                else ctx.lineTo(pt.x, pt.y);
              });
              if (closePath) ctx.closePath();
              ctx.stroke();
            };

            const purpleColor = "rgba(93, 0, 232, 1)";
            drawFeature(jawline, purpleColor);
            drawFeature(lipsOuter, purpleColor, true);
            drawFeature(rightEye, purpleColor, true);
            drawFeature(leftEye, purpleColor, true);
            drawFeature(rightBrow, purpleColor);
            drawFeature(leftBrow, purpleColor);

            // Calculate face bounding box
            let minX = Infinity,
              maxX = -Infinity,
              minY = Infinity,
              maxY = -Infinity;
            face.keypoints.forEach((kp) => {
              if (kp.x < minX) minX = kp.x;
              if (kp.x > maxX) maxX = kp.x;
              if (kp.y < minY) minY = kp.y;
              if (kp.y > maxY) maxY = kp.y;
            });

            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;
            const midX = (minX + maxX) / 2;
            const midY = (minY + maxY) / 2;
            const gap = faceWidth * 0.15; // Distance from face to trapezoids

            ctx.strokeStyle = "rgba(93, 0, 232, 1)";
            ctx.fillStyle = "rgba(93, 0, 232, .21)";
            ctx.lineWidth = 2.0;

            const drawTrapezoid = (points) => {
              ctx.beginPath();
              ctx.moveTo(points[0].x, points[0].y);
              ctx.lineTo(points[1].x, points[1].y);
              ctx.lineTo(points[2].x, points[2].y);
              ctx.lineTo(points[3].x, points[3].y);
              ctx.closePath();
              ctx.stroke();
              ctx.fill();
            };

            const sideTrapWidth = faceWidth * 0.3;
            const narrowHeight = faceHeight * 0.4;
            const wideHeight = faceHeight * 0.7;

            // 1. Left Trapezoid (Narrow side on the right, facing the cheek)
            drawTrapezoid([
              { x: minX - gap, y: midY - narrowHeight / 2 }, // Top-Right (Narrow)
              { x: minX - gap, y: midY + narrowHeight / 2 }, // Bottom-Right (Narrow)
              { x: minX - gap - sideTrapWidth, y: midY + wideHeight / 2 }, // Bottom-Left (Wide)
              { x: minX - gap - sideTrapWidth, y: midY - wideHeight / 2 }, // Top-Left (Wide)
            ]);

            // 2. Right Trapezoid (Narrow side on the left, facing the cheek)
            drawTrapezoid([
              { x: maxX + gap, y: midY - narrowHeight / 2 }, // Top-Left (Narrow)
              { x: maxX + gap, y: midY + narrowHeight / 2 }, // Bottom-Left (Narrow)
              { x: maxX + gap + sideTrapWidth, y: midY + wideHeight / 2 }, // Bottom-Right (Wide)
              { x: maxX + gap + sideTrapWidth, y: midY - wideHeight / 2 }, // Top-Right (Wide)
            ]);

            // 3. Bottom Trapezoid (Narrow side on the top, facing the jaw)
            const bottomTrapHeight = faceHeight * 0.3;
            const narrowWidth = faceWidth * 0.45;
            const wideWidth = faceWidth * 0.8;

            drawTrapezoid([
              { x: midX - narrowWidth / 2, y: maxY + gap }, // Top-Left (Narrow)
              { x: midX + narrowWidth / 2, y: maxY + gap }, // Top-Right (Narrow)
              { x: midX + wideWidth / 2, y: maxY + gap + bottomTrapHeight }, // Bottom-Right (Wide)
              { x: midX - wideWidth / 2, y: maxY + gap + bottomTrapHeight }, // Bottom-Left (Wide)
            ]);
          });
        }
      }
      // ==========================================
      // 14. PORTRAIT AFRICA KIT (Purple Theme + Holographic Africa)
      // ==========================================
      if (activeKit === "portraitafrica") {
        // Draw image at full brightness
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(canvas);

        if (faces.length > 0) {
          faces.forEach((face) => {
            // --- 1. DEFINING SPECIFIC FACIAL FEATURE INDICES ---
            const jawline = [
              234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400,
              378, 379, 365, 397, 288, 361, 323, 454,
            ];
            const lipsOuter = [
              61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
              267, 0, 37, 39, 40, 185,
            ];
            const rightEye = [
              33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
              161, 246,
            ];
            const leftEye = [
              263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
              387, 388, 466,
            ];
            const rightBrow = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
            const leftBrow = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];

            const drawFeature = (indices, color, closePath = false) => {
              ctx.beginPath();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.5;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";

              indices.forEach((index, i) => {
                const pt = face.keypoints[index];
                if (i === 0) ctx.moveTo(pt.x, pt.y);
                else ctx.lineTo(pt.x, pt.y);
              });

              if (closePath) ctx.closePath();
              ctx.stroke();
            };

            // Using your exact purple color definition
            const purplePrimary = "rgba(93, 0, 232, 1)";
            const purpleSecondary = "rgba(93, 0, 232, 1)";

            drawFeature(jawline, purplePrimary);
            drawFeature(lipsOuter, purpleSecondary, true);
            drawFeature(rightEye, purpleSecondary, true);
            drawFeature(leftEye, purpleSecondary, true);
            drawFeature(rightBrow, purplePrimary);
            drawFeature(leftBrow, purplePrimary);

            // --- 2. CALCULATE FACE BOUNDARIES FOR THE 3D BOX ---
            let minX = Infinity,
              maxX = -Infinity,
              minY = Infinity,
              maxY = -Infinity;
            face.keypoints.forEach((kp) => {
              if (kp.x < minX) minX = kp.x;
              if (kp.x > maxX) maxX = kp.x;
              if (kp.y < minY) minY = kp.y;
              if (kp.y > maxY) maxY = kp.y;
            });

            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;

            // --- 3. DRAW THE PURPLE 3D ISOMETRIC BOX ---
            const boxX = maxX + faceWidth * 0.15;
            const boxY = maxY - faceHeight * 0.2;
            const bW = faceWidth * 0.6;
            const bH = faceHeight * 0.6;
            const depth = canvas.width * 0.05;

            ctx.strokeStyle = "rgba(93, 0, 232, 0.85)"; // Matching purple
            ctx.lineWidth = 2.0;

            // Back Face
            ctx.strokeRect(boxX + depth, boxY - depth, bW, bH);
            // Front Face
            ctx.strokeRect(boxX, boxY, bW, bH);

            // Connecting Edges
            ctx.beginPath();
            ctx.moveTo(boxX, boxY);
            ctx.lineTo(boxX + depth, boxY - depth);
            ctx.moveTo(boxX + bW, boxY);
            ctx.lineTo(boxX + bW + depth, boxY - depth);
            ctx.moveTo(boxX, boxY + bH);
            ctx.lineTo(boxX + depth, boxY + bH - depth);
            ctx.moveTo(boxX + bW, boxY + bH);
            ctx.lineTo(boxX + bW + depth, boxY + bH - depth);
            ctx.stroke();

            // Purple glass pane fill
            ctx.fillStyle = "rgba(93, 0, 232, 0.15)";
            ctx.fillRect(boxX, boxY, bW, bH);

            // --- 4. DRAW HOLOGRAPHIC RED AFRICA INSIDE THE BOX ---
            // Normalized low-poly coordinates for Africa [x, y]
            const africaPoints = [
              [0.2, 0.2],
              [0.4, 0.1],
              [0.6, 0.15],
              [0.75, 0.2], // North Coast
              [0.8, 0.3],
              [0.95, 0.45],
              [0.85, 0.55], // Horn of Africa
              [0.75, 0.7],
              [0.6, 0.95],
              [0.5, 0.95], // East Coast & South Africa
              [0.4, 0.8],
              [0.45, 0.6], // Southwest Coast
              [0.35, 0.55],
              [0.15, 0.5], // Gulf of Guinea
              [0.05, 0.35],
              [0.1, 0.2], // West Coast
            ];

            // Center it perfectly in the middle of the 3D volume
            const midDepthX = boxX + depth / 2;
            const midDepthY = boxY - depth / 2;

            // Scale and pad the outline so it fits beautifully inside the glass cube
            const scaleW = bW * 0.7;
            const scaleH = bH * 0.7;
            const offsetX = midDepthX + bW * 0.15;
            const offsetY = midDepthY + bH * 0.15;

            ctx.beginPath();
            ctx.strokeStyle = "rgba(255, 0, 0, 0.9)"; // Sharp Red Outline
            ctx.fillStyle = "rgba(255, 0, 0, 0.2)"; // Soft Red Inner Glow
            ctx.lineWidth = 2.0;

            africaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });

            ctx.closePath();
            ctx.stroke();
            ctx.fill();
          });
        }
      }

      // ==========================================
      // 15. PORTRAIT AFRICA ZONE KIT (Highlighted Western Lobe)
      // ==========================================
      if (activeKit === "portraitafricazone") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(canvas);

        if (faces.length > 0) {
          faces.forEach((face) => {
            // --- 1. FACIAL FEATURES ---
            const jawline = [
              234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400,
              378, 379, 365, 397, 288, 361, 323, 454,
            ];
            const lipsOuter = [
              61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
              267, 0, 37, 39, 40, 185,
            ];
            const rightEye = [
              33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
              161, 246,
            ];
            const leftEye = [
              263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
              387, 388, 466,
            ];
            const rightBrow = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
            const leftBrow = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];

            const drawFeature = (indices, color, closePath = false) => {
              ctx.beginPath();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.5;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";
              indices.forEach((index, i) => {
                const pt = face.keypoints[index];
                if (i === 0) ctx.moveTo(pt.x, pt.y);
                else ctx.lineTo(pt.x, pt.y);
              });
              if (closePath) ctx.closePath();
              ctx.stroke();
            };

            const purplePrimary = "rgba(93, 0, 232, 1)";

            drawFeature(jawline, purplePrimary);
            drawFeature(lipsOuter, purplePrimary, true);
            drawFeature(rightEye, purplePrimary, true);
            drawFeature(leftEye, purplePrimary, true);
            drawFeature(rightBrow, purplePrimary);
            drawFeature(leftBrow, purplePrimary);

            // --- 2. FACE BOUNDARIES ---
            let minX = Infinity,
              maxX = -Infinity,
              minY = Infinity,
              maxY = -Infinity;
            face.keypoints.forEach((kp) => {
              if (kp.x < minX) minX = kp.x;
              if (kp.x > maxX) maxX = kp.x;
              if (kp.y < minY) minY = kp.y;
              if (kp.y > maxY) maxY = kp.y;
            });

            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;

            // --- 3. PURPLE 3D ISOMETRIC BOX ---
            const boxX = maxX + faceWidth * 0.15;
            const boxY = maxY - faceHeight * 0.2;
            const bW = faceWidth * 0.6;
            const bH = faceHeight * 0.6;
            const depth = canvas.width * 0.05;

            ctx.strokeStyle = "rgba(93, 0, 232, 0.85)";
            ctx.lineWidth = 2.0;

            ctx.strokeRect(boxX + depth, boxY - depth, bW, bH); // Back
            ctx.strokeRect(boxX, boxY, bW, bH); // Front

            ctx.beginPath();
            ctx.moveTo(boxX, boxY);
            ctx.lineTo(boxX + depth, boxY - depth);
            ctx.moveTo(boxX + bW, boxY);
            ctx.lineTo(boxX + bW + depth, boxY - depth);
            ctx.moveTo(boxX, boxY + bH);
            ctx.lineTo(boxX + depth, boxY + bH - depth);
            ctx.moveTo(boxX + bW, boxY + bH);
            ctx.lineTo(boxX + bW + depth, boxY + bH - depth);
            ctx.stroke();

            ctx.fillStyle = "rgba(93, 0, 232, 0.15)";
            ctx.fillRect(boxX, boxY, bW, bH);

            // --- 4. HOLOGRAPHIC RED AFRICA ---
            const midDepthX = boxX + depth / 2;
            const midDepthY = boxY - depth / 2;
            const scaleW = bW * 0.7;
            const scaleH = bH * 0.7;
            const offsetX = midDepthX + bW * 0.15;
            const offsetY = midDepthY + bH * 0.15;

            // Full Africa Outline
            const africaPoints = [
              [0.2, 0.2],
              [0.4, 0.1],
              [0.6, 0.15],
              [0.75, 0.2],
              [0.8, 0.3],
              [0.95, 0.45],
              [0.85, 0.55],
              [0.75, 0.7],
              [0.6, 0.95],
              [0.5, 0.95],
              [0.4, 0.8],
              [0.45, 0.6],
              [0.35, 0.55],
              [0.15, 0.5],
              [0.05, 0.35],
              [0.1, 0.2],
            ];

            ctx.beginPath();
            ctx.strokeStyle = "rgba(255, 0, 0, 0.9)";
            ctx.fillStyle = "rgba(255, 0, 0, 0.2)"; // Soft base glow
            ctx.lineWidth = 2.0;

            africaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.closePath();
            ctx.stroke();
            ctx.fill();

            // --- 5. FILL THE WESTERN (VERTICALLY SHORTER) SECTOR ---
            // Slicing out just the West Africa coordinates
            const westAfricaPoints = [
              [0.2, 0.2],
              [0.4, 0.1], // Top edge
              [0.45, 0.55], // Cut line straight down the middle of the map
              [0.35, 0.55],
              [0.15, 0.5], // Bottom edge (Gulf of Guinea)
              [0.05, 0.35],
              [0.1, 0.2], // Far West edge
            ];

            ctx.beginPath();
            ctx.fillStyle = "rgba(255, 50, 50, 0.95)"; // Bright, solid red
            westAfricaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.closePath();
            ctx.fill(); // Fill just this zone solid
          });
        }
      }

      // ==========================================
      // 16. AFRICA WEST + TRAPS KIT
      // ==========================================
      if (activeKit === "africawesttrap") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(canvas);

        if (faces.length > 0) {
          faces.forEach((face) => {
            // --- 1. FACIAL FEATURES ---
            const jawline = [
              234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400,
              378, 379, 365, 397, 288, 361, 323, 454,
            ];
            const lipsOuter = [
              61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
              267, 0, 37, 39, 40, 185,
            ];
            const rightEye = [
              33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
              161, 246,
            ];
            const leftEye = [
              263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
              387, 388, 466,
            ];
            const rightBrow = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
            const leftBrow = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];

            const drawFeature = (indices, color, closePath = false) => {
              ctx.beginPath();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.5;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";
              indices.forEach((index, i) => {
                const pt = face.keypoints[index];
                if (i === 0) ctx.moveTo(pt.x, pt.y);
                else ctx.lineTo(pt.x, pt.y);
              });
              if (closePath) ctx.closePath();
              ctx.stroke();
            };

            const purplePrimary = "rgba(93, 0, 232, 1)";
            drawFeature(jawline, purplePrimary);
            drawFeature(lipsOuter, purplePrimary, true);
            drawFeature(rightEye, purplePrimary, true);
            drawFeature(leftEye, purplePrimary, true);
            drawFeature(rightBrow, purplePrimary);
            drawFeature(leftBrow, purplePrimary);

            // --- 2. FACE BOUNDARIES ---
            let minX = Infinity,
              maxX = -Infinity,
              minY = Infinity,
              maxY = -Infinity;
            face.keypoints.forEach((kp) => {
              if (kp.x < minX) minX = kp.x;
              if (kp.x > maxX) maxX = kp.x;
              if (kp.y < minY) minY = kp.y;
              if (kp.y > maxY) maxY = kp.y;
            });

            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;
            const midX = (minX + maxX) / 2;
            const midY = (minY + maxY) / 2;
            const gap = faceWidth * 0.15;

            // --- 3. DRAW FACIAL TRAPEZOIDS ---
            ctx.strokeStyle = "rgba(93, 0, 232, 0.85)";
            ctx.fillStyle = "rgba(93, 0, 232, 0.1)";
            ctx.lineWidth = 2.0;

            const drawTrapezoid = (points) => {
              ctx.beginPath();
              points.forEach((pt, i) =>
                i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y)
              );
              ctx.closePath();
              ctx.stroke();
              ctx.fill();
            };

            const sideTrapWidth = faceWidth * 0.3;
            const narrowHeight = faceHeight * 0.4;
            const wideHeight = faceHeight * 0.7;
            // Left Trap
            drawTrapezoid([
              { x: minX - gap, y: midY - narrowHeight / 2 },
              { x: minX - gap, y: midY + narrowHeight / 2 },
              { x: minX - gap - sideTrapWidth, y: midY + wideHeight / 2 },
              { x: minX - gap - sideTrapWidth, y: midY - wideHeight / 2 },
            ]);
            // Right Trap
            drawTrapezoid([
              { x: maxX + gap, y: midY - narrowHeight / 2 },
              { x: maxX + gap, y: midY + narrowHeight / 2 },
              { x: maxX + gap + sideTrapWidth, y: midY + wideHeight / 2 },
              { x: maxX + gap + sideTrapWidth, y: midY - wideHeight / 2 },
            ]);
            // Bottom Trap
            const bottomTrapHeight = faceHeight * 0.3;
            const narrowWidth = faceWidth * 0.45;
            const wideWidth = faceWidth * 0.8;
            drawTrapezoid([
              { x: midX - narrowWidth / 2, y: maxY + gap },
              { x: midX + narrowWidth / 2, y: maxY + gap },
              { x: midX + wideWidth / 2, y: maxY + gap + bottomTrapHeight },
              { x: midX - wideWidth / 2, y: maxY + gap + bottomTrapHeight },
            ]);

            // --- 4. PURPLE 3D BOX ---
            const boxX = maxX + faceWidth * 0.15;
            const boxY = maxY - faceHeight * 0.2;
            const bW = faceWidth * 0.6;
            const bH = faceHeight * 0.6;
            const depth = canvas.width * 0.05;

            ctx.strokeRect(boxX + depth, boxY - depth, bW, bH);
            ctx.strokeRect(boxX, boxY, bW, bH);
            ctx.beginPath();
            ctx.moveTo(boxX, boxY);
            ctx.lineTo(boxX + depth, boxY - depth);
            ctx.moveTo(boxX + bW, boxY);
            ctx.lineTo(boxX + bW + depth, boxY - depth);
            ctx.moveTo(boxX, boxY + bH);
            ctx.lineTo(boxX + depth, boxY + bH - depth);
            ctx.moveTo(boxX + bW, boxY + bH);
            ctx.lineTo(boxX + bW + depth, boxY + bH - depth);
            ctx.stroke();
            ctx.fillRect(boxX, boxY, bW, bH);

            // --- 5. HOLOGRAPHIC RED AFRICA ---
            const midDepthX = boxX + depth / 2;
            const midDepthY = boxY - depth / 2;
            const scaleW = bW * 0.7;
            const scaleH = bH * 0.7;
            const offsetX = midDepthX + bW * 0.15;
            const offsetY = midDepthY + bH * 0.15;

            const africaPoints = [
              [0.2, 0.2],
              [0.4, 0.1],
              [0.6, 0.15],
              [0.75, 0.2],
              [0.8, 0.3],
              [0.95, 0.45],
              [0.85, 0.55],
              [0.75, 0.7],
              [0.6, 0.95],
              [0.5, 0.95],
              [0.4, 0.8],
              [0.45, 0.6],
              [0.35, 0.55],
              [0.15, 0.5],
              [0.05, 0.35],
              [0.1, 0.2],
            ];

            ctx.beginPath();
            ctx.strokeStyle = "rgba(255, 0, 0, 0.9)";
            ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
            ctx.lineWidth = 2.0;
            africaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.closePath();
            ctx.stroke();
            ctx.fill();

            // --- 6. FILL THE WESTERN ZONE ---
            const westAfricaPoints = [
              [0.2, 0.2],
              [0.4, 0.1],
              [0.45, 0.55],
              [0.35, 0.55],
              [0.15, 0.5],
              [0.05, 0.35],
              [0.1, 0.2],
            ];
            ctx.beginPath();
            ctx.fillStyle = "rgba(255, 50, 50, 0.95)";
            westAfricaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.closePath();
            ctx.fill();
          });
        }
      }

      // ==========================================
      // 17. AFRICA SOUTH + TRAPS KIT
      // ==========================================
      if (activeKit === "africasouthtrap") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );

        const faces = await detector.estimateFaces(canvas);

        if (faces.length > 0) {
          faces.forEach((face) => {
            // --- 1. FACIAL FEATURES ---
            const jawline = [
              234, 93, 132, 58, 172, 136, 150, 149, 176, 148, 152, 377, 400,
              378, 379, 365, 397, 288, 361, 323, 454,
            ];
            const lipsOuter = [
              61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
              267, 0, 37, 39, 40, 185,
            ];
            const rightEye = [
              33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
              161, 246,
            ];
            const leftEye = [
              263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
              387, 388, 466,
            ];
            const rightBrow = [46, 53, 52, 65, 55, 70, 63, 105, 66, 107];
            const leftBrow = [276, 283, 282, 295, 285, 300, 293, 334, 296, 336];

            const drawFeature = (indices, color, closePath = false) => {
              ctx.beginPath();
              ctx.strokeStyle = color;
              ctx.lineWidth = 2.5;
              ctx.lineCap = "round";
              ctx.lineJoin = "round";
              indices.forEach((index, i) => {
                const pt = face.keypoints[index];
                if (i === 0) ctx.moveTo(pt.x, pt.y);
                else ctx.lineTo(pt.x, pt.y);
              });
              if (closePath) ctx.closePath();
              ctx.stroke();
            };

            const purplePrimary = "rgba(93, 0, 232, 1)";
            drawFeature(jawline, purplePrimary);
            drawFeature(lipsOuter, purplePrimary, true);
            drawFeature(rightEye, purplePrimary, true);
            drawFeature(leftEye, purplePrimary, true);
            drawFeature(rightBrow, purplePrimary);
            drawFeature(leftBrow, purplePrimary);

            // --- 2. FACE BOUNDARIES ---
            let minX = Infinity,
              maxX = -Infinity,
              minY = Infinity,
              maxY = -Infinity;
            face.keypoints.forEach((kp) => {
              if (kp.x < minX) minX = kp.x;
              if (kp.x > maxX) maxX = kp.x;
              if (kp.y < minY) minY = kp.y;
              if (kp.y > maxY) maxY = kp.y;
            });

            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;
            const midX = (minX + maxX) / 2;
            const midY = (minY + maxY) / 2;
            const gap = faceWidth * 0.15;

            // --- 3. DRAW FACIAL TRAPEZOIDS ---
            ctx.strokeStyle = "rgba(93, 0, 232, 0.85)";
            ctx.fillStyle = "rgba(93, 0, 232, 0.1)";
            ctx.lineWidth = 2.0;

            const drawTrapezoid = (points) => {
              ctx.beginPath();
              points.forEach((pt, i) =>
                i === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y)
              );
              ctx.closePath();
              ctx.stroke();
              ctx.fill();
            };

            const sideTrapWidth = faceWidth * 0.3;
            const narrowHeight = faceHeight * 0.4;
            const wideHeight = faceHeight * 0.7;
            // Left Trap
            drawTrapezoid([
              { x: minX - gap, y: midY - narrowHeight / 2 },
              { x: minX - gap, y: midY + narrowHeight / 2 },
              { x: minX - gap - sideTrapWidth, y: midY + wideHeight / 2 },
              { x: minX - gap - sideTrapWidth, y: midY - wideHeight / 2 },
            ]);
            // Right Trap
            drawTrapezoid([
              { x: maxX + gap, y: midY - narrowHeight / 2 },
              { x: maxX + gap, y: midY + narrowHeight / 2 },
              { x: maxX + gap + sideTrapWidth, y: midY + wideHeight / 2 },
              { x: maxX + gap + sideTrapWidth, y: midY - wideHeight / 2 },
            ]);
            // Bottom Trap
            const bottomTrapHeight = faceHeight * 0.3;
            const narrowWidth = faceWidth * 0.45;
            const wideWidth = faceWidth * 0.8;
            drawTrapezoid([
              { x: midX - narrowWidth / 2, y: maxY + gap },
              { x: midX + narrowWidth / 2, y: maxY + gap },
              { x: midX + wideWidth / 2, y: maxY + gap + bottomTrapHeight },
              { x: midX - wideWidth / 2, y: maxY + gap + bottomTrapHeight },
            ]);

            // --- 4. PURPLE 3D BOX ---
            const boxX = maxX + faceWidth * 0.15;
            const boxY = maxY - faceHeight * 0.2;
            const bW = faceWidth * 0.6;
            const bH = faceHeight * 0.6;
            const depth = canvas.width * 0.05;

            ctx.strokeRect(boxX + depth, boxY - depth, bW, bH);
            ctx.strokeRect(boxX, boxY, bW, bH);
            ctx.beginPath();
            ctx.moveTo(boxX, boxY);
            ctx.lineTo(boxX + depth, boxY - depth);
            ctx.moveTo(boxX + bW, boxY);
            ctx.lineTo(boxX + bW + depth, boxY - depth);
            ctx.moveTo(boxX, boxY + bH);
            ctx.lineTo(boxX + depth, boxY + bH - depth);
            ctx.moveTo(boxX + bW, boxY + bH);
            ctx.lineTo(boxX + bW + depth, boxY + bH - depth);
            ctx.stroke();
            ctx.fillRect(boxX, boxY, bW, bH);

            // --- 5. HOLOGRAPHIC RED AFRICA ---
            const midDepthX = boxX + depth / 2;
            const midDepthY = boxY - depth / 2;
            const scaleW = bW * 0.7;
            const scaleH = bH * 0.7;
            const offsetX = midDepthX + bW * 0.15;
            const offsetY = midDepthY + bH * 0.15;

            const africaPoints = [
              [0.2, 0.2],
              [0.4, 0.1],
              [0.6, 0.15],
              [0.75, 0.2],
              [0.8, 0.3],
              [0.95, 0.45],
              [0.85, 0.55],
              [0.75, 0.7],
              [0.6, 0.95],
              [0.5, 0.95],
              [0.4, 0.8],
              [0.45, 0.6],
              [0.35, 0.55],
              [0.15, 0.5],
              [0.05, 0.35],
              [0.1, 0.2],
            ];

            ctx.beginPath();
            ctx.strokeStyle = "rgba(255, 0, 0, 0.9)";
            ctx.fillStyle = "rgba(255, 0, 0, 0.2)";
            ctx.lineWidth = 2.0;
            africaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.closePath();
            ctx.stroke();
            ctx.fill();

            // --- 6. FILL THE SOUTHERN ZONE (Bottom 1/3) ---
            const southAfricaPoints = [
              [0.43, 0.65], // Left cut start
              [0.78, 0.65], // Right cut start
              [0.75, 0.7],
              [0.6, 0.95],
              [0.5, 0.95], // East Coast to Deep South
              [0.4, 0.8], // Southwest Coast
            ];
            ctx.beginPath();
            ctx.fillStyle = "rgba(255, 50, 50, 0.95)";
            southAfricaPoints.forEach((pt, index) => {
              const px = offsetX + pt[0] * scaleW;
              const py = offsetY + pt[1] * scaleH;
              if (index === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            });
            ctx.closePath();
            ctx.fill();
          });
        }
      }

      // ==========================================
      // 18. LETTER KIT (Standalone Vector/Pixel Typography)
      // ==========================================
      if (activeKit === "letterkit") {
        // 1. Draw the base image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Slightly darken the background so the bright letters pop perfectly
        ctx.fillStyle = "rgba(0, 15, 25, 0.5)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 2. Define dynamic dimensions based on the photo's size
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Make the letters huge (40% of the canvas height)
        const letterH = canvas.height * 0.4;
        const letterW = letterH * 0.6; // Keep a clean aspect ratio
        const spacing = letterW * 0.4;

        // Calculate starting X so the group of letters is perfectly centered
        const totalWidth = letterW * 2 + spacing;
        const startX = centerX - totalWidth / 2;
        const startY = centerY - letterH / 2;

        // 3. Styling the Holographic Neon
        ctx.strokeStyle = "rgba(0, 255, 255, 1)"; // Pure Cyan
        ctx.lineWidth = Math.max(6, canvas.width / 120); // Scales thickness with image size
        ctx.lineCap = "square"; // Sharp, pixel-perfect corners
        ctx.lineJoin = "miter";

        // Add a glowing shadow effect
        ctx.shadowColor = "rgba(0, 255, 255, 0.8)";
        ctx.shadowBlur = 20;

        // --- DRAW "N" ---
        ctx.beginPath();
        ctx.moveTo(startX, startY + letterH); // Bottom Left
        ctx.lineTo(startX, startY); // Top Left
        ctx.lineTo(startX + letterW, startY + letterH); // Diagonal cut
        ctx.lineTo(startX + letterW, startY); // Top Right
        ctx.stroke();

        // --- DRAW "O" ---
        const oX = startX + letterW + spacing;
        ctx.beginPath();
        // A sharp geometric box 'O' matches the tensor/pixel aesthetic best
        ctx.rect(oX, startY, letterW, letterH);
        ctx.stroke();

        // Reset shadow so it doesn't accidentally bleed into other operations
        ctx.shadowBlur = 0;
      }
      // ==========================================
      // 18. LETTER KIT (Boundary Edge Typography Matrix)
      // ==========================================
      if (activeKit === "letterkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Add a very subtle white wash so the dark blue letters stand out perfectly against dark backgrounds
        // ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // 1. Convert to Grayscale (Luminance) to evaluate structure
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // 2. Laplacian Edge Detection to find the structural boundaries
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();

          // 3. Styling the Dark Blue Letters
          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)"; // Deep Dark Blue
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          // Calculate grid size to map out the letters proportionally
          const shortEdge = Math.min(canvas.width, canvas.height);
          const gridSize = Math.max(12, Math.floor(shortEdge / 50));
          let isLetterN = true; // We will alternate between drawing N and O

          // 4. Scan the image grid for boundaries
          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              // Check if this specific coordinate hits a strong edge boundary
              const edgeValue = maskData[y * canvas.width + x];

              // If the boundary decision passes the threshold, draw a letter here
              if (edgeValue > 0.15) {
                const letterW = gridSize * 0.6;
                const letterH = gridSize * 0.8;
                const offsetX = x + gridSize * 0.2;
                const offsetY = y + gridSize * 0.1;

                if (isLetterN) {
                  // Draw "N"
                  ctx.beginPath();
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.stroke();
                } else {
                  // Draw "O"
                  ctx.beginPath();
                  ctx.rect(offsetX, offsetY, letterW, letterH);
                  ctx.stroke();
                }

                // Toggle to the other letter for the next boundary hit
                isLetterN = !isLetterN;
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 19. HEBREW LETTER KIT (Latin + Hebrew Edge Matrix)
      // ==========================================
      if (activeKit === "letterhebrewkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Add a subtle white wash so the dark blue stands out
        // ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // Laplacian Edge Detection
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)"; // Dark Blue
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          // Expand the grid size slightly to fit two letters side-by-side
          const shortEdge = Math.min(canvas.width, canvas.height);
          const gridSize = Math.max(12, Math.floor(shortEdge / 50));
          let isLetterN = true;

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              const edgeValue = maskData[y * canvas.width + x];

              if (edgeValue > 0.15) {
                const letterW = gridSize * 0.35;
                const letterH = gridSize * 0.7;
                const spacing = gridSize * 0.15;

                const offsetX = x + gridSize * 0.1;
                const offsetY = y + gridSize * 0.15;
                const hebX = offsetX + letterW + spacing; // Hebrew letter positioned to the right

                if (isLetterN) {
                  // --- Draw Latin "N" ---
                  ctx.beginPath();
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.stroke();

                  // --- Draw Hebrew "Nun" (נ) ---
                  // Fixed: Stem is now on the RIGHT side, with the base extending to the left
                  ctx.beginPath();
                  ctx.moveTo(hebX + letterW * 0.2, offsetY); // Top left
                  ctx.lineTo(hebX + letterW * 0.8, offsetY); // Top bar to right
                  ctx.lineTo(hebX + letterW * 0.8, offsetY + letterH); // Stem down on the right
                  ctx.lineTo(hebX, offsetY + letterH); // Base bar extending left
                  ctx.stroke();
                } else {
                  // --- Draw Latin "O" ---
                  ctx.beginPath();
                  ctx.rect(offsetX, offsetY, letterW, letterH);
                  ctx.stroke();

                  // --- Draw Hebrew "Vav" (ו) ---
                  // Fixed: Stem on the right with a small top bar
                  ctx.beginPath();
                  ctx.moveTo(hebX + letterW * 0.3, offsetY); // Top left
                  ctx.lineTo(hebX + letterW * 0.7, offsetY); // Top bar to right
                  ctx.lineTo(hebX + letterW * 0.7, offsetY + letterH); // Stem down on the right
                  ctx.stroke();
                }

                isLetterN = !isLetterN;
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 20. HEBREW TEXT KIT (Vector Latin + Rendered Hebrew Text)
      // ==========================================
      if (activeKit === "letterhebrewtextkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)";
          ctx.fillStyle = "rgba(0, 0, 139, 0.9)"; // Fill style for the text
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const shortEdge = Math.min(canvas.width, canvas.height);
          const gridSize = Math.max(12, Math.floor(shortEdge / 50));

          // Dynamically size the font to match the grid
          const fontSize = Math.floor(gridSize * 0.8);
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.textBaseline = "bottom";

          let isLetterN = true;

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              if (maskData[y * canvas.width + x] > 0.15) {
                const letterW = gridSize * 0.4;
                const letterH = gridSize * 0.7;
                const spacing = gridSize * 0.15;

                const offsetX = x + gridSize * 0.1;
                const offsetY = y + gridSize * 0.15;
                const textX = offsetX + letterW + spacing;

                if (isLetterN) {
                  // Draw Vector Latin "N"
                  ctx.beginPath();
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.stroke();

                  // Render Hebrew Text "Nun" (נ)
                  ctx.fillText("נ", textX, offsetY + letterH + fontSize * 0.1);
                } else {
                  // Draw Vector Latin "O"
                  ctx.beginPath();
                  ctx.rect(offsetX, offsetY, letterW, letterH);
                  ctx.stroke();

                  // Render Hebrew Text "Vav" (ו)
                  ctx.fillText("ו", textX, offsetY + letterH + fontSize * 0.1);
                }

                isLetterN = !isLetterN;
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 21. GREEK TEXT KIT (Vector Latin + Rendered Greek Text)
      // ==========================================
      if (activeKit === "lettergreektextkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)";
          ctx.fillStyle = "rgba(0, 0, 139, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const shortEdge = Math.min(canvas.width, canvas.height);
          const gridSize = Math.max(12, Math.floor(shortEdge / 50));

          const fontSize = Math.floor(gridSize * 0.8);
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.textBaseline = "bottom";

          let isLetterN = true;

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              if (maskData[y * canvas.width + x] > 0.15) {
                const letterW = gridSize * 0.4;
                const letterH = gridSize * 0.7;
                const spacing = gridSize * 0.15;

                const offsetX = x + gridSize * 0.1;
                const offsetY = y + gridSize * 0.15;
                const textX = offsetX + letterW + spacing;

                if (isLetterN) {
                  // Draw Vector Latin "N"
                  ctx.beginPath();
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.stroke();

                  // Render Greek Text "Nu" (ν)
                  ctx.fillText("ν", textX, offsetY + letterH + fontSize * 0.1);
                } else {
                  // Draw Vector Latin "O"
                  ctx.beginPath();
                  ctx.rect(offsetX, offsetY, letterW, letterH);
                  ctx.stroke();

                  // Render Greek Text "Omicron" (ο)
                  ctx.fillText("ο", textX, offsetY + letterH + fontSize * 0.1);
                }

                isLetterN = !isLetterN;
              }
            }
          }
        });

        baseTensor.dispose();
      }
      // ==========================================
      // 20. HEBREW TEXT KIT (Original Matrix + Hebrew Overlay)
      // ==========================================
      if (activeKit === "letterhebrewtextkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)";
          ctx.fillStyle = "rgba(0, 0, 139, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          // EXACT SAME GRID AS ORIGINAL LETTERKIT
          const gridSize = Math.max(12, Math.floor(canvas.width / 50));

          // Size the font to overlay cleanly without warping the grid
          const fontSize = Math.floor(gridSize * 0.85);
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.textBaseline = "bottom";

          let isLetterN = true;

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              if (maskData[y * canvas.width + x] > 0.15) {
                // Exact same sizing as original letterkit
                const letterW = gridSize * 0.6;
                const letterH = gridSize * 0.8;
                const offsetX = x + gridSize * 0.2;
                const offsetY = y + gridSize * 0.1;

                // Position text just slightly outside the vector shape
                const textX = offsetX + letterW + 2;

                if (isLetterN) {
                  // Draw Vector Latin "N"
                  ctx.beginPath();
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.stroke();

                  // Overlay Hebrew Text "Nun" (נ)
                  ctx.fillText("נ", textX, offsetY + letterH);
                } else {
                  // Draw Vector Latin "O"
                  ctx.beginPath();
                  ctx.rect(offsetX, offsetY, letterW, letterH);
                  ctx.stroke();

                  // Overlay Hebrew Text "Vav" (ו)
                  ctx.fillText("ו", textX, offsetY + letterH);
                }

                isLetterN = !isLetterN;
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 21. GREEK TEXT KIT (Original Matrix + Greek Overlay)
      // ==========================================
      // ==========================================
      // 38. GHOST KIT (White Point Extraction & Sigmoid Color Vectors)
      // ==========================================
      if (activeKit === "ghost") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Deep dark background to force maximum white-to-black contrast
        // ctx.fillStyle = "rgba(5, 5, 5, 0.85)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // Calculate standard perceived light (Luminance)
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // --- 1. EXTRACT WHITEST POINTS ---
          // Pure white has high luminance AND practically zero variance between R, G, and B.
          const rgDiff = r.sub(g).abs();
          const rbDiff = r.sub(b).abs();
          const gbDiff = g.sub(b).abs();
          const colorVariance = rgDiff.add(rbDiff).add(gbDiff).squeeze();

          // Trap the white points (Brightness > 0.75, Color Variance < 0.2)
          const isWhite = tf.cast(
            luma
              .greater(tf.scalar(0.75))
              .logicalAnd(colorVariance.lessEqual(tf.scalar(0.2))),
            "float32"
          );

          // --- 2. EXTRACT & HIGHLIGHT COLOR ---
          // Use ReLU to act as a noise floor, killing dull gray/brown colors and keeping vibrant ones
          const colorRelu = tf.relu(colorVariance.sub(tf.scalar(0.25)));

          // Use Sigmoid to violently snap those surviving colors into highly defined geometric nodes
          const colorSigmoid = tf.sigmoid(colorRelu.mul(tf.scalar(15.0)));

          // Pull tensor data to Javascript memory for canvas rendering
          const whiteData = isWhite.dataSync();
          const colorData = colorSigmoid.dataSync();

          // Pull raw RGB data so we can dynamically paint the vectors with their true colors
          const rData = r.squeeze().dataSync();
          const gData = g.squeeze().dataSync();
          const bData = b.squeeze().dataSync();

          const w = canvas.width;
          const h = canvas.height;
          const stride = Math.max(6, Math.floor(w / 60)); // Resolution scaler

          const whitePoints = [];
          const colorPoints = [];

          // Scan the matrix
          for (let y = 0; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;

              if (whiteData[idx] === 1) {
                whitePoints.push({ x, y });
              }

              if (colorData[idx] > 0.8) {
                // Save the exact RGB values of the color instance
                colorPoints.push({
                  x,
                  y,
                  r: Math.floor(rData[idx] * 255),
                  g: Math.floor(gData[idx] * 255),
                  b: Math.floor(bData[idx] * 255),
                });
              }
            }
          }

          // --- 3. DRAW THE GHOSTLY WHITE MATRIX ---
          ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
          ctx.lineWidth = 1;
          const maxWhiteDist = stride * 3.5; // Short reach for structural white-to-white webbing

          whitePoints.forEach((p1, i) => {
            // Core white nodes
            ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
            ctx.fillRect(p1.x, p1.y, 2, 2);

            // Connect neighboring white points to form the pale structural base
            for (let j = i + 1; j < whitePoints.length; j++) {
              const p2 = whitePoints[j];
              const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
              if (dist < maxWhiteDist) {
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
              }
            }
          });

          // --- 4. DRAW THE COLOR INSTANCE VECTORS ---
          ctx.lineWidth = 1.5;
          const maxColorDist = stride * 8; // Longer reach to pull color out of the white void

          colorPoints.forEach((cp) => {
            // Draw the vibrant color instance node
            ctx.fillStyle = `rgb(${cp.r}, ${cp.g}, ${cp.b})`;
            ctx.beginPath();
            ctx.arc(cp.x, cp.y, 2.5, 0, 2 * Math.PI);
            ctx.fill();

            // Extend vectors from the white structure OUT to the color instance
            whitePoints.forEach((wp) => {
              const dist = Math.hypot(cp.x - wp.x, cp.y - wp.y);
              if (dist < maxColorDist) {
                // Gradient vector: Fades from Ghostly White to True Neon Color
                const grad = ctx.createLinearGradient(wp.x, wp.y, cp.x, cp.y);
                grad.addColorStop(0, "rgba(255, 255, 255, 0.05)");
                grad.addColorStop(1, `rgba(${cp.r}, ${cp.g}, ${cp.b}, 0.95)`);

                ctx.strokeStyle = grad;
                ctx.beginPath();
                ctx.moveTo(wp.x, wp.y);
                ctx.lineTo(cp.x, cp.y);
                ctx.stroke();
              }
            });
          });
        });
        baseTensor.dispose();
      }
      if (activeKit === "lettergreektextkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)";
          ctx.fillStyle = "rgba(0, 0, 139, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          // EXACT SAME GRID AS ORIGINAL LETTERKIT
          const gridSize = Math.max(12, Math.floor(canvas.width / 50));

          const fontSize = Math.floor(gridSize * 0.85);
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.textBaseline = "bottom";

          let isLetterN = true;

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              if (maskData[y * canvas.width + x] > 0.15) {
                // Exact same sizing as original letterkit
                const letterW = gridSize * 0.6;
                const letterH = gridSize * 0.8;
                const offsetX = x + gridSize * 0.2;
                const offsetY = y + gridSize * 0.1;

                // Position text just slightly outside the vector shape
                const textX = offsetX + letterW + 2;

                if (isLetterN) {
                  // Draw Vector Latin "N"
                  ctx.beginPath();
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.stroke();

                  // Overlay Greek Text "Nu" (ν)
                  ctx.fillText("ν", textX, offsetY + letterH);
                } else {
                  // Draw Vector Latin "O"
                  ctx.beginPath();
                  ctx.rect(offsetX, offsetY, letterW, letterH);
                  ctx.stroke();

                  // Overlay Greek Text "Omicron" (ο)
                  ctx.fillText("ο", textX, offsetY + letterH);
                }

                isLetterN = !isLetterN;
              }
            }
          }
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 22. WORD KIND KIT (Boundary Matrix Spelling 'K I N D')
      // ==========================================
      if (activeKit === "wordkindkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Add a subtle white wash so the dark blue stands out
        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // Laplacian Edge Detection
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)"; // Dark Blue
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const gridSize = Math.max(12, Math.floor(canvas.width / 50));
          let letterCycle = 0; // Cycles 0 to 3 for K, I, N, D

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              const edgeValue = maskData[y * canvas.width + x];

              if (edgeValue > 0.15) {
                const letterW = gridSize * 0.6;
                const letterH = gridSize * 0.8;
                const offsetX = x + gridSize * 0.2;
                const offsetY = y + gridSize * 0.1;

                ctx.beginPath();

                if (letterCycle === 0) {
                  // --- Draw "K" ---
                  ctx.moveTo(offsetX, offsetY);
                  ctx.lineTo(offsetX, offsetY + letterH);
                  ctx.moveTo(offsetX, offsetY + letterH * 0.5);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.moveTo(offsetX, offsetY + letterH * 0.5);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                } else if (letterCycle === 1) {
                  // --- Draw "I" ---
                  ctx.moveTo(offsetX + letterW * 0.5, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.5, offsetY + letterH);
                  ctx.moveTo(offsetX + letterW * 0.2, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.8, offsetY);
                  ctx.moveTo(offsetX + letterW * 0.2, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW * 0.8, offsetY + letterH);
                } else if (letterCycle === 2) {
                  // --- Draw "N" ---
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                } else if (letterCycle === 3) {
                  // --- Draw "D" (Geometric Boxy Style) ---
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.7, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH * 0.3);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH * 0.7);
                  ctx.lineTo(offsetX + letterW * 0.7, offsetY + letterH);
                  ctx.closePath();
                }

                ctx.stroke();

                // Increment and loop back to 0 after 3
                letterCycle = (letterCycle + 1) % 4;
              }
            }
          }
        });

        baseTensor.dispose();
      }
      // ==========================================
      // 23. WORD HIDE KIT (Boundary Matrix Spelling 'H I D E')
      // ==========================================
      if (activeKit === "wordhidekit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const gridSize = Math.max(12, Math.floor(canvas.width / 50));
          let letterCycle = 0; // Cycles 0 to 3 for H, I, D, E

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              if (maskData[y * canvas.width + x] > 0.15) {
                const letterW = gridSize * 0.6;
                const letterH = gridSize * 0.8;
                const offsetX = x + gridSize * 0.2;
                const offsetY = y + gridSize * 0.1;

                ctx.beginPath();

                if (letterCycle === 0) {
                  // --- Draw "H" ---
                  ctx.moveTo(offsetX, offsetY);
                  ctx.lineTo(offsetX, offsetY + letterH);
                  ctx.moveTo(offsetX + letterW, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.moveTo(offsetX, offsetY + letterH * 0.5);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH * 0.5);
                } else if (letterCycle === 1) {
                  // --- Draw "I" ---
                  ctx.moveTo(offsetX + letterW * 0.5, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.5, offsetY + letterH);
                  ctx.moveTo(offsetX + letterW * 0.2, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.8, offsetY);
                  ctx.moveTo(offsetX + letterW * 0.2, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW * 0.8, offsetY + letterH);
                } else if (letterCycle === 2) {
                  // --- Draw "D" ---
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.7, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH * 0.3);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH * 0.7);
                  ctx.lineTo(offsetX + letterW * 0.7, offsetY + letterH);
                  ctx.closePath();
                } else if (letterCycle === 3) {
                  // --- Draw "E" ---
                  ctx.moveTo(offsetX + letterW, offsetY);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.moveTo(offsetX, offsetY + letterH * 0.5);
                  ctx.lineTo(offsetX + letterW * 0.7, offsetY + letterH * 0.5);
                }

                ctx.stroke();
                letterCycle = (letterCycle + 1) % 4;
              }
            }
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // 24. WORD ANIMAL KIT (Boundary Matrix Spelling 'A N I M A L')
      // ==========================================
      if (activeKit === "wordanimalkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(255, 255, 255, 0.3)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const maskData = edges.dataSync();

          ctx.strokeStyle = "rgba(0, 0, 139, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const gridSize = Math.max(12, Math.floor(canvas.width / 50));
          let letterCycle = 0; // Cycles 0 to 5 for A, N, I, M, A, L

          for (let y = 0; y < canvas.height - gridSize; y += gridSize) {
            for (let x = 0; x < canvas.width - gridSize; x += gridSize) {
              if (maskData[y * canvas.width + x] > 0.15) {
                const letterW = gridSize * 0.6;
                const letterH = gridSize * 0.8;
                const offsetX = x + gridSize * 0.2;
                const offsetY = y + gridSize * 0.1;

                ctx.beginPath();

                if (letterCycle === 0 || letterCycle === 4) {
                  // --- Draw "A" ---
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW * 0.5, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.moveTo(offsetX + letterW * 0.25, offsetY + letterH * 0.5);
                  ctx.lineTo(offsetX + letterW * 0.75, offsetY + letterH * 0.5);
                } else if (letterCycle === 1) {
                  // --- Draw "N" ---
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY);
                } else if (letterCycle === 2) {
                  // --- Draw "I" ---
                  ctx.moveTo(offsetX + letterW * 0.5, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.5, offsetY + letterH);
                  ctx.moveTo(offsetX + letterW * 0.2, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.8, offsetY);
                  ctx.moveTo(offsetX + letterW * 0.2, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW * 0.8, offsetY + letterH);
                } else if (letterCycle === 3) {
                  // --- Draw "M" ---
                  ctx.moveTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX, offsetY);
                  ctx.lineTo(offsetX + letterW * 0.5, offsetY + letterH * 0.5);
                  ctx.lineTo(offsetX + letterW, offsetY);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                } else if (letterCycle === 5) {
                  // --- Draw "L" ---
                  ctx.moveTo(offsetX, offsetY);
                  ctx.lineTo(offsetX, offsetY + letterH);
                  ctx.lineTo(offsetX + letterW, offsetY + letterH);
                }

                ctx.stroke();
                letterCycle = (letterCycle + 1) % 6; // Loop back to 0 after 5
              }
            }
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // 25. WORD KIND CONTOUR KIT (Dynamic Letter-Based Stride)
      // ==========================================
      if (activeKit === "wordkindcontourkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 15, 25, 0.6)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();
          const lumaData = luma.dataSync();
          const w = canvas.width;

          ctx.strokeStyle = "rgba(0, 255, 255, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          // Base scale derived from the word length to make it unique to "KIND"
          const baseScale = Math.max(14, Math.floor(canvas.width / 40));
          let letterCycle = 0;

          let y = baseScale;
          while (y < canvas.height - baseScale) {
            let x = baseScale;

            while (x < canvas.width - baseScale) {
              const idx = Math.floor(y) * w + Math.floor(x);
              const edgeValue = maskData[idx];

              // Define specific widths for K, I, N, D to alter the stride dynamically
              let currentLetterWidth = baseScale * 0.6;
              if (letterCycle === 1) currentLetterWidth = baseScale * 0.3; // "I" is narrower!
              if (letterCycle === 3) currentLetterWidth = baseScale * 0.7; // "D" is wider!

              if (edgeValue > 0.15) {
                const right =
                  lumaData[Math.floor(y) * w + (Math.floor(x) + 1)] || 0;
                const left =
                  lumaData[Math.floor(y) * w + (Math.floor(x) - 1)] || 0;
                const bottom =
                  lumaData[(Math.floor(y) + 1) * w + Math.floor(x)] || 0;
                const top =
                  lumaData[(Math.floor(y) - 1) * w + Math.floor(x)] || 0;

                const angle =
                  Math.atan2(bottom - top, right - left) + Math.PI / 2;
                const letterH = baseScale * 0.8;
                const hw = currentLetterWidth / 2;
                const hh = letterH / 2;

                ctx.save();
                ctx.translate(x + hw, y + hh);
                ctx.rotate(angle);
                ctx.beginPath();

                if (letterCycle === 0) {
                  ctx.moveTo(-hw, -hh);
                  ctx.lineTo(-hw, hh);
                  ctx.moveTo(-hw, 0);
                  ctx.lineTo(hw, -hh);
                  ctx.moveTo(-hw, 0);
                  ctx.lineTo(hw, hh);
                } else if (letterCycle === 1) {
                  ctx.moveTo(0, -hh);
                  ctx.lineTo(0, hh);
                  ctx.moveTo(-hw, -hh);
                  ctx.lineTo(hw, -hh);
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(hw, hh);
                } else if (letterCycle === 2) {
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(-hw, -hh);
                  ctx.lineTo(hw, hh);
                  ctx.lineTo(hw, -hh);
                } else if (letterCycle === 3) {
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(-hw, -hh);
                  ctx.lineTo(hw * 0.5, -hh);
                  ctx.lineTo(hw, -hh * 0.3);
                  ctx.lineTo(hw, hh * 0.3);
                  ctx.lineTo(hw * 0.5, hh);
                  ctx.closePath();
                }

                ctx.stroke();
                ctx.restore();

                letterCycle = (letterCycle + 1) % 4;
              }

              // ADVANCE X DYNAMICALLY based on the letter's specific width + padding
              x += currentLetterWidth + baseScale * 0.4;
            }

            // ADVANCE Y based on a word-specific line height padding
            y += baseScale * 1.2;
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // 26. WORD NO CONTOUR KIT (Dynamic Stride spelling 'N O')
      // ==========================================
      if (activeKit === "wordnocontourkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 15, 25, 0.6)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();
          const lumaData = luma.dataSync();
          const w = canvas.width;

          ctx.strokeStyle = "rgba(0, 255, 255, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          // Base scale derived from the canvas width
          const baseScale = Math.max(14, Math.floor(canvas.width / 40));
          let letterCycle = 0; // Cycles 0 to 1 for N, O

          let y = baseScale;
          while (y < canvas.height - baseScale) {
            let x = baseScale;

            while (x < canvas.width - baseScale) {
              const idx = Math.floor(y) * w + Math.floor(x);
              const edgeValue = maskData[idx];

              // Dynamic widths: "O" is slightly wider than "N"
              let currentLetterWidth = baseScale * 0.6;
              if (letterCycle === 1) currentLetterWidth = baseScale * 0.75; // Wide "O"

              if (edgeValue > 0.15) {
                const right =
                  lumaData[Math.floor(y) * w + (Math.floor(x) + 1)] || 0;
                const left =
                  lumaData[Math.floor(y) * w + (Math.floor(x) - 1)] || 0;
                const bottom =
                  lumaData[(Math.floor(y) + 1) * w + Math.floor(x)] || 0;
                const top =
                  lumaData[(Math.floor(y) - 1) * w + Math.floor(x)] || 0;

                const angle =
                  Math.atan2(bottom - top, right - left) + Math.PI / 2;
                const letterH = baseScale * 0.8;
                const hw = currentLetterWidth / 2;
                const hh = letterH / 2;

                ctx.save();
                ctx.translate(x + hw, y + hh);
                ctx.rotate(angle);
                ctx.beginPath();

                if (letterCycle === 0) {
                  // --- Draw "N" ---
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(-hw, -hh);
                  ctx.lineTo(hw, hh);
                  ctx.lineTo(hw, -hh);
                } else if (letterCycle === 1) {
                  // --- Draw "O" ---
                  ctx.rect(-hw, -hh, currentLetterWidth, letterH);
                }

                ctx.stroke();
                ctx.restore();

                letterCycle = (letterCycle + 1) % 2;
              }

              // Advance stride dynamically based on letter size
              x += currentLetterWidth + baseScale * 0.4;
            }
            y += baseScale * 1.2;
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // 27. WORD ANIMAL CONTOUR KIT (Dynamic Stride spelling 'A N I M A L')
      // ==========================================
      if (activeKit === "wordanimalcontourkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 15, 25, 0.6)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();
          const lumaData = luma.dataSync();
          const w = canvas.width;

          ctx.strokeStyle = "rgba(0, 255, 255, 0.9)";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const baseScale = Math.max(14, Math.floor(canvas.width / 40));
          let letterCycle = 0; // Cycles 0 to 5 for A, N, I, M, A, L

          let y = baseScale;
          while (y < canvas.height - baseScale) {
            let x = baseScale;

            while (x < canvas.width - baseScale) {
              const idx = Math.floor(y) * w + Math.floor(x);
              const edgeValue = maskData[idx];

              // Dynamic widths based on specific letters!
              let currentLetterWidth = baseScale * 0.6; // Default for A, N
              if (letterCycle === 2) currentLetterWidth = baseScale * 0.3; // Narrow "I"
              if (letterCycle === 3) currentLetterWidth = baseScale * 0.8; // Wide "M"
              if (letterCycle === 5) currentLetterWidth = baseScale * 0.5; // Slightly narrow "L"

              if (edgeValue > 0.15) {
                const right =
                  lumaData[Math.floor(y) * w + (Math.floor(x) + 1)] || 0;
                const left =
                  lumaData[Math.floor(y) * w + (Math.floor(x) - 1)] || 0;
                const bottom =
                  lumaData[(Math.floor(y) + 1) * w + Math.floor(x)] || 0;
                const top =
                  lumaData[(Math.floor(y) - 1) * w + Math.floor(x)] || 0;

                const angle =
                  Math.atan2(bottom - top, right - left) + Math.PI / 2;
                const letterH = baseScale * 0.8;
                const hw = currentLetterWidth / 2;
                const hh = letterH / 2;

                ctx.save();
                ctx.translate(x + hw, y + hh);
                ctx.rotate(angle);
                ctx.beginPath();

                if (letterCycle === 0 || letterCycle === 4) {
                  // --- Draw "A" ---
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(0, -hh);
                  ctx.lineTo(hw, hh);
                  ctx.moveTo(-hw * 0.5, 0);
                  ctx.lineTo(hw * 0.5, 0);
                } else if (letterCycle === 1) {
                  // --- Draw "N" ---
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(-hw, -hh);
                  ctx.lineTo(hw, hh);
                  ctx.lineTo(hw, -hh);
                } else if (letterCycle === 2) {
                  // --- Draw "I" ---
                  ctx.moveTo(0, -hh);
                  ctx.lineTo(0, hh);
                  ctx.moveTo(-hw, -hh);
                  ctx.lineTo(hw, -hh);
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(hw, hh);
                } else if (letterCycle === 3) {
                  // --- Draw "M" ---
                  ctx.moveTo(-hw, hh);
                  ctx.lineTo(-hw, -hh);
                  ctx.lineTo(0, 0);
                  ctx.lineTo(hw, -hh);
                  ctx.lineTo(hw, hh);
                } else if (letterCycle === 5) {
                  // --- Draw "L" ---
                  ctx.moveTo(-hw, -hh);
                  ctx.lineTo(-hw, hh);
                  ctx.lineTo(hw, hh);
                }

                ctx.stroke();
                ctx.restore();

                letterCycle = (letterCycle + 1) % 6;
              }

              // Advance stride dynamically
              x += currentLetterWidth + baseScale * 0.4;
            }
            y += baseScale * 1.2;
          }
        });
        baseTensor.dispose();
      }

      ////

      // ==========================================
      // 28. CUSTOM TEXT CONTOUR KIT (Full Alphabet Dictionary + Dynamic Form)
      // ==========================================

      // ==========================================
      // 28. CUSTOM TEXT CONTOUR KIT (Full Alphabet Dictionary + Dynamic Form)
      // ==========================================
      if (activeKit === "customtextcontourkit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height); // <-- FIXED LINE
        const baseTensor = tf.browser.fromPixels(canvas);

        // Grab the text from the DOM input, default to 'CYBER' if empty
        const inputElement = document.getElementById("customMatrixInput");
        const rawText =
          inputElement && inputElement.value ? inputElement.value : "CYBER";
        // Sanitize to uppercase letters and spaces only
        const word = rawText.toUpperCase().replace(/[^A-Z ]/g, "");
        const fallbackWord = word.length > 0 ? word : "CYBER";

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.72)
            .add(g.mul(0.42))
            .add(b.mul(0.52))
            .add(r.mul(0.14))
            .squeeze();

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          const maskData = edges.dataSync();
          const lumaData = luma.dataSync();
          const w = canvas.width;

          ctx.strokeStyle = "rgba(71, 0, 255, 1.00)"; // Magenta to match the button!
          ctx.lineWidth = 3;
          ctx.lineCap = "square";
          ctx.lineJoin = "miter";

          const baseScale = Math.max(14, Math.floor(canvas.width / 40));
          let letterCycle = 0;

          let y = baseScale;
          while (y < canvas.height - baseScale) {
            let x = baseScale;

            while (x < canvas.width - baseScale) {
              const idx = Math.floor(y) * w + Math.floor(x);
              const edgeValue = maskData[idx];

              const char = fallbackWord[letterCycle];
              let currentLetterWidth = baseScale * 0.6; // Default width

              // Custom widths for specific letters
              if (char === "I") currentLetterWidth = baseScale * 0.3;
              if (char === "W" || char === "M")
                currentLetterWidth = baseScale * 0.8;
              if (char === " ") currentLetterWidth = baseScale * 0.4; // Space character padding

              if (edgeValue > 0.15 && char !== " ") {
                const right =
                  lumaData[Math.floor(y) * w + (Math.floor(x) + 1)] || 0;
                const left =
                  lumaData[Math.floor(y) * w + (Math.floor(x) - 1)] || 0;
                const bottom =
                  lumaData[(Math.floor(y) + 1) * w + Math.floor(x)] || 0;
                const top =
                  lumaData[(Math.floor(y) - 1) * w + Math.floor(x)] || 0;

                const angle =
                  Math.atan2(bottom - top, right - left) + Math.PI / 2;
                const letterH = baseScale * 0.8;
                const hw = currentLetterWidth / 2;
                const hh = letterH / 2;

                ctx.save();
                ctx.translate(x + hw, y + hh);
                ctx.rotate(angle);
                ctx.beginPath();

                // THE MASTER ALPHABET DICTIONARY
                switch (char) {
                  case "A":
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(0, -hh);
                    ctx.lineTo(hw, hh);
                    ctx.moveTo(-hw * 0.5, 0);
                    ctx.lineTo(hw * 0.5, 0);
                    break;
                  case "B":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw * 0.7, hh);
                    ctx.lineTo(hw, hh * 0.5);
                    ctx.lineTo(hw * 0.7, 0);
                    ctx.lineTo(hw, -hh * 0.5);
                    ctx.lineTo(hw * 0.7, -hh);
                    ctx.closePath();
                    ctx.moveTo(-hw, 0);
                    ctx.lineTo(hw * 0.7, 0);
                    break;
                  case "C":
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    break;
                  case "D":
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(hw * 0.5, -hh);
                    ctx.lineTo(hw, -hh * 0.3);
                    ctx.lineTo(hw, hh * 0.3);
                    ctx.lineTo(hw * 0.5, hh);
                    ctx.closePath();
                    break;
                  case "E":
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    ctx.moveTo(-hw, 0);
                    ctx.lineTo(hw * 0.7, 0);
                    break;
                  case "F":
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.moveTo(-hw, 0);
                    ctx.lineTo(hw * 0.7, 0);
                    break;
                  case "G":
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    ctx.lineTo(hw, 0);
                    ctx.lineTo(0, 0);
                    break;
                  case "H":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(hw, hh);
                    ctx.moveTo(-hw, 0);
                    ctx.lineTo(hw, 0);
                    break;
                  case "I":
                    ctx.moveTo(0, -hh);
                    ctx.lineTo(0, hh);
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(hw, -hh);
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    break;
                  case "J":
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(hw, hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(-hw, 0);
                    break;
                  case "K":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.moveTo(-hw, 0);
                    ctx.lineTo(hw, -hh);
                    ctx.moveTo(-hw, 0);
                    ctx.lineTo(hw, hh);
                    break;
                  case "L":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    break;
                  case "M":
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(0, 0);
                    ctx.lineTo(hw, -hh);
                    ctx.lineTo(hw, hh);
                    break;
                  case "N":
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(hw, hh);
                    ctx.lineTo(hw, -hh);
                    break;
                  case "O":
                    ctx.rect(-hw, -hh, currentLetterWidth, letterH);
                    break;
                  case "P":
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(hw, -hh);
                    ctx.lineTo(hw, 0);
                    ctx.lineTo(-hw, 0);
                    break;
                  case "Q":
                    ctx.rect(-hw, -hh, currentLetterWidth, letterH);
                    ctx.moveTo(0, 0);
                    ctx.lineTo(hw, hh);
                    break;
                  case "R":
                    ctx.moveTo(-hw, hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(hw, -hh);
                    ctx.lineTo(hw, 0);
                    ctx.lineTo(-hw, 0);
                    ctx.moveTo(0, 0);
                    ctx.lineTo(hw, hh);
                    break;
                  case "S":
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(-hw, -hh);
                    ctx.lineTo(-hw, 0);
                    ctx.lineTo(hw, 0);
                    ctx.lineTo(hw, hh);
                    ctx.lineTo(-hw, hh);
                    break;
                  case "T":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(hw, -hh);
                    ctx.moveTo(0, -hh);
                    ctx.lineTo(0, hh);
                    break;
                  case "U":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    ctx.lineTo(hw, -hh);
                    break;
                  case "V":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(0, hh);
                    ctx.lineTo(hw, -hh);
                    break;
                  case "W":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(0, 0);
                    ctx.lineTo(hw, hh);
                    ctx.lineTo(hw, -hh);
                    break;
                  case "X":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(hw, hh);
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(-hw, hh);
                    break;
                  case "Y":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(0, 0);
                    ctx.moveTo(hw, -hh);
                    ctx.lineTo(0, 0);
                    ctx.lineTo(0, hh);
                    break;
                  case "Z":
                    ctx.moveTo(-hw, -hh);
                    ctx.lineTo(hw, -hh);
                    ctx.lineTo(-hw, hh);
                    ctx.lineTo(hw, hh);
                    break;
                }

                ctx.stroke();
                ctx.restore();
              }

              // Move letter cycle forward
              letterCycle = (letterCycle + 1) % fallbackWord.length;

              // Advance stride dynamically based on the letter's specific width + padding
              x += currentLetterWidth + baseScale * 0.4;
            }

            // Advance Y based on a word-specific line height padding
            y += baseScale * 1.2;
          }
        });
        baseTensor.dispose();
      }

      // if (activeKit === "customtextcontourkit") {
      //   ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      //   const baseTensor = tf.browser.fromPixels(canvas);

      //   // ctx.fillStyle = "rgba(0, 15, 25, 0.6)";
      //   // ctx.fillRect(0, 0, canvas.width, canvas.height);

      //   // Grab the text from the DOM input, default to 'CYBER' if empty
      //   const inputElement = document.getElementById("customMatrixInput");
      //   const rawText =
      //     inputElement && inputElement.value ? inputElement.value : "CYBER";
      //   // Sanitize to uppercase letters and spaces only
      //   const word = rawText.toUpperCase().replace(/[^A-Z ]/g, "");
      //   const fallbackWord = word.length > 0 ? word : "CYBER";

      //   tf.tidy(() => {
      //     const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
      //     const [r, g, b] = tf.split(normalized, 3, 2);
      //     const luma = r
      //       .mul(0.72)
      //       .add(g.mul(0.42))
      //       .add(b.mul(0.52))
      //       .add(r.mul(0.14))
      //       .squeeze();

      //     const edgeKernel = tf.tensor4d(
      //       [0, 1, 0, 1, -4, 1, 0, 1, 0],
      //       [3, 3, 1, 1]
      //     );
      //     const expandedLuma = luma.expandDims(0).expandDims(-1);
      //     const edges = tf
      //       .conv2d(expandedLuma, edgeKernel, 1, "same")
      //       .abs()
      //       .squeeze();

      //     const maskData = edges.dataSync();
      //     const lumaData = luma.dataSync();
      //     const w = canvas.width;

      //     ctx.strokeStyle = "rgba(71, 0, 255, 1.00)"; // Magenta to match the button!
      //     ctx.lineWidth = 3;
      //     ctx.lineCap = "square";
      //     ctx.lineJoin = "miter";

      //     const baseScale = Math.max(14, Math.floor(canvas.width / 40));
      //     let letterCycle = 0;

      //     let y = baseScale;
      //     while (y < canvas.height - baseScale) {
      //       let x = baseScale;

      //       while (x < canvas.width - baseScale) {
      //         const idx = Math.floor(y) * w + Math.floor(x);
      //         const edgeValue = maskData[idx];

      //         const char = fallbackWord[letterCycle];
      //         let currentLetterWidth = baseScale * 0.6; // Default width

      //         // Custom widths for specific letters
      //         if (char === "I") currentLetterWidth = baseScale * 0.3;
      //         if (char === "W" || char === "M")
      //           currentLetterWidth = baseScale * 0.8;
      //         if (char === " ") currentLetterWidth = baseScale * 0.4; // Space character padding

      //         if (edgeValue > 0.15 && char !== " ") {
      //           const right =
      //             lumaData[Math.floor(y) * w + (Math.floor(x) + 1)] || 0;
      //           const left =
      //             lumaData[Math.floor(y) * w + (Math.floor(x) - 1)] || 0;
      //           const bottom =
      //             lumaData[(Math.floor(y) + 1) * w + Math.floor(x)] || 0;
      //           const top =
      //             lumaData[(Math.floor(y) - 1) * w + Math.floor(x)] || 0;

      //           const angle =
      //             Math.atan2(bottom - top, right - left) + Math.PI / 2;
      //           const letterH = baseScale * 0.8;
      //           const hw = currentLetterWidth / 2;
      //           const hh = letterH / 2;

      //           ctx.save();
      //           ctx.translate(x + hw, y + hh);
      //           ctx.rotate(angle);
      //           ctx.beginPath();

      //           // THE MASTER ALPHABET DICTIONARY
      //           switch (char) {
      //             case "A":
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(0, -hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.moveTo(-hw * 0.5, 0);
      //               ctx.lineTo(hw * 0.5, 0);
      //               break;
      //             case "B":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw * 0.7, hh);
      //               ctx.lineTo(hw, hh * 0.5);
      //               ctx.lineTo(hw * 0.7, 0);
      //               ctx.lineTo(hw, -hh * 0.5);
      //               ctx.lineTo(hw * 0.7, -hh);
      //               ctx.closePath();
      //               ctx.moveTo(-hw, 0);
      //               ctx.lineTo(hw * 0.7, 0);
      //               break;
      //             case "C":
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "D":
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(hw * 0.5, -hh);
      //               ctx.lineTo(hw, -hh * 0.3);
      //               ctx.lineTo(hw, hh * 0.3);
      //               ctx.lineTo(hw * 0.5, hh);
      //               ctx.closePath();
      //               break;
      //             case "E":
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.moveTo(-hw, 0);
      //               ctx.lineTo(hw * 0.7, 0);
      //               break;
      //             case "F":
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.moveTo(-hw, 0);
      //               ctx.lineTo(hw * 0.7, 0);
      //               break;
      //             case "G":
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.lineTo(hw, 0);
      //               ctx.lineTo(0, 0);
      //               break;
      //             case "H":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.moveTo(-hw, 0);
      //               ctx.lineTo(hw, 0);
      //               break;
      //             case "I":
      //               ctx.moveTo(0, -hh);
      //               ctx.lineTo(0, hh);
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(hw, -hh);
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "J":
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(-hw, 0);
      //               break;
      //             case "K":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.moveTo(-hw, 0);
      //               ctx.lineTo(hw, -hh);
      //               ctx.moveTo(-hw, 0);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "L":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "M":
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(0, 0);
      //               ctx.lineTo(hw, -hh);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "N":
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.lineTo(hw, -hh);
      //               break;
      //             case "O":
      //               ctx.rect(-hw, -hh, currentLetterWidth, letterH);
      //               break;
      //             case "P":
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(hw, -hh);
      //               ctx.lineTo(hw, 0);
      //               ctx.lineTo(-hw, 0);
      //               break;
      //             case "Q":
      //               ctx.rect(-hw, -hh, currentLetterWidth, letterH);
      //               ctx.moveTo(0, 0);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "R":
      //               ctx.moveTo(-hw, hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(hw, -hh);
      //               ctx.lineTo(hw, 0);
      //               ctx.lineTo(-hw, 0);
      //               ctx.moveTo(0, 0);
      //               ctx.lineTo(hw, hh);
      //               break;
      //             case "S":
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(-hw, -hh);
      //               ctx.lineTo(-hw, 0);
      //               ctx.lineTo(hw, 0);
      //               ctx.lineTo(hw, hh);
      //               ctx.lineTo(-hw, hh);
      //               break;
      //             case "T":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(hw, -hh);
      //               ctx.moveTo(0, -hh);
      //               ctx.lineTo(0, hh);
      //               break;
      //             case "U":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.lineTo(hw, -hh);
      //               break;
      //             case "V":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(0, hh);
      //               ctx.lineTo(hw, -hh);
      //               break;
      //             case "W":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(0, 0);
      //               ctx.lineTo(hw, hh);
      //               ctx.lineTo(hw, -hh);
      //               break;
      //             case "X":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(hw, hh);
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               break;
      //             case "Y":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(0, 0);
      //               ctx.moveTo(hw, -hh);
      //               ctx.lineTo(0, 0);
      //               ctx.lineTo(0, hh);
      //               break;
      //             case "Z":
      //               ctx.moveTo(-hw, -hh);
      //               ctx.lineTo(hw, -hh);
      //               ctx.lineTo(-hw, hh);
      //               ctx.lineTo(hw, hh);
      //               break;
      //           }

      //           ctx.stroke();
      //           ctx.restore();
      //         }

      //         // Move letter cycle forward
      //         letterCycle = (letterCycle + 1) % fallbackWord.length;

      //         // Advance stride dynamically
      //         x += currentLetterWidth + baseScale * 0.4;
      //       }
      //       y += baseScale * 1.2;
      //     }
      //   });
      //   baseTensor.dispose();
      // }

      // ==========================================
      // 29. ROAD KIT (Asphalt / Concrete Topography)
      // ==========================================
      if (activeKit === "road") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        tf.tidy(() => {
          const gridHeight = 70;
          const gridWidth = Math.floor(
            (canvas.width / canvas.height) * gridHeight
          );

          const resizedImage = tf.image.resizeBilinear(baseTensor, [
            gridHeight,
            gridWidth,
          ]);
          const normalized = resizedImage.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // Calculate Luma for brightness
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // ASPHALT LOGIC: R, G, and B must be very close to each other (low saturation/gray)
          const isGray = r
            .sub(g)
            .abs()
            .lessEqual(tf.scalar(0.15))
            .logicalAnd(r.sub(b).abs().lessEqual(tf.scalar(0.15)));

          // Focus only on mid-tones (ignores bright skies and dark shadows)
          const isMidTone = luma
            .greater(tf.scalar(0.15))
            .logicalAnd(luma.lessEqual(tf.scalar(0.6)));

          // Combine them to isolate the street
          const isRoad = isGray.logicalAnd(isMidTone);
          const maskData = tf.cast(isRoad, "float32").dataSync();

          const scaleX = canvas.width / gridWidth;
          const scaleY = canvas.height / gridHeight;

          ctx.strokeStyle = "rgba(255, 165, 0, 0.9)"; // Neon Construction Orange
          ctx.lineWidth = 1.5;

          for (let y = 0; y < gridHeight - 1; y++) {
            for (let x = 0; x < gridWidth - 1; x++) {
              if (maskData[y * gridWidth + x] === 1) {
                const right = maskData[y * gridWidth + (x + 1)];
                const down = maskData[(y + 1) * gridWidth + x];

                const px = x * scaleX;
                const py = y * scaleY;
                const nextX = (x + 1) * scaleX;
                const nextY = (y + 1) * scaleY;

                ctx.beginPath();
                // Draw a topographic lattice over the road surface
                if (right === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(nextX, py);
                }
                if (down === 1) {
                  ctx.moveTo(px, py);
                  ctx.lineTo(px, nextY);
                }
                ctx.stroke();
              }
            }
          }
        });
        baseTensor.dispose();
      }
      // ==========================================
      // 30. CAR WIREFRAME KIT (Tensor-Driven Vehicle Architecture)
      // ==========================================
      if (activeKit === "car") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Dim the background to let the tensor HUD pop
        // ctx.fillStyle = "rgba(0, 10, 20, 0.75)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 1. MACRO SCANNER: Find the vehicle
        const model = await cocoSsd.load();
        const predictions = await model.detect(canvas);

        for (const pred of predictions) {
          if (
            pred.class === "car" ||
            pred.class === "truck" ||
            pred.class === "bus"
          ) {
            let [x, y, w, h] = pred.bbox;

            // Safe boundaries to prevent tensor slice crashes
            x = Math.max(0, Math.floor(x));
            y = Math.max(0, Math.floor(y));
            w = Math.min(canvas.width - x, Math.floor(w));
            h = Math.min(canvas.height - y, Math.floor(h));

            if (w <= 0 || h <= 0) continue;

            tf.tidy(() => {
              // 2. ISOLATED MICRO-TENSOR: Slice only the car for mathematical processing
              const carTensor = baseTensor.slice([y, x, 0], [h, w, 3]);
              const normalized = carTensor.toFloat().div(tf.scalar(255.0));
              const [r, g, b] = tf.split(normalized, 3, 2);
              const luma = r
                .mul(0.299)
                .add(g.mul(0.587))
                .add(b.mul(0.114))
                .squeeze();

              // 3. TIRE DETECTION (ReLU): Isolate the darkest features (ovals/wheels)
              const invertedLuma = tf.scalar(1.0).sub(luma);
              // Push through ReLU to destroy mid-tones and keep only deep shadows
              const tireEssence = tf.relu(
                invertedLuma.sub(tf.scalar(0.75)).mul(tf.scalar(4.0))
              );
              const tireData = tireEssence.dataSync();

              // 4. CHASSIS / CUBE DETECTION (Sigmoid): Structural edges
              const edgeKernel = tf.tensor4d(
                [0, 1, 0, 1, -4, 1, 0, 1, 0],
                [3, 3, 1, 1]
              );
              const expandedLuma = luma.expandDims(0).expandDims(-1);
              const edges = tf
                .conv2d(expandedLuma, edgeKernel, 1, "same")
                .abs()
                .squeeze();

              // Push through Sigmoid to sharpen the geometric lines (roof, windows, bumpers)
              const chassisEssence = tf.sigmoid(
                edges.sub(tf.scalar(0.2)).mul(tf.scalar(10.0))
              );
              const chassisData = chassisEssence.dataSync();

              // Calculate proportional stride based on the vehicle's size
              const stride = Math.max(4, Math.floor(w / 45));

              const tirePoints = [];
              const platePoints = [];

              // --- DRAW CHASSIS WIREFRAME (Cyan) ---
              ctx.strokeStyle = "rgba(0, 255, 255, 0.6)";
              ctx.lineWidth = 1;
              ctx.beginPath();
              let firstPoint = true;

              for (let cy = 0; cy < h; cy += stride) {
                for (let cx = 0; cx < w; cx += stride) {
                  const idx = cy * w + cx;
                  const canvasX = x + cx;
                  const canvasY = y + cy;

                  // Map the chassis edges
                  if (chassisData[idx] > 0.7 && cy < h * 0.85) {
                    if (firstPoint) {
                      ctx.moveTo(canvasX, canvasY);
                      firstPoint = false;
                    } else {
                      ctx.lineTo(canvasX, canvasY);
                    }
                  }

                  // Accumulate Tire Matrix (Bottom 40% of the vehicle)
                  if (cy > h * 0.6 && tireData[idx] > 0.5) {
                    tirePoints.push({ x: canvasX, y: canvasY });
                  }

                  // Accumulate License Plate Matrix (High structural contrast, bottom center)
                  if (
                    cy > h * 0.5 &&
                    cy < h * 0.9 &&
                    cx > w * 0.3 &&
                    cx < w * 0.7
                  ) {
                    if (chassisData[idx] > 0.85)
                      platePoints.push({ x: canvasX, y: canvasY });
                  }
                }
              }
              ctx.stroke(); // Render the chassis lines

              // --- DRAW TIRE OVALS (Magenta) ---
              if (tirePoints.length > 0) {
                ctx.fillStyle = "rgba(255, 0, 255, 0.8)";
                tirePoints.forEach((pt) => {
                  ctx.beginPath();
                  ctx.arc(pt.x, pt.y, stride * 0.8, 0, 2 * Math.PI);
                  ctx.fill();
                });
              }

              // --- DRAW LICENSE PLATE MATRIX (Yellow) ---
              if (platePoints.length > 0) {
                ctx.strokeStyle = "rgba(255, 255, 0, 0.9)";
                ctx.lineWidth = 2;
                ctx.beginPath();
                platePoints.forEach((pt, i) => {
                  if (i === 0) ctx.moveTo(pt.x, pt.y);
                  else ctx.lineTo(pt.x, pt.y);
                });
                ctx.stroke();
              }

              // Draw Outer Bounding Dimension Cube
              ctx.strokeStyle = "rgba(0, 255, 255, 0.4)";
              ctx.lineWidth = 1;
              ctx.strokeRect(x, y, w, h);

              // Target Readout
              ctx.fillStyle = "#00ffff";
              ctx.font = "bold 12px monospace";
              ctx.fillText(`CUBE-DIM: ${w}x${h}px`, x, y - 5);
            });
          }
        }
        baseTensor.dispose();
      }

      // ==========================================
      // 31. CAR CUBE KIT (3D Perspective Padding & Geometric Planes)
      // ==========================================
      if (activeKit === "carcube") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Dim background for the neon 3D planes to glow
        ctx.fillStyle = "rgba(0, 10, 20, 0.7)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const model = await cocoSsd.load();
        const predictions = await model.detect(canvas);

        for (const pred of predictions) {
          if (["car", "truck", "bus"].includes(pred.class)) {
            let [x, y, w, h] = pred.bbox;

            x = Math.max(0, Math.floor(x));
            y = Math.max(0, Math.floor(y));
            w = Math.min(canvas.width - x, Math.floor(w));
            h = Math.min(canvas.height - y, Math.floor(h));

            if (w <= 0 || h <= 0) continue;

            tf.tidy(() => {
              const carTensor = baseTensor.slice([y, x, 0], [h, w, 3]);
              const normalized = carTensor.toFloat().div(tf.scalar(255.0));
              const [r, g, b] = tf.split(normalized, 3, 2);
              const luma = r
                .mul(0.299)
                .add(g.mul(0.587))
                .add(b.mul(0.114))
                .squeeze();

              // 1. TENSOR ACTIVATIONS
              const edgeKernel = tf.tensor4d(
                [0, 1, 0, 1, -4, 1, 0, 1, 0],
                [3, 3, 1, 1]
              );
              const expandedLuma = luma.expandDims(0).expandDims(-1);
              const edges = tf
                .conv2d(expandedLuma, edgeKernel, 1, "same")
                .abs()
                .squeeze();
              const chassisEssence = tf.sigmoid(
                edges.sub(tf.scalar(0.15)).mul(tf.scalar(12.0))
              );

              const invertedLuma = tf.scalar(1.0).sub(luma);
              const tireEssence = tf.relu(
                invertedLuma.sub(tf.scalar(0.8)).mul(tf.scalar(5.0))
              );

              const brightSpots = tf.relu(luma.sub(tf.scalar(0.65)));
              const plateEssence = brightSpots.mul(chassisEssence);

              const chassisData = chassisEssence.dataSync();
              const tireData = tireEssence.dataSync();
              const plateData = plateEssence.dataSync();

              // 2. DYNAMIC Y-SCANNING (Find Roofline and Beltline/Hoodline)
              let maxBeltEdges = 0;
              let actualBeltY = y + h * 0.45; // Default fallback beltline
              let actualRoofY = y + h * 0.05; // Default fallback roofline
              let foundRoof = false;

              for (let cy = 0; cy < h * 0.6; cy++) {
                let edgeCount = 0;
                for (let cx = 0; cx < w; cx++) {
                  if (chassisData[cy * w + cx] > 0.6) edgeCount++;
                }
                if (!foundRoof && edgeCount > w * 0.08) {
                  actualRoofY = y + cy;
                  foundRoof = true;
                }
                if (cy > h * 0.25 && edgeCount > maxBeltEdges) {
                  maxBeltEdges = edgeCount;
                  actualBeltY = y + cy;
                }
              }

              // 3. SCAN TIRES & PLATES & FASCIA DIRECTION
              const stride = Math.max(6, Math.floor(w / 35));
              let leftEdgeDensity = 0,
                rightEdgeDensity = 0;
              const tirePoints = [];
              let pMinX = w,
                pMaxX = 0,
                pMinY = h,
                pMaxY = 0;
              let plateFound = false;

              for (let cy = 0; cy < h; cy += stride) {
                for (let cx = 0; cx < w; cx += stride) {
                  const idx = cy * w + cx;
                  const absX = x + cx;
                  const absY = y + cy;

                  if (chassisData[idx] > 0.5) {
                    if (cx < w * 0.3) leftEdgeDensity++;
                    if (cx > w * 0.7) rightEdgeDensity++;
                  }

                  if (cy > h * 0.6 && tireData[idx] > 0.4)
                    tirePoints.push({ x: absX, y: absY });

                  if (
                    cy > h * 0.5 &&
                    cy < h * 0.95 &&
                    (cx < w * 0.35 || cx > w * 0.65)
                  ) {
                    if (plateData[idx] > 0.3) {
                      plateFound = true;
                      if (cx < pMinX) pMinX = cx;
                      if (cx > pMaxX) pMaxX = cx;
                      if (cy < pMinY) pMinY = cy;
                      if (cy > pMaxY) pMaxY = cy;
                    }
                  }
                }
              }

              const isFacingRight = rightEdgeDensity > leftEdgeDensity;

              // 4. 3D CUBE PROJECTION MATH
              // We pad the car using the bounding box, then extrude a 3D depth
              const depthX = isFacingRight ? -w * 0.25 : w * 0.25;
              const depthY = -h * 0.15;

              // X-axis columns based on the bounding box
              const rearX = isFacingRight ? x : x + w;
              const frontX = isFacingRight ? x + w : x;
              const greenhouseRearX = isFacingRight
                ? x + w * 0.15
                : x + w * 0.85;
              const greenhouseFrontX = isFacingRight
                ? x + w * 0.7
                : x + w * 0.3;

              // 2D to 3D helper function
              const p2D = (cx, cy) => ({ x: cx, y: cy });
              const p3D = (cx, cy) => ({ x: cx + depthX, y: cy + depthY });

              const drawPlane = (pts, strokeColor, fillColor, label) => {
                ctx.beginPath();
                ctx.moveTo(pts[0].x, pts[0].y);
                ctx.lineTo(pts[1].x, pts[1].y);
                ctx.lineTo(pts[2].x, pts[2].y);
                ctx.lineTo(pts[3].x, pts[3].y);
                ctx.closePath();
                ctx.fillStyle = fillColor;
                ctx.fill();
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();

                if (label) {
                  ctx.fillStyle = strokeColor;
                  ctx.font = "bold 12px monospace";
                  ctx.fillText(
                    label,
                    (pts[0].x + pts[2].x) / 2 - 25,
                    (pts[0].y + pts[2].y) / 2
                  );
                }
              };

              // --- A. THE EXACT OUTER CUBE (Wireframe Skeleton) ---
              ctx.strokeStyle = "rgba(0, 255, 255, 0.3)";
              ctx.lineWidth = 1;
              ctx.strokeRect(x, y, w, h); // Front 2D Box
              ctx.strokeRect(x + depthX, y + depthY, w, h); // Back 3D Box
              // Connect corners to form the padded cube
              ctx.beginPath();
              ctx.moveTo(x, y);
              ctx.lineTo(x + depthX, y + depthY);
              ctx.moveTo(x + w, y);
              ctx.lineTo(x + w + depthX, y + depthY);
              ctx.moveTo(x, y + h);
              ctx.lineTo(x + depthX, y + h + depthY);
              ctx.moveTo(x + w, y + h);
              ctx.lineTo(x + w + depthX, y + h + depthY);
              ctx.stroke();

              // --- B. INTERNAL PLANES (Roof, Hood, Windows, Fascia) ---

              // Lower Side Plane (Cyan)
              drawPlane(
                [
                  p2D(rearX, actualBeltY),
                  p2D(frontX, actualBeltY),
                  p2D(frontX, y + h),
                  p2D(rearX, y + h),
                ],
                "rgba(0, 255, 255, 1)",
                "rgba(0, 255, 255, 0.15)",
                "LOWER SIDE"
              );

              // Upper Side Plane / Windows (Magenta)
              drawPlane(
                [
                  p2D(greenhouseRearX, actualRoofY),
                  p2D(greenhouseFrontX, actualRoofY),
                  p2D(greenhouseFrontX, actualBeltY),
                  p2D(greenhouseRearX, actualBeltY),
                ],
                "rgba(255, 0, 255, 1)",
                "rgba(255, 0, 255, 0.15)",
                "WINDOWS"
              );

              // Roof Plane Rectangle (Green - Extruded back in 3D)
              drawPlane(
                [
                  p2D(greenhouseRearX, actualRoofY),
                  p2D(greenhouseFrontX, actualRoofY),
                  p3D(greenhouseFrontX, actualRoofY),
                  p3D(greenhouseRearX, actualRoofY),
                ],
                "rgba(0, 255, 0, 1)",
                "rgba(0, 255, 0, 0.2)",
                "ROOF"
              );

              // Hood Plane Rectangle (Yellow - Extruded back in 3D)
              if (isFacingRight) {
                drawPlane(
                  [
                    p2D(greenhouseFrontX, actualBeltY),
                    p2D(frontX, actualBeltY),
                    p3D(frontX, actualBeltY),
                    p3D(greenhouseFrontX, actualBeltY),
                  ],
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.2)",
                  "HOOD"
                );
              } else {
                drawPlane(
                  [
                    p2D(frontX, actualBeltY),
                    p2D(greenhouseFrontX, actualBeltY),
                    p3D(greenhouseFrontX, actualBeltY),
                    p3D(frontX, actualBeltY),
                  ],
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.2)",
                  "HOOD"
                );
              }

              // Front Fascia Plane (Blue)
              drawPlane(
                [
                  p2D(frontX, actualBeltY),
                  p2D(frontX, y + h),
                  p3D(frontX, y + h),
                  p3D(frontX, actualBeltY),
                ],
                "rgba(0, 100, 255, 1)",
                "rgba(0, 100, 255, 0.25)",
                "FRONT"
              );

              // Rear Fascia Plane (Red)
              drawPlane(
                [
                  p2D(rearX, actualRoofY),
                  p2D(rearX, y + h),
                  p3D(rearX, y + h),
                  p3D(rearX, actualRoofY),
                ],
                "rgba(255, 0, 0, 1)",
                "rgba(255, 0, 0, 0.25)",
                "REAR"
              );

              // --- C. TIRES (Magenta Matrices) ---
              // ctx.fillStyle = "rgba(255, 0, 255, 0.9)";
              // tirePoints.forEach((p) => {
              //   ctx.beginPath();
              //   ctx.arc(p.x, p.y, stride * 0.45, 0, 2 * Math.PI);
              //   ctx.fill();
              // });

              // --- D. LICENSE PLATE (Yellow Square) ---
              if (plateFound && pMaxX - pMinX > 0) {
                const pad = stride;
                const absPMinX = x + pMinX - pad;
                const absPMaxX = x + pMaxX + pad;
                const absPMinY = y + pMinY - pad;
                const absPMaxY = y + pMaxY + pad;

                ctx.strokeStyle = "rgba(255, 255, 0, 1)";
                ctx.lineWidth = 2.5;
                ctx.strokeRect(
                  absPMinX,
                  absPMinY,
                  absPMaxX - absPMinX,
                  absPMaxY - absPMinY
                );
                // Crosshair
                ctx.beginPath();
                ctx.moveTo(absPMinX, absPMinY);
                ctx.lineTo(absPMaxX, absPMaxY);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(absPMaxX, absPMinY);
                ctx.lineTo(absPMinX, absPMaxY);
                ctx.stroke();
              }

              // --- E. CUBE HUD READOUT ---
              // ctx.fillStyle = "#00ffff";
              // ctx.font = "bold 13px monospace";
              // ctx.fillText(`3D CUBE MATRIX: ${w}x${h}px`, x, y - 8);
            });
          }
        }
        baseTensor.dispose();
      }

      // ==========================================
      // 32. TILTED CAR CUBE KIT (Rotated 3D Padding & Geometric Planes)
      // ==========================================
      if (activeKit === "carcubetilt") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 10, 20, 0.7)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const model = await cocoSsd.load();
        const predictions = await model.detect(canvas);

        for (const pred of predictions) {
          if (["car", "truck", "bus"].includes(pred.class)) {
            let [x, y, w, h] = pred.bbox;

            x = Math.max(0, Math.floor(x));
            y = Math.max(0, Math.floor(y));
            w = Math.min(canvas.width - x, Math.floor(w));
            h = Math.min(canvas.height - y, Math.floor(h));

            if (w <= 0 || h <= 0) continue;

            tf.tidy(() => {
              const carTensor = baseTensor.slice([y, x, 0], [h, w, 3]);
              const normalized = carTensor.toFloat().div(tf.scalar(255.0));
              const [r, g, b] = tf.split(normalized, 3, 2);
              const luma = r
                .mul(0.299)
                .add(g.mul(0.587))
                .add(b.mul(0.114))
                .squeeze();

              // 1. TENSOR ACTIVATIONS
              const edgeKernel = tf.tensor4d(
                [0, 1, 0, 1, -4, 1, 0, 1, 0],
                [3, 3, 1, 1]
              );
              const expandedLuma = luma.expandDims(0).expandDims(-1);
              const edges = tf
                .conv2d(expandedLuma, edgeKernel, 1, "same")
                .abs()
                .squeeze();
              const chassisEssence = tf.sigmoid(
                edges.sub(tf.scalar(0.15)).mul(tf.scalar(12.0))
              );

              const invertedLuma = tf.scalar(1.0).sub(luma);
              const tireEssence = tf.relu(
                invertedLuma.sub(tf.scalar(0.8)).mul(tf.scalar(5.0))
              );

              const brightSpots = tf.relu(luma.sub(tf.scalar(0.65)));
              const plateEssence = brightSpots.mul(chassisEssence);

              const chassisData = chassisEssence.dataSync();
              const tireData = tireEssence.dataSync();
              const plateData = plateEssence.dataSync();

              // 2. DYNAMIC Y-SCANNING (Find raw Roofline and Beltline)
              let maxBeltEdges = 0;
              let actualBeltY = y + h * 0.45;
              let actualRoofY = y + h * 0.05;
              let foundRoof = false;

              for (let cy = 0; cy < h * 0.6; cy++) {
                let edgeCount = 0;
                for (let cx = 0; cx < w; cx++) {
                  if (chassisData[cy * w + cx] > 0.6) edgeCount++;
                }
                if (!foundRoof && edgeCount > w * 0.08) {
                  actualRoofY = y + cy;
                  foundRoof = true;
                }
                if (cy > h * 0.25 && edgeCount > maxBeltEdges) {
                  maxBeltEdges = edgeCount;
                  actualBeltY = y + cy;
                }
              }

              // 3. TIRE & PLATE SCANNER
              const stride = Math.max(6, Math.floor(w / 35));
              let leftEdgeDensity = 0,
                rightEdgeDensity = 0;

              let lWheel = { x: 0, y: 0, count: 0 };
              let rWheel = { x: 0, y: 0, count: 0 };
              const tirePoints = [];

              let pMinX = w,
                pMaxX = 0,
                pMinY = h,
                pMaxY = 0;
              let plateFound = false;

              for (let cy = 0; cy < h; cy += stride) {
                for (let cx = 0; cx < w; cx += stride) {
                  const idx = cy * w + cx;
                  const absX = x + cx;
                  const absY = y + cy;

                  if (chassisData[idx] > 0.5) {
                    if (cx < w * 0.3) leftEdgeDensity++;
                    if (cx > w * 0.7) rightEdgeDensity++;
                  }

                  if (cy > h * 0.6 && tireData[idx] > 0.4) {
                    tirePoints.push({ x: absX, y: absY });
                    if (cx < w / 2) {
                      lWheel.x += absX;
                      lWheel.y += absY;
                      lWheel.count++;
                    } else {
                      rWheel.x += absX;
                      rWheel.y += absY;
                      rWheel.count++;
                    }
                  }

                  if (
                    cy > h * 0.5 &&
                    cy < h * 0.95 &&
                    (cx < w * 0.35 || cx > w * 0.65)
                  ) {
                    if (plateData[idx] > 0.3) {
                      plateFound = true;
                      if (cx < pMinX) pMinX = cx;
                      if (cx > pMaxX) pMaxX = cx;
                      if (cy < pMinY) pMinY = cy;
                      if (cy > pMaxY) pMaxY = cy;
                    }
                  }
                }
              }

              // 4. CALCULATE TILT (PITCH) BASED ON TIRES
              lWheel.x =
                lWheel.count > 0 ? lWheel.x / lWheel.count : x + w * 0.2;
              lWheel.y =
                lWheel.count > 0 ? lWheel.y / lWheel.count : y + h * 0.85;
              rWheel.x =
                rWheel.count > 0 ? rWheel.x / rWheel.count : x + w * 0.8;
              rWheel.y =
                rWheel.count > 0 ? rWheel.y / rWheel.count : y + h * 0.85;

              // Calculate the mathematical slope of the car's stance
              let pitchSlope =
                (rWheel.y - lWheel.y) / (rWheel.x - lWheel.x || 1);
              pitchSlope = Math.max(-0.4, Math.min(0.4, pitchSlope)); // Cap extreme glitches

              // Dynamic Y-coordinate generator based on the tire slope
              const getTireY = (targetX) =>
                lWheel.y + pitchSlope * (targetX - lWheel.x);

              // Calculate relative heights for roof and beltline to maintain the tilt
              const centerTireY = getTireY(x + w / 2);
              const roofHeightOffset = centerTireY - actualRoofY;
              const beltHeightOffset = centerTireY - actualBeltY;

              const getY = (targetX, isRoof, isBelt) => {
                const base = getTireY(targetX);
                if (isRoof) return base - roofHeightOffset;
                if (isBelt) return base - beltHeightOffset;
                return base; // Bottom of car
              };

              // 5. 3D PROJECTION MATH
              const isFacingRight = rightEdgeDensity > leftEdgeDensity;
              const depthX = isFacingRight ? -w * 0.25 : w * 0.25;
              const depthY = -h * 0.15;

              const rearX = isFacingRight ? x : x + w;
              const frontX = isFacingRight ? x + w : x;
              const cabinRearX = isFacingRight ? x + w * 0.15 : x + w * 0.85;
              const cabinFrontX = isFacingRight ? x + w * 0.65 : x + w * 0.35;

              const p2D = (cx, isRoof = false, isBelt = false) => ({
                x: cx,
                y: getY(cx, isRoof, isBelt),
              });
              const p3D = (cx, isRoof = false, isBelt = false) => ({
                x: cx + depthX,
                y: getY(cx, isRoof, isBelt) + depthY,
              });

              const drawPlane = (pts, strokeColor, fillColor, label) => {
                ctx.beginPath();
                ctx.moveTo(pts[0].x, pts[0].y);
                ctx.lineTo(pts[1].x, pts[1].y);
                ctx.lineTo(pts[2].x, pts[2].y);
                ctx.lineTo(pts[3].x, pts[3].y);
                ctx.closePath();
                ctx.fillStyle = fillColor;
                ctx.fill();
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();

                if (label) {
                  ctx.fillStyle = strokeColor;
                  ctx.font = "bold 12px monospace";
                  ctx.fillText(
                    label,
                    (pts[0].x + pts[2].x) / 2 - 25,
                    (pts[0].y + pts[2].y) / 2
                  );
                }
              };

              // --- A. THE EXACT OUTER CUBE (Tilted Padding) ---
              // Draws the entire rotated bounding box padding
              const cBL = p2D(x);
              const cBR = p2D(x + w);
              const cTL = p2D(x, true);
              const cTR = p2D(x + w, true);
              const c3BL = p3D(x);
              const c3BR = p3D(x + w);
              const c3TL = p3D(x, true);
              const c3TR = p3D(x + w, true);

              ctx.strokeStyle = "rgba(0, 255, 255, 0.4)";
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              // Front Face
              ctx.moveTo(cBL.x, cBL.y);
              ctx.lineTo(cBR.x, cBR.y);
              ctx.lineTo(cTR.x, cTR.y);
              ctx.lineTo(cTL.x, cTL.y);
              ctx.closePath();
              ctx.stroke();
              // Back Face
              ctx.moveTo(c3BL.x, c3BL.y);
              ctx.lineTo(c3BR.x, c3BR.y);
              ctx.lineTo(c3TR.x, c3TR.y);
              ctx.lineTo(c3TL.x, c3TL.y);
              ctx.closePath();
              ctx.stroke();
              // Connectors
              ctx.beginPath();
              ctx.moveTo(cBL.x, cBL.y);
              ctx.lineTo(c3BL.x, c3BL.y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(cBR.x, cBR.y);
              ctx.lineTo(c3BR.x, c3BR.y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(cTR.x, cTR.y);
              ctx.lineTo(c3TR.x, c3TR.y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(cTL.x, cTL.y);
              ctx.lineTo(c3TL.x, c3TL.y);
              ctx.stroke();

              // --- B. INTERNAL PLANES (Tilted & Aligned) ---

              // Lower Side Plane (Cyan)
              drawPlane(
                [
                  p2D(rearX, false, true),
                  p2D(frontX, false, true),
                  p2D(frontX),
                  p2D(rearX),
                ],
                "rgba(0, 255, 255, 1)",
                "rgba(0, 255, 255, 0.1)",
                "LOWER SIDE"
              );

              // Upper Side Plane / Windows (Magenta)
              drawPlane(
                [
                  p2D(cabinRearX, true),
                  p2D(cabinFrontX, true),
                  p2D(cabinFrontX, false, true),
                  p2D(cabinRearX, false, true),
                ],
                "rgba(255, 0, 255, 1)",
                "rgba(255, 0, 255, 0.15)",
                "WINDOWS"
              );

              // Roof Plane Rectangle (Green - Extruded 3D)
              drawPlane(
                [
                  p2D(cabinRearX, true),
                  p2D(cabinFrontX, true),
                  p3D(cabinFrontX, true),
                  p3D(cabinRearX, true),
                ],
                "rgba(0, 255, 0, 1)",
                "rgba(0, 255, 0, 0.2)",
                "ROOF"
              );

              // Hood Plane Rectangle (Yellow - Extruded 3D)
              if (isFacingRight) {
                drawPlane(
                  [
                    p2D(cabinFrontX, false, true),
                    p2D(frontX, false, true),
                    p3D(frontX, false, true),
                    p3D(cabinFrontX, false, true),
                  ],
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.2)",
                  "HOOD"
                );
              } else {
                drawPlane(
                  [
                    p2D(frontX, false, true),
                    p2D(cabinFrontX, false, true),
                    p3D(cabinFrontX, false, true),
                    p3D(frontX, false, true),
                  ],
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.2)",
                  "HOOD"
                );
              }

              // Front Fascia Plane (Blue)
              drawPlane(
                [
                  p2D(frontX, false, true),
                  p2D(frontX),
                  p3D(frontX),
                  p3D(frontX, false, true),
                ],
                "rgba(0, 100, 255, 1)",
                "rgba(0, 100, 255, 0.25)",
                "FRONT"
              );

              // Rear Fascia Plane (Red)
              drawPlane(
                [p2D(rearX, true), p2D(rearX), p3D(rearX), p3D(rearX, true)],
                "rgba(255, 0, 0, 1)",
                "rgba(255, 0, 0, 0.25)",
                "REAR"
              );

              // --- C. TIRES & PLATE ---
              ctx.fillStyle = "rgba(255, 0, 255, 0.9)";
              tirePoints.forEach((p) => {
                ctx.beginPath();
                ctx.arc(p.x, p.y, stride * 0.45, 0, 2 * Math.PI);
                ctx.fill();
              });

              if (plateFound && pMaxX - pMinX > 0) {
                const pad = stride;
                const absPMinX = x + pMinX - pad;
                const absPMaxX = x + pMaxX + pad;
                const absPMinY = y + pMinY - pad;
                const absPMaxY = y + pMaxY + pad;

                ctx.strokeStyle = "rgba(255, 255, 0, 1)";
                ctx.lineWidth = 2.5;
                ctx.strokeRect(
                  absPMinX,
                  absPMinY,
                  absPMaxX - absPMinX,
                  absPMaxY - absPMinY
                );
                ctx.beginPath();
                ctx.moveTo(absPMinX, absPMinY);
                ctx.lineTo(absPMaxX, absPMaxY);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(absPMaxX, absPMinY);
                ctx.lineTo(absPMinX, absPMaxY);
                ctx.stroke();
              }

              ctx.fillStyle = "#00ffff";
              ctx.font = "bold 13px monospace";
              ctx.fillText(`TILTED PADDING MATRIX: ${w}x${h}px`, x, cTL.y - 10);
            });
          }
        }
        baseTensor.dispose();
      }

      // ==========================================
      // 33. CAR CUBE SEGMENTED (Shrink-Wrapped Cube + Window/Tire Anchors)
      // ==========================================
      if (activeKit === "carcubesegment") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 10, 20, 0.75)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const model = await cocoSsd.load();
        const predictions = await model.detect(canvas);

        for (const pred of predictions) {
          if (["car", "truck", "bus"].includes(pred.class)) {
            let [x, y, w, h] = pred.bbox;

            x = Math.max(0, Math.floor(x));
            y = Math.max(0, Math.floor(y));
            w = Math.min(canvas.width - x, Math.floor(w));
            h = Math.min(canvas.height - y, Math.floor(h));

            if (w <= 0 || h <= 0) continue;

            tf.tidy(() => {
              const carTensor = baseTensor.slice([y, x, 0], [h, w, 3]);
              const normalized = carTensor.toFloat().div(tf.scalar(255.0));
              const [r, g, b] = tf.split(normalized, 3, 2);
              const luma = r
                .mul(0.299)
                .add(g.mul(0.587))
                .add(b.mul(0.114))
                .squeeze();

              // 1. TENSOR ACTIVATIONS
              const edgeKernel = tf.tensor4d(
                [0, 1, 0, 1, -4, 1, 0, 1, 0],
                [3, 3, 1, 1]
              );
              const expandedLuma = luma.expandDims(0).expandDims(-1);
              const edges = tf
                .conv2d(expandedLuma, edgeKernel, 1, "same")
                .abs()
                .squeeze();
              const chassisEssence = tf.sigmoid(
                edges.sub(tf.scalar(0.2)).mul(tf.scalar(10.0))
              );

              const invertedLuma = tf.scalar(1.0).sub(luma);
              const tireEssence = tf.relu(
                invertedLuma.sub(tf.scalar(0.8)).mul(tf.scalar(5.0))
              );

              const chassisData = chassisEssence.dataSync();
              const tireData = tireEssence.dataSync();

              const stride = Math.max(4, Math.floor(w / 45));

              let leftEdgeDensity = 0,
                rightEdgeDensity = 0;
              let lWheel = { x: 0, y: 0, count: 0 };
              let rWheel = { x: 0, y: 0, count: 0 };
              const tirePoints = [];

              // To shrink-wrap the box, we track the outermost physical edges
              let tightMinX = w,
                tightMaxX = 0;
              let tightMinY = h,
                tightMaxY = 0;

              for (let cy = 0; cy < h; cy += stride) {
                for (let cx = 0; cx < w; cx += stride) {
                  const idx = cy * w + cx;
                  const absX = x + cx;
                  const absY = y + cy;

                  const isEdge = chassisData[idx] > 0.5;

                  if (isEdge) {
                    if (cx < w * 0.3) leftEdgeDensity++;
                    if (cx > w * 0.7) rightEdgeDensity++;

                    // Shrink-wrap tracking: Find the true metal boundaries
                    if (cx < tightMinX) tightMinX = cx;
                    if (cx > tightMaxX) tightMaxX = cx;
                    if (cy < tightMinY && cy > h * 0.05) tightMinY = cy; // Ignore roof racks
                  }

                  // Tire tracking
                  if (cy > h * 0.55 && tireData[idx] > 0.4) {
                    tirePoints.push({ x: absX, y: absY });
                    if (cy > tightMaxY) tightMaxY = cy; // Bottom of tires is true bottom
                    if (cx < w / 2) {
                      lWheel.x += absX;
                      lWheel.y += absY;
                      lWheel.count++;
                    } else {
                      rWheel.x += absX;
                      rWheel.y += absY;
                      rWheel.count++;
                    }
                  }
                }
              }

              // 2. DIRECTION & ANCHORING
              const isFacingRight = rightEdgeDensity > leftEdgeDensity;

              lWheel.x =
                lWheel.count > 0 ? lWheel.x / lWheel.count : x + w * 0.2;
              lWheel.y =
                lWheel.count > 0 ? lWheel.y / lWheel.count : y + h * 0.85;
              rWheel.x =
                rWheel.count > 0 ? rWheel.x / rWheel.count : x + w * 0.8;
              rWheel.y =
                rWheel.count > 0 ? rWheel.y / rWheel.count : y + h * 0.85;

              // Identify rear vs front tire
              const rearTire = isFacingRight ? lWheel : rWheel;
              const frontTire = isFacingRight ? rWheel : lWheel;

              let pitchSlope =
                (rWheel.y - lWheel.y) / (rWheel.x - lWheel.x || 1);
              pitchSlope = Math.max(-0.2, Math.min(0.2, pitchSlope));
              const getPitchY = (targetX) =>
                lWheel.y + pitchSlope * (targetX - lWheel.x);

              // 3. TIGHTENED COORDINATES (The Shrink-Wrap)
              // We now use the physical edges we found, NOT the loose COCO-SSD box
              const trueRearX = x + tightMinX;
              const trueFrontX = x + tightMaxX;
              const trueRoofY = y + tightMinY;
              const trueBeltY =
                trueRoofY + (getPitchY(x + w / 2) - trueRoofY) * 0.45; // ~45% down from roof

              // 4. 3D PROJECTION (Depth)
              const depthX = isFacingRight ? -w * 0.2 : w * 0.2;
              const depthY = -h * 0.12;

              // Helper for 2D/3D points based on the TIGHT box
              const p2D = (cx, isRoof = false, isBelt = false) => {
                let cy = getPitchY(cx);
                if (isRoof) cy -= getPitchY(x + w / 2) - trueRoofY;
                if (isBelt) cy -= getPitchY(x + w / 2) - trueBeltY;
                return { x: cx, y: cy };
              };
              const p3D = (pt) => ({ x: pt.x + depthX, y: pt.y + depthY });

              const drawPlane = (
                p1,
                p2,
                p3,
                p4,
                strokeColor,
                fillColor,
                label
              ) => {
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.lineTo(p3.x, p3.y);
                ctx.lineTo(p4.x, p4.y);
                ctx.closePath();
                ctx.fillStyle = fillColor;
                ctx.fill();
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();
                if (label) {
                  ctx.fillStyle = strokeColor;
                  ctx.font = "bold 11px monospace";
                  ctx.fillText(
                    label,
                    (p1.x + p3.x) / 2 - 20,
                    (p1.y + p3.y) / 2
                  );
                }
              };

              // --- A. THE TIGHT 3D PADDING CUBE ---
              // Uses the shrink-wrapped coordinates so it perfectly hugs the car
              const cBL = p2D(trueRearX);
              const cBR = p2D(trueFrontX);
              const cTL = p2D(trueRearX, true);
              const cTR = p2D(trueFrontX, true);

              ctx.strokeStyle = "rgba(0, 255, 255, 0.4)";
              ctx.lineWidth = 1;
              ctx.beginPath();
              // Front Face
              ctx.moveTo(cBL.x, cBL.y);
              ctx.lineTo(cBR.x, cBR.y);
              ctx.lineTo(cTR.x, cTR.y);
              ctx.lineTo(cTL.x, cTL.y);
              ctx.closePath();
              ctx.stroke();
              // Back Face
              ctx.moveTo(p3D(cBL).x, p3D(cBL).y);
              ctx.lineTo(p3D(cBR).x, p3D(cBR).y);
              ctx.lineTo(p3D(cTR).x, p3D(cTR).y);
              ctx.lineTo(p3D(cTL).x, p3D(cTL).y);
              ctx.closePath();
              ctx.stroke();
              // Connectors
              ctx.beginPath();
              ctx.moveTo(cBL.x, cBL.y);
              ctx.lineTo(p3D(cBL).x, p3D(cBL).y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(cBR.x, cBR.y);
              ctx.lineTo(p3D(cBR).x, p3D(cBR).y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(cTR.x, cTR.y);
              ctx.lineTo(p3D(cTR).x, p3D(cTR).y);
              ctx.stroke();
              ctx.beginPath();
              ctx.moveTo(cTL.x, cTL.y);
              ctx.lineTo(p3D(cTL).x, p3D(cTL).y);
              ctx.stroke();

              // --- B. SEGMENTED WINDOW PLANES ---
              // Back Window (Rear Edge to Rear Tire)
              const bWinTL = p2D(trueRearX, true);
              const bWinTR = p2D(rearTire.x, true);
              const bWinBL = p2D(trueRearX, false, true);
              const bWinBR = p2D(rearTire.x, false, true);
              drawPlane(
                bWinTL,
                bWinTR,
                bWinBR,
                bWinBL,
                "rgba(255, 100, 0, 1)",
                "rgba(255, 100, 0, 0.2)",
                "BACK WIN"
              );

              // Side Windows (Rear Tire to Front Tire)
              const sWinTL = p2D(rearTire.x, true);
              const sWinTR = p2D(frontTire.x, true);
              const sWinBL = p2D(rearTire.x, false, true);
              const sWinBR = p2D(frontTire.x, false, true);
              drawPlane(
                sWinTL,
                sWinTR,
                sWinBR,
                sWinBL,
                "rgba(255, 0, 255, 1)",
                "rgba(255, 0, 255, 0.2)",
                "SIDE WIN"
              );

              // --- C. HOOD AND ROOF PLANES (Snapped to Cube) ---
              // Roof Plane
              drawPlane(
                cTL,
                cTR,
                p3D(cTR),
                p3D(cTL),
                "rgba(0, 255, 0, 1)",
                "rgba(0, 255, 0, 0.2)",
                "ROOF"
              );

              // Hood Plane (Front Tire to True Front Edge)
              if (isFacingRight) {
                const hoodL = p2D(frontTire.x, false, true);
                const hoodR = p2D(trueFrontX, false, true);
                drawPlane(
                  hoodL,
                  hoodR,
                  p3D(hoodR),
                  p3D(hoodL),
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.2)",
                  "HOOD"
                );
              } else {
                const hoodL = p2D(trueRearX, false, true);
                const hoodR = p2D(frontTire.x, false, true);
                drawPlane(
                  hoodL,
                  hoodR,
                  p3D(hoodR),
                  p3D(hoodL),
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.2)",
                  "HOOD"
                );
              }

              // --- D. TARGET THE REAR TIRE (Stationary Anchor) ---
              // Magenta tire points
              ctx.fillStyle = "rgba(255, 0, 255, 0.5)";
              tirePoints.forEach((p) => {
                ctx.beginPath();
                ctx.arc(p.x, p.y, stride * 0.4, 0, 2 * Math.PI);
                ctx.fill();
              });

              // Explicit Crosshair/Oval on the Rear Axle
              ctx.strokeStyle = "rgba(255, 100, 0, 1)"; // Orange to match back window
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.ellipse(
                rearTire.x,
                rearTire.y,
                w * 0.08,
                h * 0.1,
                0,
                0,
                2 * Math.PI
              );
              ctx.stroke();
              ctx.beginPath();
              ctx.arc(rearTire.x, rearTire.y, 4, 0, 2 * Math.PI);
              ctx.fillStyle = "white";
              ctx.fill();

              // HUD Text
              ctx.fillStyle = "#00ffff";
              ctx.font = "bold 12px monospace";
              ctx.fillText(`TIGHT CUBE: Math-Aligned`, trueRearX, cTL.y - 8);
            });
          }
        }
        baseTensor.dispose();
      }
      // ==========================================
      // 37. ROAD CREST KIT (ReLU Pavement + Sigmoid Crest Lines)
      // ==========================================
      if (activeKit === "roadcrest") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Dim the environment slightly so the road data pops
        ctx.fillStyle = "rgba(0, 10, 20, 0.5)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // Calculate standard Luminance
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();
          // --- 1. RELU PAVEMENT BASE ---
          // Pavement is highly desaturated (gray). We find areas where R, G, and B are very similar.
          const rgDiff = r.sub(g).abs();
          const rbDiff = r.sub(b).abs();
          const colorVariance = rgDiff.add(rbDiff).squeeze();

          // Create a binary mask where the color variance is low (it's gray/white/black)
          const isDesaturated = tf.cast(
            colorVariance.lessEqual(tf.scalar(0.05)),
            "float32"
          );

          // Push through ReLU: Multiply the grays by Luma, and subtract 0.1 to completely erase pure pitch-black shadows
          const pavementEssence = tf.relu(
            luma.mul(isDesaturated).sub(tf.scalar(0.1))
          );

          // --- 2. SIGMOID CREST & LANE LINES ---
          // A. Structural Crest (Edge Detection)
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const edges = tf
            .conv2d(luma.expandDims(0).expandDims(-1), edgeKernel, 1, "same")
            .abs()
            .squeeze();

          // B. Yellow Line Filter (High Red + Green, Low Blue)
          const yellowEssence = tf.relu(
            r
              .add(g.mul(tf.scalar(20)))
              .sub(b.mul(tf.scalar(22.5)))
              .squeeze()
          );

          // Combine the Structural Edges and the Yellow Lines into one master signal
          const crestSignal = edges.add(yellowEssence);

          // Push through Sigmoid to violently snap the crests into solid, highly defined shapes
          const crestDefined = tf.sigmoid(
            crestSignal.sub(tf.scalar(0.3)).mul(tf.scalar(12.0))
          );

          // --- 3. DATA EXTRACTION & DRAWING ---
          const pavementData = pavementEssence.dataSync();
          const crestData = crestDefined.dataSync();
          const w = canvas.width;
          const h = canvas.height;
          const stride = Math.max(4, Math.floor(w / 80));

          // Start scanning from just above the middle of the image (the horizon)
          const horizonY = Math.floor(h * 0.4);
          const crestPoints = [];

          for (let y = horizonY; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;

              // Plot the Pavement Base (Soft Grays to Bright Whites)
              const pavementStrength = pavementData[idx];
              if (pavementStrength > 0.05) {
                // The brighter the pavement, the more opaque the blue/gray dot
                ctx.fillStyle = `rgba(130, 0, 255, ${pavementStrength * 0.9})`;
                ctx.fillRect(x, y, 2, 2);
              }

              // Collect the defined Crest / Lane lines
              if (crestData[idx] > 0.7) {
                crestPoints.push({ x, y });
              }
            }
          }

          // Draw the sharp Crests over the pavement
          ctx.fillStyle = "rgba(255, 235, 0, 1)"; // Neon Yellow
          crestPoints.forEach((pt) => {
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, 1.8, 0, 2 * Math.PI);
            ctx.fill();
          });
        });

        baseTensor.dispose();
      }

      // ==========================================
      // 34. CAR CUBE FUSED (Planes Built DIRECTLY onto Bounding Box Faces)
      // ==========================================
      if (activeKit === "carcubefused") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 10, 20, 0.75)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const model = await cocoSsd.load();
        const predictions = await model.detect(canvas);

        for (const pred of predictions) {
          if (["car", "truck", "bus"].includes(pred.class)) {
            let [x, y, w, h] = pred.bbox;

            x = Math.max(0, Math.floor(x));
            y = Math.max(0, Math.floor(y));
            w = Math.min(canvas.width - x, Math.floor(w));
            h = Math.min(canvas.height - y, Math.floor(h));

            if (w <= 0 || h <= 0) continue;

            tf.tidy(() => {
              const carTensor = baseTensor.slice([y, x, 0], [h, w, 3]);
              const normalized = carTensor.toFloat().div(tf.scalar(255.0));
              const [r, g, b] = tf.split(normalized, 3, 2);
              const luma = r
                .mul(0.299)
                .add(g.mul(0.587))
                .add(b.mul(0.114))
                .squeeze();

              // 1. TENSOR ACTIVATIONS
              const edgeKernel = tf.tensor4d(
                [0, 1, 0, 1, -4, 1, 0, 1, 0],
                [3, 3, 1, 1]
              );
              const expandedLuma = luma.expandDims(0).expandDims(-1);
              const edges = tf
                .conv2d(expandedLuma, edgeKernel, 1, "same")
                .abs()
                .squeeze();
              const chassisEssence = tf.sigmoid(
                edges.sub(tf.scalar(0.2)).mul(tf.scalar(10.0))
              );
              const invertedLuma = tf.scalar(1.0).sub(luma);
              const tireEssence = tf.relu(
                invertedLuma.sub(tf.scalar(0.8)).mul(tf.scalar(5.0))
              );

              const chassisData = chassisEssence.dataSync();
              const tireData = tireEssence.dataSync();

              const stride = Math.max(4, Math.floor(w / 45));
              let leftEdgeDensity = 0,
                rightEdgeDensity = 0;
              let lWheel = { x: 0, y: 0, count: 0 };
              let rWheel = { x: 0, y: 0, count: 0 };
              const tirePoints = [];

              let tightMinX = w,
                tightMaxX = 0,
                tightMinY = h;

              for (let cy = 0; cy < h; cy += stride) {
                for (let cx = 0; cx < w; cx += stride) {
                  const idx = cy * w + cx;
                  const absX = x + cx;
                  const absY = y + cy;

                  if (chassisData[idx] > 0.5) {
                    if (cx < w * 0.3) leftEdgeDensity++;
                    if (cx > w * 0.7) rightEdgeDensity++;
                    if (cx < tightMinX) tightMinX = cx;
                    if (cx > tightMaxX) tightMaxX = cx;
                    if (cy < tightMinY && cy > h * 0.05) tightMinY = cy;
                  }

                  if (cy > h * 0.55 && tireData[idx] > 0.4) {
                    tirePoints.push({ x: absX, y: absY });
                    if (cx < w / 2) {
                      lWheel.x += absX;
                      lWheel.y += absY;
                      lWheel.count++;
                    } else {
                      rWheel.x += absX;
                      rWheel.y += absY;
                      rWheel.count++;
                    }
                  }
                }
              }

              const isFacingRight = rightEdgeDensity > leftEdgeDensity;

              // Wheel Anchors
              lWheel.x =
                lWheel.count > 0 ? lWheel.x / lWheel.count : x + w * 0.2;
              lWheel.y =
                lWheel.count > 0 ? lWheel.y / lWheel.count : y + h * 0.85;
              rWheel.x =
                rWheel.count > 0 ? rWheel.x / rWheel.count : x + w * 0.8;
              rWheel.y =
                rWheel.count > 0 ? rWheel.y / rWheel.count : y + h * 0.85;
              const rearTire = isFacingRight ? lWheel : rWheel;

              // Tight Bounding Box Limits
              const bx1 = x + tightMinX;
              const bx2 = x + tightMaxX;
              const by1 = y + tightMinY; // Roof height
              const by2 = Math.max(lWheel.y, rWheel.y); // Tire/Ground height
              const bHeight = by2 - by1;
              const bBeltY = by1 + bHeight * 0.45; // Beltline height

              // 3D Depth Offsets
              const dx = isFacingRight ? -w * 0.2 : w * 0.2;
              const dy = -h * 0.12;

              // --- 2. MASTER VERTICES OF THE CUBE (Front & Back Faces) ---
              // Front Face Coordinates (2D Surface)
              const F_TL = { x: bx1, y: by1 };
              const F_TR = { x: bx2, y: by1 };
              const F_BL = { x: bx1, y: by2 };
              const F_BR = { x: bx2, y: by2 };
              const F_ML = { x: bx1, y: bBeltY }; // Beltline left
              const F_MR = { x: bx2, y: bBeltY }; // Beltline right

              // Back Face Coordinates (3D Extrusion)
              const B_TL = { x: F_TL.x + dx, y: F_TL.y + dy };
              const B_TR = { x: F_TR.x + dx, y: F_TR.y + dy };
              const B_BL = { x: F_BL.x + dx, y: F_BL.y + dy };
              const B_BR = { x: F_BR.x + dx, y: F_BR.y + dy };
              const B_ML = { x: F_ML.x + dx, y: F_ML.y + dy };
              const B_MR = { x: F_MR.x + dx, y: F_MR.y + dy };

              // Partitioning X for Windows vs Hood based on direction
              const wheelSplitX = isFacingRight ? rWheel.x : lWheel.x;
              const F_MS = { x: wheelSplitX, y: bBeltY };
              const F_TS = { x: wheelSplitX, y: by1 };
              const B_MS = { x: F_MS.x + dx, y: F_MS.y + dy };
              const B_TS = { x: F_TS.x + dx, y: F_TS.y + dy };

              // Plane Drawing Helper
              const drawQuad = (
                p1,
                p2,
                p3,
                p4,
                strokeColor,
                fillColor,
                label
              ) => {
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.lineTo(p3.x, p3.y);
                ctx.lineTo(p4.x, p4.y);
                ctx.closePath();
                ctx.fillStyle = fillColor;
                ctx.fill();
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();
                if (label) {
                  ctx.fillStyle = strokeColor;
                  ctx.font = "bold 11px monospace";
                  ctx.fillText(
                    label,
                    (p1.x + p3.x) / 2 - 20,
                    (p1.y + p3.y) / 2
                  );
                }
              };

              // --- 3. FILL PLANES DIRECTLY ONTO CUBE FACES ---

              // A. ROOF PLANE (Top Face of 3D Cube)
              drawQuad(
                F_TL,
                F_TR,
                B_TR,
                B_TL,
                "rgba(0, 255, 0, 1)",
                "rgba(0, 255, 0, 0.2)",
                "ROOF PLANE"
              );

              // B. LOWER SIDE PLANE (Bottom Half of Front Face)
              drawQuad(
                F_ML,
                F_MR,
                F_BR,
                F_BL,
                "rgba(0, 255, 255, 1)",
                "rgba(0, 255, 255, 0.15)",
                "LOWER SIDE"
              );

              if (isFacingRight) {
                // C. WINDOWS (Top-Left Quad of Front Face)
                drawQuad(
                  F_TL,
                  F_TS,
                  F_MS,
                  F_ML,
                  "rgba(255, 0, 255, 1)",
                  "rgba(255, 0, 255, 0.2)",
                  "WINDOWS"
                );
                // D. HOOD PLANE (3D Extrusion on Top-Right Beltline)
                drawQuad(
                  F_MS,
                  F_MR,
                  B_MR,
                  B_MS,
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.25)",
                  "HOOD PLANE"
                );
                // E. FRONT FASCIA (Right End Face of Cube)
                drawQuad(
                  F_MR,
                  F_BR,
                  B_BR,
                  B_MR,
                  "rgba(0, 100, 255, 1)",
                  "rgba(0, 100, 255, 0.25)",
                  "FRONT FASCIA"
                );
                // F. REAR FASCIA (Left End Face of Cube)
                drawQuad(
                  F_TL,
                  F_BL,
                  B_BL,
                  B_TL,
                  "rgba(255, 0, 0, 1)",
                  "rgba(255, 0, 0, 0.25)",
                  "REAR FASCIA"
                );
              } else {
                // C. WINDOWS (Top-Right Quad of Front Face)
                drawQuad(
                  F_TS,
                  F_TR,
                  F_MR,
                  F_MS,
                  "rgba(255, 0, 255, 1)",
                  "rgba(255, 0, 255, 0.2)",
                  "WINDOWS"
                );
                // D. HOOD PLANE (3D Extrusion on Top-Left Beltline)
                drawQuad(
                  F_ML,
                  F_MS,
                  B_MS,
                  B_ML,
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.25)",
                  "HOOD PLANE"
                );
                // E. FRONT FASCIA (Left End Face of Cube)
                drawQuad(
                  F_ML,
                  F_BL,
                  B_BL,
                  B_ML,
                  "rgba(0, 100, 255, 1)",
                  "rgba(0, 100, 255, 0.25)",
                  "FRONT FASCIA"
                );
                // F. REAR FASCIA (Right End Face of Cube)
                drawQuad(
                  F_TR,
                  F_BR,
                  B_BR,
                  B_TR,
                  "rgba(255, 0, 0, 1)",
                  "rgba(255, 0, 0, 0.25)",
                  "REAR FASCIA"
                );
              }

              // --- 4. DRAW STRUCTURAL CUBE SKELETON (Outer Edges) ---
              ctx.strokeStyle = "rgba(0, 255, 255, 0.8)";
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              // Outer Wireframe Connectors
              ctx.moveTo(F_TL.x, F_TL.y);
              ctx.lineTo(F_TR.x, F_TR.y);
              ctx.lineTo(F_BR.x, F_BR.y);
              ctx.lineTo(F_BL.x, F_BL.y);
              ctx.closePath();
              ctx.moveTo(B_TL.x, B_TL.y);
              ctx.lineTo(B_TR.x, B_TR.y);
              ctx.lineTo(B_BR.x, B_BR.y);
              ctx.lineTo(B_BL.x, B_BL.y);
              ctx.closePath();
              ctx.moveTo(F_TL.x, F_TL.y);
              ctx.lineTo(B_TL.x, B_TL.y);
              ctx.moveTo(F_TR.x, F_TR.y);
              ctx.lineTo(B_TR.x, B_TR.y);
              ctx.moveTo(F_BL.x, F_BL.y);
              ctx.lineTo(B_BL.x, B_BL.y);
              ctx.moveTo(F_BR.x, F_BR.y);
              ctx.lineTo(B_BR.x, B_BR.y);
              ctx.stroke();

              // --- 5. TIRE & REAR AXLE ANCHORS ---
              ctx.fillStyle = "rgba(255, 0, 255, 0.5)";
              tirePoints.forEach((p) => {
                ctx.beginPath();
                ctx.arc(p.x, p.y, stride * 0.4, 0, 2 * Math.PI);
                ctx.fill();
              });

              // Orange Stationary Wheel Circle
              ctx.strokeStyle = "rgba(255, 100, 0, 1)";
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.ellipse(
                rearTire.x,
                rearTire.y,
                w * 0.07,
                h * 0.09,
                0,
                0,
                2 * Math.PI
              );
              ctx.stroke();
              ctx.beginPath();
              ctx.arc(rearTire.x, rearTire.y, 4, 0, 2 * Math.PI);
              ctx.fillStyle = "white";
              ctx.fill();

              // Label
              ctx.fillStyle = "#00ffff";
              ctx.font = "bold 12px monospace";
              ctx.fillText(`FUSED CUBE MATRIX: ${w}x${h}px`, bx1, F_TL.y - 8);
            });
          }
        }
        baseTensor.dispose();
      }
      // ==========================================
      // 36. TIRE-ALIGNED CUBE KIT (Wheel-Rim Centroid Lock)
      // ==========================================
      if (activeKit === "carcubealign") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        ctx.fillStyle = "rgba(0, 10, 20, 0.75)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        const model = await cocoSsd.load();
        const predictions = await model.detect(canvas);

        for (const pred of predictions) {
          if (["car", "truck", "bus"].includes(pred.class)) {
            let [x, y, w, h] = pred.bbox;

            x = Math.max(0, Math.floor(x));
            y = Math.max(0, Math.floor(y));
            w = Math.min(canvas.width - x, Math.floor(w));
            h = Math.min(canvas.height - y, Math.floor(h));

            if (w <= 0 || h <= 0) continue;

            tf.tidy(() => {
              const carTensor = baseTensor.slice([y, x, 0], [h, w, 3]);
              const normalized = carTensor.toFloat().div(tf.scalar(255.0));
              const [r, g, b] = tf.split(normalized, 3, 2);
              const luma = r
                .mul(0.299)
                .add(g.mul(0.587))
                .add(b.mul(0.114))
                .squeeze();

              // 1. TENSOR ACTIVATIONS
              const edgeKernel = tf.tensor4d(
                [0, 1, 0, 1, -4, 1, 0, 1, 0],
                [3, 3, 1, 1]
              );
              const expandedLuma = luma.expandDims(0).expandDims(-1);
              const edges = tf
                .conv2d(expandedLuma, edgeKernel, 1, "same")
                .abs()
                .squeeze();
              const chassisEssence = tf.sigmoid(
                edges.sub(tf.scalar(0.2)).mul(tf.scalar(10.0))
              );

              const invertedLuma = tf.scalar(1.0).sub(luma);
              const tireEssence = tf.relu(
                invertedLuma.sub(tf.scalar(0.7)).mul(tf.scalar(4.0))
              );

              // RIM HUB MATRIX: Spoke Edges * Dark Rubber (Finds the actual wheel center)
              const hubEssence = chassisEssence.mul(tireEssence);

              const chassisData = chassisEssence.dataSync();
              const hubData = hubEssence.dataSync();

              let leftEdgeDensity = 0,
                rightEdgeDensity = 0;

              // --- 2. STRICT WHEEL-WELL SEARCH ZONES ---
              // Excludes bumpers (0-22% and 82-100%) and ground shadows (>78% height)
              const minYSearch = Math.floor(h * 0.52);
              const maxYSearch = Math.floor(h * 0.78);

              let fWheelPoints = [];
              let rWheelPoints = [];

              for (let cy = minYSearch; cy < maxYSearch; cy += 3) {
                for (let cx = 0; cx < w; cx += 3) {
                  const idx = cy * w + cx;
                  const absX = x + cx;
                  const absY = y + cy;

                  if (chassisData[idx] > 0.5) {
                    if (cx < w * 0.3) leftEdgeDensity++;
                    if (cx > w * 0.7) rightEdgeDensity++;
                  }

                  // Front Wheel Zone (22% to 46% of car length)
                  if (cx >= w * 0.22 && cx <= w * 0.46 && hubData[idx] > 0.2) {
                    fWheelPoints.push({ x: absX, y: absY });
                  }
                  // Rear Wheel Zone (58% to 82% of car length)
                  if (cx >= w * 0.58 && cx <= w * 0.82 && hubData[idx] > 0.2) {
                    rWheelPoints.push({ x: absX, y: absY });
                  }
                }
              }

              // Calculate Centroids strictly within wheel zones
              let lWheel = { x: x + w * 0.33, y: y + h * 0.66 };
              let rWheel = { x: x + w * 0.7, y: y + h * 0.66 };

              if (fWheelPoints.length > 3) {
                lWheel.x =
                  fWheelPoints.reduce((acc, p) => acc + p.x, 0) /
                  fWheelPoints.length;
                lWheel.y =
                  fWheelPoints.reduce((acc, p) => acc + p.y, 0) /
                  fWheelPoints.length;
              }

              if (rWheelPoints.length > 3) {
                rWheel.x =
                  rWheelPoints.reduce((acc, p) => acc + p.x, 0) /
                  rWheelPoints.length;
                rWheel.y =
                  rWheelPoints.reduce((acc, p) => acc + p.y, 0) /
                  rWheelPoints.length;
              }

              const isFacingRight = rightEdgeDensity > leftEdgeDensity;
              const rearTire = isFacingRight ? lWheel : rWheel;
              const frontTire = isFacingRight ? rWheel : lWheel;

              // --- 3. HARD ANCHORED GEOMETRY ---
              const dxTires = rWheel.x - lWheel.x;
              const dyTires = rWheel.y - lWheel.y;
              const pitchSlope = dyTires / (dxTires || 1);
              const getTireLineY = (targetX) =>
                lWheel.y + pitchSlope * (targetX - lWheel.x);

              const padX = w * 0.1;
              const minX = lWheel.x - padX;
              const maxX = rWheel.x + padX;
              const bodyHeight = h * 0.5;
              const windowHeight = bodyHeight * 0.45;

              // Wireframe Vertices anchored to actual Wheel Hub Line
              const F_BL = { x: minX, y: getTireLineY(minX) };
              const F_BR = { x: maxX, y: getTireLineY(maxX) };
              const F_TL = { x: minX, y: F_BL.y - bodyHeight };
              const F_TR = { x: maxX, y: F_BR.y - bodyHeight };

              const F_ML = { x: minX, y: F_BL.y - (bodyHeight - windowHeight) };
              const F_MR = { x: maxX, y: F_BR.y - (bodyHeight - windowHeight) };

              const F_WL = { x: lWheel.x, y: lWheel.y - bodyHeight };
              const F_WR = { x: rWheel.x, y: rWheel.y - bodyHeight };
              const F_WLB = {
                x: lWheel.x,
                y: lWheel.y - (bodyHeight - windowHeight),
              };
              const F_WRB = {
                x: rWheel.x,
                y: rWheel.y - (bodyHeight - windowHeight),
              };

              const depthX = isFacingRight ? -w * 0.18 : w * 0.18;
              const depthY = -h * 0.1;
              const p3D = (pt) => ({ x: pt.x + depthX, y: pt.y + depthY });

              const drawQuad = (
                p1,
                p2,
                p3,
                p4,
                strokeColor,
                fillColor,
                label
              ) => {
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.lineTo(p3.x, p3.y);
                ctx.lineTo(p4.x, p4.y);
                ctx.closePath();
                ctx.fillStyle = fillColor;
                ctx.fill();
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();
                if (label) {
                  ctx.fillStyle = strokeColor;
                  ctx.font = "bold 11px monospace";
                  ctx.fillText(
                    label,
                    (p1.x + p3.x) / 2 - 20,
                    (p1.y + p3.y) / 2
                  );
                }
              };

              // --- 4. DRAW CUBE & PLANES ---
              ctx.strokeStyle = "rgba(0, 255, 255, 0.9)";
              ctx.lineWidth = 1.8;
              ctx.beginPath();
              ctx.moveTo(F_TL.x, F_TL.y);
              ctx.lineTo(F_TR.x, F_TR.y);
              ctx.lineTo(F_BR.x, F_BR.y);
              ctx.lineTo(F_BL.x, F_BL.y);
              ctx.closePath();
              ctx.moveTo(p3D(F_TL).x, p3D(F_TL).y);
              ctx.lineTo(p3D(F_TR).x, p3D(F_TR).y);
              ctx.lineTo(p3D(F_BR).x, p3D(F_BR).y);
              ctx.lineTo(p3D(F_BL).x, p3D(F_BL).y);
              ctx.closePath();
              ctx.moveTo(F_TL.x, F_TL.y);
              ctx.lineTo(p3D(F_TL).x, p3D(F_TL).y);
              ctx.moveTo(F_TR.x, F_TR.y);
              ctx.lineTo(p3D(F_TR).x, p3D(F_TR).y);
              ctx.moveTo(F_BL.x, F_BL.y);
              ctx.lineTo(p3D(F_BL).x, p3D(F_BL).y);
              ctx.moveTo(F_BR.x, F_BR.y);
              ctx.lineTo(p3D(F_BR).x, p3D(F_BR).y);
              ctx.stroke();

              drawQuad(
                F_ML,
                F_MR,
                F_BR,
                F_BL,
                "rgba(0, 255, 255, 1)",
                "rgba(0, 255, 255, 0.15)",
                "LOWER SIDE"
              );
              drawQuad(
                F_WL,
                F_WR,
                F_WRB,
                F_WLB,
                "rgba(255, 0, 255, 1)",
                "rgba(255, 0, 255, 0.25)",
                "WINDOWS"
              );
              drawQuad(
                F_WL,
                F_WR,
                p3D(F_WR),
                p3D(F_WL),
                "rgba(0, 255, 0, 1)",
                "rgba(0, 255, 0, 0.25)",
                "ROOF"
              );

              if (isFacingRight) {
                drawQuad(
                  F_WRB,
                  F_MR,
                  p3D(F_MR),
                  p3D(F_WRB),
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.25)",
                  "HOOD"
                );
                drawQuad(
                  F_MR,
                  F_BR,
                  p3D(F_BR),
                  p3D(F_MR),
                  "rgba(0, 100, 255, 1)",
                  "rgba(0, 100, 255, 0.25)",
                  "FRONT"
                );
                drawQuad(
                  F_TL,
                  F_BL,
                  p3D(F_BL),
                  p3D(F_TL),
                  "rgba(255, 0, 0, 1)",
                  "rgba(255, 0, 0, 0.25)",
                  "REAR"
                );
              } else {
                drawQuad(
                  F_ML,
                  F_WLB,
                  p3D(F_WLB),
                  p3D(F_ML),
                  "rgba(255, 255, 0, 1)",
                  "rgba(255, 255, 0, 0.25)",
                  "HOOD"
                );
                drawQuad(
                  F_ML,
                  F_BL,
                  p3D(F_BL),
                  p3D(F_ML),
                  "rgba(0, 100, 255, 1)",
                  "rgba(0, 100, 255, 0.25)",
                  "FRONT"
                );
                drawQuad(
                  F_TR,
                  F_BR,
                  p3D(F_BR),
                  p3D(F_TR),
                  "rgba(255, 0, 0, 1)",
                  "rgba(255, 0, 0, 0.25)",
                  "REAR"
                );
              }

              // --- 5. DRAW TARGETS DIRECTLY AT HUB CENTROIDS ---
              const wheelRadiusX = w * 0.05;
              const wheelRadiusY = h * 0.07;

              // Front Wheel (Magenta)
              ctx.strokeStyle = "rgba(255, 0, 255, 1)";
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.ellipse(
                frontTire.x,
                frontTire.y,
                wheelRadiusX,
                wheelRadiusY,
                0,
                0,
                2 * Math.PI
              );
              ctx.stroke();

              // Rear Wheel Anchor (Orange Target Ring)
              ctx.strokeStyle = "rgba(255, 100, 0, 1)";
              ctx.lineWidth = 3;
              ctx.beginPath();
              ctx.ellipse(
                rearTire.x,
                rearTire.y,
                wheelRadiusX * 1.1,
                wheelRadiusY * 1.1,
                0,
                0,
                2 * Math.PI
              );
              ctx.stroke();
              ctx.beginPath();
              ctx.arc(rearTire.x, rearTire.y, 4, 0, 2 * Math.PI);
              ctx.fillStyle = "white";
              ctx.fill();

              // Tire Baseline Vector
              ctx.strokeStyle = "rgba(255, 255, 0, 0.9)";
              ctx.lineWidth = 2;
              ctx.beginPath();
              ctx.moveTo(lWheel.x, lWheel.y);
              ctx.lineTo(rWheel.x, rWheel.y);
              ctx.stroke();

              const pitchDeg = (
                Math.atan2(dyTires, dxTires) *
                (180 / Math.PI)
              ).toFixed(1);
              ctx.fillStyle = "#00ffff";
              ctx.font = "bold 12px monospace";
              ctx.fillText(
                `HUB CENTROID LOCKED [${pitchDeg}°]`,
                F_TL.x,
                F_TL.y - 8
              );
            });
          }
        }
        baseTensor.dispose();
      }

      ////////// NEW KIT ****vvvvvv********vvvvvvv NEW KIT

      // ==========================================
      // 39. GUARDIAN KIT (Pitch-Black Micro-Contrast Amplification)
      // ==========================================
      if (activeKit === "guardian") {
        // ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Pitch black wash to force the amber mesh to glow intensely
        // ctx.fillStyle = "rgba(0, 2, 8, 0.85)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // 1. THE PITCH-BLACK MASK
          // Only look at areas where light is practically non-existent (Luma <= 0.12)
          const shadowMask = tf.cast(
            luma.lessEqual(tf.scalar(0.12)),
            "float32"
          );

          // 2. NIGHT-VISION AMPLIFICATION
          // In pitch black, pixel values are like 0.01.
          // We violently multiply them by 25.0 to stretch the micro-contrast so the edge detector can "see" in the dark.
          const amplifiedDarks = luma.mul(tf.scalar(25.0));

          // 3. MICRO-STRUCTURE EDGE DETECTION
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const edges = tf
            .conv2d(
              amplifiedDarks.expandDims(0).expandDims(-1),
              edgeKernel,
              1,
              "same"
            )
            .abs()
            .squeeze();

          // 4. MESH EXTRACTION
          // Erase anything outside the pitch-black zones
          const hiddenStructures = edges.mul(shadowMask);

          // Push through Sigmoid to snap the raw amplified data into binary nodes
          const guardianNodes = tf.sigmoid(
            hiddenStructures.sub(tf.scalar(0.4)).mul(tf.scalar(12.0))
          );

          const nodeData = guardianNodes.dataSync();
          const w = canvas.width;
          const h = canvas.height;

          const stride = Math.max(4, Math.floor(w / 80));
          const points = [];

          for (let y = 0; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              if (nodeData[y * w + x] > 0.8) {
                // Add jitter to make the mesh feel alive and electric
                const jitterX = x + (Math.random() * 2 - 1);
                const jitterY = y + (Math.random() * 2 - 1);
                points.push({ x: jitterX, y: jitterY });
              }
            }
          }

          // 5. DRAW THE AMBER SHIELD
          const maxDist = stride * 4.5;
          ctx.lineWidth = 1;
          ctx.lineCap = "round";

          // Anchor nodes
          ctx.fillStyle = "rgba(7, 6, 237, 0.9)";
          points.forEach((pt) => ctx.fillRect(pt.x, pt.y, 1.5, 1.5));

          for (let i = 0; i < points.length; i++) {
            const p1 = points[i];
            let connections = 0;

            for (let j = i + 1; j < points.length; j++) {
              const p2 = points[j];
              const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

              if (dist < maxDist) {
                connections++;
                const opacity = 1 - dist / maxDist;
                ctx.strokeStyle = `rgba(7, 6, 237, ${opacity * 0.9})`;

                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();

                // Glass shielding fill for clusters
                if (connections === 2 && j + 1 < points.length) {
                  const p3 = points[j + 1];
                  if (Math.hypot(p3.x - p1.x, p3.y - p1.y) < maxDist) {
                    ctx.fillStyle = "rgba(87, 6, 237, 0.11)"; // Thick amber glass
                    ctx.beginPath();
                    ctx.moveTo(p1.x, p1.y);
                    ctx.lineTo(p2.x, p2.y);
                    ctx.lineTo(p3.x, p3.y);
                    ctx.closePath();
                    ctx.fill();
                  }
                }
              }
              // Limit connections so it retains an architectural wireframe look
              if (connections > 4) break;
            }
          }

          // ctx.fillStyle = "#ffaa00";
          // ctx.font = "bold 12px monospace";
          // ctx.fillText(
          //   `GUARDIAN (NIGHT VISION) | MICRO-NODES: ${points.length}`,
          //   20,
          //   h - 20
          // );
        });
        baseTensor.dispose();
      }

      // ==========================================
      // 40. STARLIGHT KIT (Organic Mesh & Color Node Integration)
      // ==========================================
      if (activeKit === "starlight") {
        // 1. Visual canvas for the user
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // 2. Pure Tensor Bypass
        const rawTensor = tf.browser.fromPixels(img);
        const baseTensor = tf.image.resizeBilinear(rawTensor, [
          canvas.height,
          canvas.width,
        ]);
        rawTensor.dispose();

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          const rawLuma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // --- 1. GATHER ALL LIGHT AND DARK (ReLU) ---
          const brightsRelu = tf.relu(rawLuma.sub(tf.scalar(0.7)));
          const darksRelu = tf.relu(tf.scalar(0.3).sub(rawLuma));
          const lumaNetworkRelu = brightsRelu.add(darksRelu);
          const lumaNodes = tf.sigmoid(lumaNetworkRelu.mul(tf.scalar(15.0)));

          // --- 2. GATHER ALL COLORS (ReLU) ---
          const rgDiff = r.sub(g).abs();
          const rbDiff = r.sub(b).abs();
          const gbDiff = g.sub(b).abs();
          const colorVariance = rgDiff.add(rbDiff).add(gbDiff).squeeze();

          const colorRelu = tf.relu(colorVariance.sub(tf.scalar(0.25)));
          const colorNodes = tf.sigmoid(colorRelu.mul(tf.scalar(15.0)));

          // --- 3. PULL DATA TO JAVASCRIPT ---
          const lumaNodeData = lumaNodes.dataSync();
          const colorNodeData = colorNodes.dataSync();
          const rawLumaData = rawLuma.dataSync();

          const rData = r.squeeze().dataSync();
          const gData = g.squeeze().dataSync();
          const bData = b.squeeze().dataSync();

          const w = canvas.width;
          const h = canvas.height;
          const stride = Math.max(5, Math.floor(w / 70));

          const whiteNodes = [];
          const blueNodes = [];
          const colorPoints = [];

          // Scan the matrix arrays
          for (let y = 0; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;

              // JITTER: Randomize the exact coordinates to break the rigid grid look!
              const jx = x + (Math.random() * stride - stride * 0.5);
              const jy = y + (Math.random() * stride - stride * 0.5);

              // Gather Light/Dark Anchors
              if (lumaNodeData[idx] > 0.5) {
                if (rawLumaData[idx] > 0.5) {
                  whiteNodes.push({ x: jx, y: jy });
                } else {
                  blueNodes.push({ x: jx, y: jy });
                }
              }

              // Gather Color Nodes
              if (colorNodeData[idx] > 0.5) {
                colorPoints.push({
                  x: jx,
                  y: jy,
                  r: Math.floor(rData[idx] * 255),
                  g: Math.floor(gData[idx] * 255),
                  b: Math.floor(bData[idx] * 255),
                });
              }
            }
          }

          // --- 4. RENDER THE EXTRACTED NETWORK ---
          const maxAnchorDist = stride * 5.5;

          // A. Draw the Base Nodes
          whiteNodes.forEach((pt) => {
            ctx.fillStyle = "rgba(255, 255, 255, 1)";
            ctx.fillRect(pt.x - 1, pt.y - 1, 2, 2);
          });

          blueNodes.forEach((pt) => {
            ctx.fillStyle = "rgba(0, 100, 255, 1)";
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, 1.5, 0, 2 * Math.PI);
            ctx.fill();
          });

          // B. BLUE-TO-BLUE MESH (3 to 6 Connections)
          blueNodes.forEach((bPt) => {
            let bDists = blueNodes
              .filter((other) => other !== bPt)
              .map((other) => ({
                pt: other,
                d: Math.hypot(other.x - bPt.x, other.y - bPt.y),
              }))
              .filter((item) => item.d < maxAnchorDist * 1.2) // Give blue nodes a slightly wider reach
              .sort((a, b) => a.d - b.d);

            const bConnectCount = Math.floor(Math.random() * 4) + 3; // Connects to 3, 4, 5, or 6
            const bTargets = bDists.slice(0, bConnectCount);

            bTargets.forEach((target, index) => {
              ctx.strokeStyle = "rgba(0, 100, 255, 1)";
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(bPt.x, bPt.y);
              ctx.lineTo(target.pt.x, target.pt.y);
              ctx.stroke();

              // Translucent Geometric Blue Face Fills
              if (index < bTargets.length - 1) {
                const nextTarget = bTargets[index + 1];
                if (
                  Math.hypot(
                    target.pt.x - nextTarget.pt.x,
                    target.pt.y - nextTarget.pt.y
                  ) < maxAnchorDist
                ) {
                  ctx.fillStyle = "rgba(0, 120, 255, .1)";
                  ctx.beginPath();
                  ctx.moveTo(bPt.x, bPt.y);
                  ctx.lineTo(target.pt.x, target.pt.y);
                  ctx.lineTo(nextTarget.pt.x, nextTarget.pt.y);
                  ctx.closePath();
                  ctx.fill();
                }
              }
            });
          });

          // C. WHITE-TO-WHITE & WHITE-TO-BLUE MESH
          whiteNodes.forEach((wPt) => {
            // 1. Connect to 1 or 2 nearby WHITE nodes
            let wDists = whiteNodes
              .filter((other) => other !== wPt)
              .map((other) => ({
                pt: other,
                d: Math.hypot(other.x - wPt.x, other.y - wPt.y),
              }))
              .filter((item) => item.d < maxAnchorDist)
              .sort((a, b) => a.d - b.d);

            const wConnectCount = Math.floor(Math.random() * 2) + 1;
            const wTargets = wDists.slice(0, wConnectCount);

            wTargets.forEach((target) => {
              ctx.strokeStyle = "rgba(255, 255, 255, 1)";
              ctx.lineWidth = 1.2;
              ctx.beginPath();
              ctx.moveTo(wPt.x, wPt.y);
              ctx.lineTo(target.pt.x, target.pt.y);
              ctx.stroke();
            });

            // 2. Connect to 3, 4, or 5 nearby BLUE nodes
            let bDists = blueNodes
              .map((other) => ({
                pt: other,
                d: Math.hypot(other.x - wPt.x, other.y - wPt.y),
              }))
              .filter((item) => item.d < maxAnchorDist)
              .sort((a, b) => a.d - b.d);

            const bConnectCount = Math.floor(Math.random() * 3) + 3;
            const bTargets = bDists.slice(0, bConnectCount);

            bTargets.forEach((target, index) => {
              const grad = ctx.createLinearGradient(
                wPt.x,
                wPt.y,
                target.pt.x,
                target.pt.y
              );
              grad.addColorStop(0, "rgba(255, 255, 255, 1)");
              grad.addColorStop(1, "rgba(141, 0, 255, 1)");

              ctx.strokeStyle = grad;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(wPt.x, wPt.y);
              ctx.lineTo(target.pt.x, target.pt.y);
              ctx.stroke();

              // Translucent Transition Faces
              if (index < bTargets.length - 1) {
                const nextTarget = bTargets[index + 1];
                if (
                  Math.hypot(
                    target.pt.x - nextTarget.pt.x,
                    target.pt.y - nextTarget.pt.y
                  ) < maxAnchorDist
                ) {
                  ctx.fillStyle = "rgba(141, 0, 255, .21)";
                  ctx.beginPath();
                  ctx.moveTo(wPt.x, wPt.y);
                  ctx.lineTo(target.pt.x, target.pt.y);
                  ctx.lineTo(nextTarget.pt.x, nextTarget.pt.y);
                  ctx.closePath();
                  ctx.fill();
                }
              }
            });

            // Bonus Geometric Face: White-White-Blue Fill
            if (wTargets.length > 0 && bTargets.length > 0) {
              ctx.fillStyle = "rgba(255, 255, 255, 1)";
              ctx.beginPath();
              ctx.moveTo(wPt.x, wPt.y);
              ctx.lineTo(wTargets[0].pt.x, wTargets[0].pt.y);
              ctx.lineTo(bTargets[0].pt.x, bTargets[0].pt.y);
              ctx.closePath();
              ctx.fill();
            }
          });

          // D. COLOR NODES (Integrated deeply into the mesh, no more wild vectors)
          const allLumaPoints = [...whiteNodes, ...blueNodes];

          // colorPoints.forEach((cp) => {
          //   // Draw the pure color node
          //   ctx.fillStyle = `rgba(255,0,0,1)`;
          //   ctx.beginPath();
          //   ctx.arc(cp.x, cp.y, 2.5, 0, 2 * Math.PI);
          //   ctx.fill();

          //   // Find 1 or 2 closest mesh nodes to anchor to locally
          //   let closestMesh = allLumaPoints
          //     .map((other) => ({
          //       pt: other,
          //       d: Math.hypot(other.x - cp.x, other.y - cp.y),
          //     }))
          //     .filter((item) => item.d < maxAnchorDist * 0.8) // Tight radius so lines don't get wild
          //     .sort((a, b) => a.d - b.d);

          //   closestMesh.slice(0, 2).forEach((target) => {
          //     const grad = ctx.createLinearGradient(
          //       cp.x,
          //       cp.y,
          //       target.pt.x,
          //       target.pt.y
          //     );
          //     grad.addColorStop(0, `rgba(255,0,0,1)`);

          //     // Fade into the structure based on what type of node it's attaching to
          //     // if (whiteNodes.includes(target.pt)) {
          //     //   grad.addColorStop(1, "rgba(255, 255, 255, 0.2)");
          //     // } else {
          //     //   grad.addColorStop(1, "rgba(0, 100, 255, 0.2)");
          //     // }

          //     ctx.strokeStyle = grad;
          //     ctx.lineWidth = 1.2;
          //     ctx.beginPath();
          //     ctx.moveTo(cp.x, cp.y);
          //     ctx.lineTo(target.pt.x, target.pt.y);
          //     ctx.stroke();
          //   });
          // });
        });
        baseTensor.dispose();
      }

      // ==========================================
      // NEW: HUMAN DEPTH KIT (Face Features, Limbs/Torso, Depth Sorting)
      // ==========================================
      if (activeKit === "humandepth") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // --- 1. POSE DETECTION (Arms, Legs, Torso & Depth) ---
        const poseModel = poseDetection.SupportedModels.MoveNet;
        const poseDetector = await poseDetection.createDetector(poseModel);
        const poses = await poseDetector.estimatePoses(img);

        if (poses.length > 0) {
          // Calculate rough bounding box area for each pose to determine who is closer
          const posesWithArea = poses.map((pose) => {
            const xs = pose.keypoints.map((kp) => kp.x);
            const ys = pose.keypoints.map((kp) => kp.y);
            const minX = Math.min(...xs),
              maxX = Math.max(...xs);
            const minY = Math.min(...ys),
              maxY = Math.max(...ys);
            const area = (maxX - minX) * (maxY - minY);
            return { ...pose, area, minX, minY, maxX, maxY };
          });

          // Sort by area descending (largest area = closest to camera / in front)
          posesWithArea.sort((a, b) => b.area - a.area);

          posesWithArea.forEach((pose, index) => {
            // Closest person is Cyan (IN FRONT), others are Orange (BEHIND)
            const isFront = index === 0;
            const color = isFront ? "#00ffff" : "#ffaa00";
            const label = isFront ? "IN FRONT" : `BEHIND (Depth Lvl ${index})`;

            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.fillStyle = color;

            const kp = pose.keypoints;

            // Helper to draw bone if both points are confident enough
            const drawBone = (i, j) => {
              if (kp[i].score > 0.3 && kp[j].score > 0.3) {
                ctx.beginPath();
                ctx.moveTo(kp[i].x, kp[i].y);
                ctx.lineTo(kp[j].x, kp[j].y);
                ctx.stroke();
              }
            };

            // TORSO: Shoulders (5,6) to Hips (11,12)
            drawBone(5, 6);
            drawBone(6, 12);
            drawBone(12, 11);
            drawBone(11, 5);
            // ARMS: Shoulders to Elbows to Wrists
            drawBone(5, 7);
            drawBone(7, 9); // Left arm
            drawBone(6, 8);
            drawBone(8, 10); // Right arm
            // LEGS: Hips to Knees to Ankles
            drawBone(11, 13);
            drawBone(13, 15); // Left leg
            drawBone(12, 14);
            drawBone(14, 16); // Right leg

            // Label the depth mapping
            ctx.font = "bold 16px Arial";
            ctx.fillText(label, pose.minX, Math.max(20, pose.minY - 10));
          });
        }

        // --- 2. FACE DETECTION (Strictly Eyes & Lips focus) ---
        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const faceDetector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );
        const faces = await faceDetector.estimateFaces(img);

        faces.forEach((face) => {
          const lipsOuter = [
            61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269,
            267, 0, 37, 39, 40, 185,
          ];
          const rightEye = [
            33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160,
            161, 246,
          ];
          const leftEye = [
            263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386,
            387, 388, 466,
          ];

          const drawFeature = (indices, color) => {
            ctx.beginPath();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            indices.forEach((index, i) => {
              const pt = face.keypoints[index];
              if (i === 0) ctx.moveTo(pt.x, pt.y);
              else ctx.lineTo(pt.x, pt.y);
            });
            ctx.closePath();
            ctx.stroke();
          };

          // Draw Eyes (Cyan) and Lips (Magenta) to isolate structural features
          drawFeature(rightEye, "#00ffff");
          drawFeature(leftEye, "#00ffff");
          drawFeature(lipsOuter, "#ff00ff");
        });
      }

      // ==========================================
      // NEW: HUMAN PRESENCE KIT (Partial Body / Edge Combinations)
      // ==========================================

      // ==========================================
      // NEW: BONE FLOW KIT (Structural Light Ridges & Limb Segments)
      // ==========================================
      if (activeKit === "boneflow") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Darken the background heavily so the structural light ridges pop
        // ctx.fillStyle = "rgba(0, 5, 10, 0.85)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          // 1. NORMALIZER & SPLIT: Convert 0-255 pixels to 0.0-1.0 floats, then split into RGB
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);

          // 2. LUMINANCE: Calculate pure perceived light (ignoring color/clothing)
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();

          // 3. STRUCTURAL KERNEL: Find the physical boundaries (arms, legs, torsos)
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          // 4. THE ReLU FILTER: As you suggested, light comes off the bone straight.
          // We subtract 0.2 to crush weak background textures into oblivion.
          // We then use ReLU to keep ONLY the strong structural highlights.
          const boneHighlights = tf.relu(edges.sub(tf.scalar(0.2)));

          // Multiply by 8 to amplify the surviving limb structures, then cap with Sigmoid
          const boneStructure = tf.sigmoid(boneHighlights.mul(tf.scalar(8.0)));

          const maskData = boneStructure.dataSync();
          const w = canvas.width;
          const h = canvas.height;

          // Dynamic stride based on image size to capture fine details like fingers
          const stride = Math.max(3, Math.floor(w / 100));
          const structuralPoints = [];

          // Scan the matrix for surviving "bone light" pixels
          for (let y = 0; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              if (maskData[y * w + x] > 0.6) {
                structuralPoints.push({ x, y });
              }
            }
          }

          // 5. DRAW THE FLOW: Connect the ridges to show the structural flow
          ctx.strokeStyle = "rgba(0, 255, 255, 0.8)"; // Electric Cyan
          ctx.lineWidth = 1.5;
          ctx.lineCap = "round";

          // Tight maximum distance forces the lines to follow the natural straight geometry of the limbs
          const maxDist = stride * 3;

          for (let i = 0; i < structuralPoints.length; i++) {
            const p1 = structuralPoints[i];

            // Draw a tiny bright node where the light is strongest
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(p1.x, p1.y, 1.5, 1.5);

            // Connect nearby structural nodes
            for (let j = i + 1; j < structuralPoints.length; j++) {
              const p2 = structuralPoints[j];
              const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);

              if (dist < maxDist) {
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
              }
            }
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // NEW: HUMAN SCAFFOLD KIT (5x5 Macro-Kernels + Hierarchical Filtering)
      // Eliminates background noise by forcing limbs to attach to torsos.
      // ==========================================
      if (activeKit === "humanscaffold") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // ctx.fillStyle = "rgba(0, 5, 15, 0.85)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();
          const expandedLuma = luma.expandDims(0).expandDims(-1);

          // 1. MACRO-KERNELS (5x5)
          // The Torso Kernel: Looks for a wide, solid central mass of light/structure
          const torsoKernel = tf.tensor4d(
            [
              -1, -1, -1, -1, -1, -1, 1, 1, 1, -1, -1, 1, 2, 1, -1, -1, 1, 1, 1,
              -1, -1, -1, -1, -1, -1,
            ],
            [5, 5, 1, 1]
          );

          // The Limb Shaft Kernel: Looks for elongated, straight structures bridging spaces
          const limbKernel = tf.tensor4d(
            [
              0, -1, 2, -1, 0, 0, -1, 2, -1, 0, 0, -1, 2, -1, 0, 0, -1, 2, -1,
              0, 0, -1, 2, -1, 0,
            ],
            [5, 5, 1, 1]
          );

          // The Extremity Kernel: Micro-details (3x3)
          const extKernel = tf.tensor4d(
            [-1, -1, -1, -1, 8, -1, -1, -1, -1],
            [3, 3, 1, 1]
          );

          // 2. RUN THE CONVOLUTIONS
          const rawTorso = tf
            .conv2d(expandedLuma, torsoKernel, 1, "same")
            .abs()
            .squeeze();
          // We transpose the limb kernel to catch both vertical and horizontal limb shafts
          const rawLimbV = tf
            .conv2d(expandedLuma, limbKernel, 1, "same")
            .abs()
            .squeeze();
          const rawLimbH = tf
            .conv2d(expandedLuma, limbKernel.transpose([1, 0, 2, 3]), 1, "same")
            .abs()
            .squeeze();
          const rawLimb = rawLimbV.add(rawLimbH);
          const rawExt = tf
            .conv2d(expandedLuma, extKernel, 1, "same")
            .abs()
            .squeeze();

          // 3. CLEAN THE DATA (ReLU + Sigmoid)
          // High thresholds aggressively delete background flak
          const torsoMask = tf.sigmoid(
            tf.relu(rawTorso.sub(tf.scalar(0.4))).mul(tf.scalar(10.0))
          );
          const limbMask = tf.sigmoid(
            tf.relu(rawLimb.sub(tf.scalar(0.3))).mul(tf.scalar(10.0))
          );
          const extMask = tf.sigmoid(
            tf.relu(rawExt.sub(tf.scalar(0.5))).mul(tf.scalar(10.0))
          );

          // 4. HIERARCHICAL SCAFFOLDING (The Magic)
          // Dilate the torso so it acts as an "anchor zone" for limbs
          const poolFilter = tf.tensor4d(
            [1, 1, 1, 1, 1, 1, 1, 1, 1],
            [3, 3, 1, 1]
          );
          const torsoZone = tf
            .conv2d(
              torsoMask.expandDims(0).expandDims(-1),
              poolFilter,
              1,
              "same"
            )
            .squeeze();
          const limbZone = tf
            .conv2d(
              limbMask.expandDims(0).expandDims(-1),
              poolFilter,
              1,
              "same"
            )
            .squeeze();

          // Multiply masks: Limbs ONLY exist if they touch a TorsoZone.
          // Extremities ONLY exist if they touch a LimbZone.
          const verifiedLimbs = limbMask.mul(
            tf.cast(torsoZone.greater(tf.scalar(0.1)), "float32")
          );
          const verifiedExt = extMask.mul(
            tf.cast(limbZone.greater(tf.scalar(0.1)), "float32")
          );

          // 5. EXTRACT & DRAW
          const torsoData = torsoMask.dataSync();
          const limbData = verifiedLimbs.dataSync();
          const extData = verifiedExt.dataSync();

          const w = canvas.width;
          const h_img = canvas.height;
          const stride = Math.max(5, Math.floor(w / 80));

          for (let y = 0; y < h_img; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;
              const len = stride * 0.8;

              // Draw Torsos (Magenta Blocks)
              if (torsoData[idx] > 0.8) {
                ctx.fillStyle = "rgba(255, 0, 255, 0.4)";
                ctx.fillRect(x - len, y - len, len * 2, len * 2);
              }
              // Draw Verified Limbs (Cyan Lines)
              if (limbData[idx] > 0.6) {
                ctx.strokeStyle = "#00ffff";
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(x - len, y - len);
                ctx.lineTo(x + len, y + len);
                ctx.stroke();
              }
              // Draw Verified Extremities (Neon Green Dots)
              if (extData[idx] > 0.6) {
                ctx.fillStyle = "#00ff00";
                ctx.beginPath();
                ctx.arc(x, y, 2.5, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // NEW: ANATOMY MATRIX KIT (13 Macro-Kernels for Definite Human Segments)
      // Uses structural intersections, limb thicknesses, and facial geometry
      // ==========================================
      if (activeKit === "anatomymatrix") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Darken the background heavily so the HUD elements glow
        // ctx.fillStyle = "rgba(0, 5, 12, 0.85)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();
          const expandedLuma = luma.expandDims(0).expandDims(-1);

          // --- 1. DEFINE ALL 13 ANATOMICAL 5x5 KERNELS ---

          // JOINTS & INTERSECTIONS
          const kVbend = tf.tensor4d(
            [
              1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kLsocket = tf.tensor4d(
            [
              0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, -4, 1, 1, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kTjunction = tf.tensor4d(
            [
              0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 1, -4, 1, 1, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kYsplit = tf.tensor4d(
            [
              1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 0, 0, -6, 0, 0, 0, 0, 1, 0, 0, 0, 0,
              1, 0, 0,
            ],
            [5, 5, 1, 1]
          );

          // LIMB SHAFTS (THICKNESS & TAPER)
          const kFemur = tf.tensor4d(
            [
              -2, 1, 2, 1, -2, -2, 1, 2, 1, -2, -2, 1, 2, 1, -2, -2, 1, 2, 1,
              -2, -2, 1, 2, 1, -2,
            ],
            [5, 5, 1, 1]
          );
          const kCalf = tf.tensor4d(
            [
              -2, 1, 2, 1, -2, -2, 0, 2, 0, -2, -2, -1, 2, -1, -2, -2, -1, 2,
              -1, -2, -2, -1, 1, -1, -2,
            ],
            [5, 5, 1, 1]
          );
          const kHumerus = tf.tensor4d(
            [
              -1, -2, 3, -2, -1, -1, -2, 3, -2, -1, -1, -2, 3, -2, -1, -1, -2,
              3, -2, -1, -1, -2, 3, -2, -1,
            ],
            [5, 5, 1, 1]
          );
          const kForearm = tf.tensor4d(
            [
              0, -3, 4, -3, 0, 0, -3, 4, -3, 0, 0, -3, 4, -3, 0, 0, -3, 4, -3,
              0, 0, -3, 4, -3, 0,
            ],
            [5, 5, 1, 1]
          );

          // FACIAL FEATURES (MICRO)
          const kEye = tf.tensor4d(
            [
              -1, -2, -2, -2, -1, -2, 4, 6, 4, -2, -1, -2, -2, -2, -1, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kEyebrow = tf.tensor4d(
            [
              0, -1, -2, -1, 0, -1, 3, 4, 3, -1, -2, -1, -2, -1, -2, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kLips = tf.tensor4d(
            [
              0, -1, 2, -1, 0, -2, 1, -4, 1, -2, 0, -1, 2, -1, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );

          // HEAD SILHOUETTES (MACRO)
          const kSideProfile = tf.tensor4d(
            [
              -1, -2, 1, 1, -1, -2, 1, -2, 2, -1, -2, 1, 4, -1, 2, -1, 1, -1,
              -2, 1, 0, 1, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kBackHead = tf.tensor4d(
            [
              -2, -2, 2, -2, -2, -1, 1, -2, 1, -1, 2, -2, -2, -2, 2, -1, 2, -2,
              2, -1, 0, 2, -2, 2, 0,
            ],
            [5, 5, 1, 1]
          );

          // --- 2. TENSOR PROCESSOR ENGINE ---
          const processConv = (kernel, thresholdOffset) => {
            const edges = tf
              .conv2d(expandedLuma, kernel, 1, "same")
              .abs()
              .squeeze();
            // High intensity cleanup: subtract threshold, apply ReLU, and amplify with Sigmoid
            return tf
              .sigmoid(
                tf
                  .relu(edges.sub(tf.scalar(thresholdOffset)))
                  .mul(tf.scalar(12.0))
              )
              .dataSync();
          };

          // Heavy extraction (This may take a moment to compute on large images, hence the loading state!)
          const dVbend = processConv(kVbend, 0.35);
          const dLsocket = processConv(kLsocket, 0.35);
          const dTjunction = processConv(kTjunction, 0.35);
          const dYsplit = processConv(kYsplit, 0.35);

          const dFemur = processConv(kFemur, 0.4);
          const dCalf = processConv(kCalf, 0.4);
          const dHumerus = processConv(kHumerus, 0.4);
          const dForearm = processConv(kForearm, 0.45);

          const dEye = processConv(kEye, 0.5); // Eyes need high contrast threshold
          const dEyebrow = processConv(kEyebrow, 0.4);
          const dLips = processConv(kLips, 0.45);

          const dSideProfile = processConv(kSideProfile, 0.35);
          const dBackHead = processConv(kBackHead, 0.35);

          const w = canvas.width;
          const h_img = canvas.height;

          // Use a large stride to prevent UI clutter and text overlapping
          const stride = Math.max(10, Math.floor(w / 45));

          // --- 3. DRAWING LOGIC ---
          const drawBoundary = (x, y, label, color) => {
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = 1.5;

            // Draw the Boundary Decision Box
            const s = stride * 0.8;
            ctx.beginPath();
            ctx.rect(x - s, y - s, s * 2, s * 2);
            ctx.stroke();

            // Draw a targeting dot
            ctx.fillRect(x - 1, y - 1, 3, 3);

            // Add the text label slightly offset
            ctx.font = "bold 10px monospace";
            ctx.fillText(label, x + s + 3, y + 4);
          };

          // --- 4. MAP THE RESULTS ---
          for (let y = 0; y < h_img; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;
              // Only draw if confidence is extremely high (> 0.9) to ensure accuracy

              // GROUP A: HEAD & FACE (Yellows & Greens)
              // if (dEye[idx] > 0.9) drawBoundary(x, y, "EYE", "#ffff00");
              // else if (dLips[idx] > 0.9) drawBoundary(x, y, "LIPS", "#ffaa00");
              // else if (dEyebrow[idx] > 0.9)
              //   drawBoundary(x, y, "EYEBROW", "#ccff00");
              // else if (dSideProfile[idx] > 0.9)
              //   drawBoundary(x, y, "SIDE PROFILE", "#00ff00");
              // else if (dBackHead[idx] > 0.9)
              //   drawBoundary(x, y, "BACK OF HEAD", "#00cc00");
              // GROUP B: JOINTS & CONNECTIONS (Magentas)
              // if (dVbend[idx] > 0.9)
              //   drawBoundary(x, y, "V-BEND (KNEE/ELBOW)", "#ff00ff");
              // else if (dLsocket[idx] > 0.9)
              //   drawBoundary(x, y, "L-SOCKET (HIP/SHOULDER)", "#ff00aa");
              // else if (dTjunction[idx] > 0.9)
              //   drawBoundary(x, y, "T-JUNCTION (TORSO)", "#cc00ff");
              // else if (dYsplit[idx] > 0.9)
              //   drawBoundary(x, y, "Y-SPLIT (HAND/FOOT)", "#ff0088");
              // GROUP C: LIMB SHAFTS (Cyans & Blues)
              if (dFemur[idx] > 0.9) drawBoundary(x, y, "FEMUR", "#0088ff");
              else if (dCalf[idx] > 0.9) drawBoundary(x, y, "CALF", "#00ffff");
              else if (dHumerus[idx] > 0.9)
                drawBoundary(x, y, "HUMERUS", "#00ccff");
              else if (dForearm[idx] > 0.9)
                drawBoundary(x, y, "FOREARM", "#88ffff");
            }
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // NEW: DIRECTIONAL ANATOMY KIT (5-Kernel Multi-Axis Mapping)
      // Maps vertical, horizontal, diagonal, and extremity segments using pure math
      // ==========================================
      if (activeKit === "directionalanatomy") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // Deep background to let the multi-colored vectors glow
        // ctx.fillStyle = "rgba(0, 5, 10, 0.9)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();
          const expandedLuma = luma.expandDims(0).expandDims(-1);

          // 1. DEFINE ALL 5 EDGE KERNELS
          const vKernel = tf.tensor4d(
            [-1, 0, 1, -2, 0, 2, -1, 0, 1],
            [3, 3, 1, 1]
          ); // Vertical
          const hKernel = tf.tensor4d(
            [-1, -2, -1, 0, 0, 0, 1, 2, 1],
            [3, 3, 1, 1]
          ); // Horizontal
          const fdKernel = tf.tensor4d(
            [0, 1, 2, -1, 0, 1, -2, -1, 0],
            [3, 3, 1, 1]
          ); // Forward Diagonal
          const bdKernel = tf.tensor4d(
            [-2, -1, 0, -1, 0, 1, 0, 1, 2],
            [3, 3, 1, 1]
          ); // Backward Diagonal
          const extKernel = tf.tensor4d(
            [-1, -1, -1, -1, 8, -1, -1, -1, -1],
            [3, 3, 1, 1]
          ); // Extremities

          // 2. RUN SIMULTANEOUS CONVOLUTIONS
          const vEdges = tf
            .conv2d(expandedLuma, vKernel, 1, "same")
            .abs()
            .squeeze();
          const hEdges = tf
            .conv2d(expandedLuma, hKernel, 1, "same")
            .abs()
            .squeeze();
          const fdEdges = tf
            .conv2d(expandedLuma, fdKernel, 1, "same")
            .abs()
            .squeeze();
          const bdEdges = tf
            .conv2d(expandedLuma, bdKernel, 1, "same")
            .abs()
            .squeeze();
          const extEdges = tf
            .conv2d(expandedLuma, extKernel, 1, "same")
            .abs()
            .squeeze();

          // 3. FILTER & AMPLIFY (ReLU + Sigmoid)
          // Subtracting 0.2 removes weak fabric wrinkles and background noise
          const processEdge = (edgeTensor) =>
            tf.sigmoid(
              tf.relu(edgeTensor.sub(tf.scalar(0.2))).mul(tf.scalar(8.0))
            );

          const vData = processEdge(vEdges).dataSync();
          const hData = processEdge(hEdges).dataSync();
          const fdData = processEdge(fdEdges).dataSync();
          const bdData = processEdge(bdEdges).dataSync();
          const extData = processEdge(extEdges).dataSync();

          const w = canvas.width;
          const h_img = canvas.height;
          const stride = Math.max(4, Math.floor(w / 100));

          ctx.lineWidth = 1.5;
          ctx.lineCap = "round";

          // 4. MAP THE VECTORS
          for (let y = 0; y < h_img; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;
              const len = stride * 0.8;

              // Priority 1: High-Frequency Extremities (Hands, Feet, Faces)
              if (extData[idx] > 0.75) {
                ctx.fillStyle = "#00ff00"; // Neon Green
                ctx.beginPath();
                ctx.arc(x, y, 1.5, 0, Math.PI * 2);
                ctx.fill();
              }
              // Priority 2: Forward Diagonal (Stepping Legs, Raised Arms)
              else if (fdData[idx] > 0.65) {
                ctx.strokeStyle = "#ffaa00"; // Neon Orange
                ctx.beginPath();
                ctx.moveTo(x - len, y + len);
                ctx.lineTo(x + len, y - len);
                ctx.stroke();
              }
              // Priority 3: Backward Diagonal (Trailing Legs, Bent Arms)
              else if (bdData[idx] > 0.65) {
                ctx.strokeStyle = "#ff00ff"; // Magenta
                ctx.beginPath();
                ctx.moveTo(x - len, y - len);
                ctx.lineTo(x + len, y + len);
                ctx.stroke();
              }
              // Priority 4: Vertical (Standing Legs, Torso Sides)
              else if (vData[idx] > 0.6) {
                ctx.strokeStyle = "#ff0000"; // Bright Red
                ctx.beginPath();
                ctx.moveTo(x, y - len);
                ctx.lineTo(x, y + len);
                ctx.stroke();
              }
              // Priority 5: Horizontal (Outstretched Arms, Shoulders)
              else if (hData[idx] > 0.6) {
                ctx.strokeStyle = "#0088ff"; // Electric Blue
                ctx.beginPath();
                ctx.moveTo(x - len, y);
                ctx.lineTo(x + len, y);
                ctx.stroke();
              }
            }
          }
        });
        baseTensor.dispose();
      }

      // ==========================================
      // NEW: ANATOMY LOGIC KIT (13 Kernels + Spatial Context Verification)
      // Only draws body parts if they physically connect to corresponding anatomical neighbors.
      // ==========================================
      if (activeKit === "anatomylogic") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // ctx.fillStyle = "rgba(0, 5, 12, 0.85)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();
          const expandedLuma = luma.expandDims(0).expandDims(-1);

          // --- 1. THE 13 MACRO-KERNELS ---
          const kVbend = tf.tensor4d(
            [
              1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, -4, 0, 0, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kLsocket = tf.tensor4d(
            [
              0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, -4, 1, 1, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kTjunction = tf.tensor4d(
            [
              0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 1, -4, 1, 1, 0, 0, 0, 0, 0, 0, 0,
              0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kYsplit = tf.tensor4d(
            [
              1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 0, 0, -6, 0, 0, 0, 0, 1, 0, 0, 0, 0,
              1, 0, 0,
            ],
            [5, 5, 1, 1]
          );

          const kFemur = tf.tensor4d(
            [
              -2, 1, 2, 1, -2, -2, 1, 2, 1, -2, -2, 1, 2, 1, -2, -2, 1, 2, 1,
              -2, -2, 1, 2, 1, -2,
            ],
            [5, 5, 1, 1]
          );
          const kCalf = tf.tensor4d(
            [
              -2, 1, 2, 1, -2, -2, 0, 2, 0, -2, -2, -1, 2, -1, -2, -2, -1, 2,
              -1, -2, -2, -1, 1, -1, -2,
            ],
            [5, 5, 1, 1]
          );
          const kHumerus = tf.tensor4d(
            [
              -1, -2, 3, -2, -1, -1, -2, 3, -2, -1, -1, -2, 3, -2, -1, -1, -2,
              3, -2, -1, -1, -2, 3, -2, -1,
            ],
            [5, 5, 1, 1]
          );
          const kForearm = tf.tensor4d(
            [
              0, -3, 4, -3, 0, 0, -3, 4, -3, 0, 0, -3, 4, -3, 0, 0, -3, 4, -3,
              0, 0, -3, 4, -3, 0,
            ],
            [5, 5, 1, 1]
          );

          const kEye = tf.tensor4d(
            [
              -1, -2, -2, -2, -1, -2, 4, 6, 4, -2, -1, -2, -2, -2, -1, 0, 0, 0,
              0, 0, 0, 0, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kEyebrow = tf.tensor4d(
            [
              0, -1, -2, -1, 0, -1, 3, 4, 3, -1, -2, -1, -2, -1, -2, 0, 0, 0, 0,
              0, 0, 0, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kLips = tf.tensor4d(
            [
              0, -1, 2, -1, 0, -2, 1, -4, 1, -2, 0, -1, 2, -1, 0, 0, 0, 0, 0, 0,
              0, 0, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );

          const kSideProfile = tf.tensor4d(
            [
              -1, -2, 1, 1, -1, -2, 1, -2, 2, -1, -2, 1, 4, -1, 2, -1, 1, -1,
              -2, 1, 0, 1, 0, 0, 0,
            ],
            [5, 5, 1, 1]
          );
          const kBackHead = tf.tensor4d(
            [
              -2, -2, 2, -2, -2, -1, 1, -2, 1, -1, 2, -2, -2, -2, 2, -1, 2, -2,
              2, -1, 0, 2, -2, 2, 0,
            ],
            [5, 5, 1, 1]
          );

          // --- 2. EXTRACT TENSOR DATA ---
          const processConv = (kernel, thresholdOffset) => {
            const edges = tf
              .conv2d(expandedLuma, kernel, 1, "same")
              .abs()
              .squeeze();
            return tf
              .sigmoid(
                tf
                  .relu(edges.sub(tf.scalar(thresholdOffset)))
                  .mul(tf.scalar(12.0))
              )
              .dataSync();
          };

          const dataMap = {
            joint: [
              processConv(kVbend, 0.35),
              processConv(kLsocket, 0.35),
              processConv(kTjunction, 0.35),
            ],
            extremity: [processConv(kYsplit, 0.35)],
            limb: [
              processConv(kFemur, 0.4),
              processConv(kCalf, 0.4),
              processConv(kHumerus, 0.4),
              processConv(kForearm, 0.45),
            ],
            face: [
              processConv(kEye, 0.5),
              processConv(kEyebrow, 0.4),
              processConv(kLips, 0.45),
            ],
            head: [
              processConv(kSideProfile, 0.35),
              processConv(kBackHead, 0.35),
            ],
          };

          const w = canvas.width;
          const h_img = canvas.height;
          const stride = Math.max(8, Math.floor(w / 50));
          const maxConnectDist = stride * 4; // Max distance for anatomical parts to be considered "connected"

          // Arrays to hold coordinates before drawing
          const pools = {
            joint: [],
            extremity: [],
            limb: [],
            face: [],
            head: [],
          };
          const unverifiedNodes = [];

          // --- 3. HARVEST ALL HIGH-CONFIDENCE NODES ---
          const checkGroup = (groupData, x, y, idx, category, label, color) => {
            for (let i = 0; i < groupData.length; i++) {
              if (groupData[i][idx] > 0.9) {
                const node = { x, y, category, label, color };
                pools[category].push(node);
                unverifiedNodes.push(node);
                break; // Stop checking other kernels in this group if one fired
              }
            }
          };

          for (let y = 0; y < h_img; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;
              // Pass labels based on group arrays
              checkGroup(
                dataMap.face,
                x,
                y,
                idx,
                "face",
                "FACIAL FEATURE",
                "#ffff00"
              );
              checkGroup(
                dataMap.head,
                x,
                y,
                idx,
                "head",
                "HEAD MACRO",
                "#00ff00"
              );
              checkGroup(
                dataMap.joint,
                x,
                y,
                idx,
                "joint",
                "JOINT / SOCKET",
                "#ff00aa"
              );
              checkGroup(
                dataMap.extremity,
                x,
                y,
                idx,
                "extremity",
                "HAND / FOOT",
                "#ff0088"
              );
              checkGroup(
                dataMap.limb,
                x,
                y,
                idx,
                "limb",
                "LIMB SHAFT",
                "#00ffff"
              );
            }
          }

          // --- 4. SPATIAL VERIFICATION LOGIC (The Filter) ---
          // Returns true if a node is near ANY node in the target pool
          const isNear = (node, targetPool) => {
            return targetPool.some(
              (target) =>
                Math.hypot(target.x - node.x, target.y - node.y) <=
                maxConnectDist
            );
          };

          const verifiedNodes = [];

          unverifiedNodes.forEach((node) => {
            let isValid = false;

            // Rule 1: Limbs MUST touch a Joint, another Limb, or an Extremity
            if (node.category === "limb") {
              if (
                isNear(node, pools.joint) ||
                isNear(node, pools.extremity) ||
                isNear(node, pools.limb)
              )
                isValid = true;
            }
            // Rule 2: Extremities (Hands/Feet) MUST touch a Limb
            else if (node.category === "extremity") {
              if (isNear(node, pools.limb)) isValid = true;
            }
            // Rule 3: Joints MUST touch a Limb or a Head (neck)
            else if (node.category === "joint") {
              if (isNear(node, pools.limb) || isNear(node, pools.head))
                isValid = true;
            }
            // Rule 4: Facial Features MUST touch another Face feature or a Head Macro
            else if (node.category === "face") {
              if (
                isNear(node, pools.head) ||
                pools.face.filter(
                  (f) =>
                    f !== node &&
                    Math.hypot(f.x - node.x, f.y - node.y) <= maxConnectDist
                ).length > 0
              )
                isValid = true;
            }
            // Rule 5: Head Macros MUST touch a Joint (Neck/Shoulder) or Facial Feature
            else if (node.category === "head") {
              if (isNear(node, pools.joint) || isNear(node, pools.face))
                isValid = true;
            }

            if (isValid) verifiedNodes.push(node);
          });

          // --- 5. DRAW ONLY VERIFIED BOUNDARIES ---
          ctx.lineWidth = 1.5;
          const s = stride * 0.8;

          verifiedNodes.forEach((node) => {
            ctx.strokeStyle = node.color;
            ctx.fillStyle = node.color;

            ctx.beginPath();
            ctx.rect(node.x - s, node.y - s, s * 2, s * 2);
            ctx.stroke();

            ctx.fillRect(node.x - 1, node.y - 1, 3, 3);
            ctx.font = "bold 10px monospace";
            ctx.fillText(node.label, node.x + s + 3, node.y + 4);
          });
        });
        baseTensor.dispose();
      }

      // ==========================================
      // NEW: ANATOMY TRACE KIT (No Squares, Segmented Traces Only)
      // Enforces strict anatomical connection rules before drawing a trace.
      // ==========================================

      // ==========================================
      // FIXED: ANATOMY TRACE KIT (Zero-Balanced Kernels & Local Maxima)
      // Segmented bone traces with strict math to prevent background flak
      // ==========================================
      // ==========================================
      // FIXED: ANATOMY TRACE KIT (Human Presence - Plus Signs Only)
      // Cleaned up unused kernels. Draws plus signs for limbs/extremities. No labels.
      // Erases left-edge padding artifacts using a structural margin.
      // ==========================================
      // ==========================================
      // FIXED: HUMAN PRESENCE KIT (Plus Signs Only)
      // ==========================================
      if (activeKit === "humanpresence") {
        // <--- CHANGED FROM "anatomytrace"
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // ... (Keep the rest of the exact same code we just fixed!)

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [r, g, b] = tf.split(normalized, 3, 2);
          const luma = r
            .mul(0.299)
            .add(g.mul(0.587))
            .add(b.mul(0.114))
            .squeeze();
          const expandedLuma = luma.expandDims(0).expandDims(-1);

          // 1. EXACT ZERO-SUM MACRO-KERNELS (Only Limbs & Extremities)
          const kYsplit = tf.tensor4d(
            [
              1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, -6, 0, 0, 0, 0, 1, 0, 0, 0, 0,
              1, 0, 0,
            ],
            [5, 5, 1, 1]
          );

          const kFemur = tf.tensor4d(
            [
              -2, 1, 2, 1, -2, -2, 1, 2, 1, -2, -2, 1, 2, 1, -2, -2, 1, 2, 1,
              -2, -2, 1, 2, 1, -2,
            ],
            [5, 5, 1, 1]
          );

          const kCalf = tf.tensor4d(
            [
              -2, 1, 2, 1, -2, -1.5, 0.5, 2, 0.5, -1.5, -1, 0, 2, 0, -1, -0.5,
              -0.5, 2, -0.5, -0.5, 0, -1, 2, -1, 0,
            ],
            [5, 5, 1, 1]
          );

          const kHumerus = tf.tensor4d(
            [
              -1.5, -1, 5, -1, -1.5, -1.5, -1, 5, -1, -1.5, -1.5, -1, 5, -1,
              -1.5, -1.5, -1, 5, -1, -1.5, -1.5, -1, 5, -1, -1.5,
            ],
            [5, 5, 1, 1]
          );

          const kForearm = tf.tensor4d(
            [
              -1, -2, 6, -2, -1, -1, -2, 6, -2, -1, -1, -2, 6, -2, -1, -1, -2,
              6, -2, -1, -1, -2, 6, -2, -1,
            ],
            [5, 5, 1, 1]
          );

          // 2. RUN STRICT RELU CONVOLUTIONS
          const processConv = (kernel, thresholdOffset) => {
            const edges = tf.conv2d(expandedLuma, kernel, 1, "same").squeeze();
            return tf
              .sigmoid(
                tf
                  .relu(edges.sub(tf.scalar(thresholdOffset)))
                  .mul(tf.scalar(15.0))
              )
              .dataSync();
          };

          const dataMap = {
            extremity: [processConv(kYsplit, 1.5)],
            limb: [
              processConv(kFemur, 2.0),
              processConv(kCalf, 2.0),
              processConv(kHumerus, 3.0),
              processConv(kForearm, 3.5),
            ],
          };

          const w = canvas.width;
          const h_img = canvas.height;
          const stride = Math.max(12, Math.floor(w / 40));
          const maxConnectDist = stride * 3.5;

          const pools = { extremity: [], limb: [] };
          const unverifiedNodes = [];

          // 3. HARVEST UNVERIFIED NODES
          const checkGroup = (groupData, x, y, idx, category, color) => {
            for (let i = 0; i < groupData.length; i++) {
              if (groupData[i][idx] > 0.85) {
                const node = { x, y, category, color };
                pools[category].push(node);
                unverifiedNodes.push(node);
                break;
              }
            }
          };

          // BUG FIX: By starting the loop at 'stride' and ending before 'w - stride',
          // we completely ignore the 0-padded canvas borders, erasing the left-edge artifacts.
          for (let y = stride; y < h_img - stride; y += stride) {
            for (let x = stride; x < w - stride; x += stride) {
              const idx = y * w + x;
              checkGroup(dataMap.extremity, x, y, idx, "extremity", "#ff0088");
              checkGroup(dataMap.limb, x, y, idx, "limb", "#00ffff");
            }
          }

          // 4. SPATIAL VERIFICATION LOGIC (Erases orphaned flak)
          const isNear = (node, targetPool) =>
            targetPool.some(
              (t) => Math.hypot(t.x - node.x, t.y - node.y) <= maxConnectDist
            );

          const verifiedNodes = [];

          unverifiedNodes.forEach((node) => {
            let isValid = false;
            // Limbs must touch an Extremity or another Limb
            if (
              node.category === "limb" &&
              (isNear(node, pools.extremity) || isNear(node, pools.limb))
            )
              isValid = true;
            // Extremities must attach to a Limb
            else if (node.category === "extremity" && isNear(node, pools.limb))
              isValid = true;

            if (isValid) verifiedNodes.push(node);
          });

          // 5. DRAW THE TRACES (Plus signs for Limbs and Extremities ONLY, NO LABELS)
          ctx.lineWidth = 3;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          const len = stride * 0.7;

          verifiedNodes.forEach((node) => {
            ctx.strokeStyle = node.color;
            ctx.fillStyle = node.color;

            ctx.beginPath();

            // Draw a Plus Sign (+)
            ctx.moveTo(node.x - len, node.y);
            ctx.lineTo(node.x + len, node.y);
            ctx.moveTo(node.x, node.y - len);
            ctx.lineTo(node.x, node.y + len);

            ctx.stroke();
          });
        });
        baseTensor.dispose();
      }

      ///////

      // ==========================================
      // FIXED: RAINBOW CONTOUR KIT (Strict Native Color Segregation)
      // Extracts a master topographic map, then sorts lines perfectly into 7 isolated colors.
      // ==========================================
      if (activeKit === "starspirit") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        // ctx.fillStyle = "rgba(0, 2, 8, 0.95)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);

        tf.tidy(() => {
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [rRaw, gRaw, bRaw] = tf.split(normalized, 3, 2);

          // SQUEEZE: Fixes the tensor dimension crash!
          const r = rRaw.squeeze();
          const g = gRaw.squeeze();
          const b = bRaw.squeeze();

          const luma = r.mul(0.299).add(g.mul(0.587)).add(b.mul(0.114));

          // 1. MASTER TOPOGRAPHIC MAP
          // Maps every single shift between dark and light across the entire image
          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();

          // Clean the edges for sharp, solid boundary lines
          const masterEdges = tf.sigmoid(
            tf.relu(edges.sub(tf.scalar(0.08))).mul(tf.scalar(25.0))
          );

          // 2. EXTRACT TO NATIVE JAVASCRIPT FOR LIGHTNING FAST ISOLATION
          const edgeData = masterEdges.dataSync();
          const rData = r.dataSync();
          const gData = g.dataSync();
          const bData = b.dataSync();

          const w = canvas.width;
          const h = canvas.height;
          const stride = Math.max(3, Math.floor(w / 120));
          const maxDist = stride * 2.5;

          // 7 perfectly isolated buckets for the lines
          const pools = {
            red: [],
            orange: [],
            yellow: [],
            green: [],
            cyan: [],
            blue: [],
            violet: [],
          };

          for (let y = 0; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;

              // Only analyze pixels that sit directly on a light/dark topographic border
              if (edgeData[idx] > 0.5) {
                const rv = rData[idx];
                const gv = gData[idx];
                const bv = bData[idx];

                const maxC = Math.max(rv, gv, bv);
                const minC = Math.min(rv, gv, bv);
                const sat = maxC - minC;

                // Only trace boundaries inside heavily saturated colors (ignores grays/blacks/whites)
                if (sat > 0.15) {
                  let pool = null;

                  // Mathematically sort the boundary pixel into 1 of the 7 pure color buckets based on Hue
                  if (maxC === rv) {
                    if (gv > bv && gv > minC + 0.35 * sat) {
                      pool = gv > minC + 0.7 * sat ? "yellow" : "orange";
                    } else if (bv > gv && bv > minC + 0.6 * sat) {
                      pool = "violet";
                    } else {
                      pool = "red";
                    }
                  } else if (maxC === gv) {
                    if (rv > bv && rv > minC + 0.7 * sat) pool = "yellow";
                    else if (bv > rv && bv > minC + 0.6 * sat) pool = "cyan";
                    else pool = "green";
                  } else {
                    if (gv > rv && gv > minC + 0.6 * sat) pool = "cyan";
                    else if (rv > gv && rv > minC + 0.6 * sat) pool = "violet";
                    else pool = "blue";
                  }

                  if (pool) pools[pool].push({ x, y });
                }
              }
            }
          }

          // 3. DRAW THE DISCRETE COLOR CONTOURS
          ctx.lineWidth = 1.5;
          ctx.lineCap = "round";

          // By passing a specific bucket (e.g., Red), the red borders will ONLY draw to other Red borders.
          const drawPool = (points, colorHex) => {
            ctx.strokeStyle = colorHex;
            points.forEach((p1, i) => {
              const lookAhead = Math.min(points.length, i + 25);
              for (let j = i + 1; j < lookAhead; j++) {
                const p2 = points[j];
                if (Math.hypot(p2.x - p1.x, p2.y - p1.y) < maxDist) {
                  ctx.beginPath();
                  ctx.moveTo(p1.x, p1.y);
                  ctx.lineTo(p2.x, p2.y);
                  ctx.stroke();
                }
              }
            });
          };

          drawPool(pools.red, "#ff0000"); // Pure Red
          drawPool(pools.orange, "#ff7700"); // Orange
          drawPool(pools.yellow, "#ffee00"); // Yellow
          drawPool(pools.green, "#00ff00"); // Green
          drawPool(pools.cyan, "#00ffff"); // Light Blue (Cyan)
          drawPool(pools.blue, "#0044ff"); // Deep Blue
          drawPool(pools.violet, "#aa00ff"); // Violet
        });

        baseTensor.dispose();
      }

      // ==========================================
      // NEW: STAR POWER 3D (Topographical Color Mapping on a 3D Cube)
      // ==========================================
      if (activeKit === "cosm") {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const baseTensor = tf.browser.fromPixels(canvas);

        const faceModel =
          faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh;
        const detector = await faceLandmarksDetection.createDetector(
          faceModel,
          {
            runtime: "tfjs",
            refineLandmarks: true,
          }
        );
        const faces = await detector.estimateFaces(canvas);

        tf.tidy(() => {
          // --- 1. EXTRACT STAR SPIRIT TOPOGRAPHY ---
          const normalized = baseTensor.toFloat().div(tf.scalar(255.0));
          const [rRaw, gRaw, bRaw] = tf.split(normalized, 3, 2);
          const r = rRaw.squeeze();
          const g = gRaw.squeeze();
          const b = bRaw.squeeze();
          const luma = r.mul(0.299).add(g.mul(0.587)).add(b.mul(0.114));

          const edgeKernel = tf.tensor4d(
            [0, 1, 0, 1, -4, 1, 0, 1, 0],
            [3, 3, 1, 1]
          );
          const expandedLuma = luma.expandDims(0).expandDims(-1);
          const edges = tf
            .conv2d(expandedLuma, edgeKernel, 1, "same")
            .abs()
            .squeeze();
          const masterEdges = tf.sigmoid(
            tf.relu(edges.sub(tf.scalar(0.08))).mul(tf.scalar(25.0))
          );

          const edgeData = masterEdges.dataSync();
          const rData = r.dataSync();
          const gData = g.dataSync();
          const bData = b.dataSync();
          const w = canvas.width;
          const h = canvas.height;
          const stride = Math.max(3, Math.floor(w / 120));
          const maxDist = stride * 2.5;

          const pools = {
            red: [],
            orange: [],
            yellow: [],
            green: [],
            cyan: [],
            blue: [],
            violet: [],
          };

          for (let y = 0; y < h; y += stride) {
            for (let x = 0; x < w; x += stride) {
              const idx = y * w + x;
              if (edgeData[idx] > 0.5) {
                const rv = rData[idx];
                const gv = gData[idx];
                const bv = bData[idx];
                const maxC = Math.max(rv, gv, bv);
                const minC = Math.min(rv, gv, bv);
                const sat = maxC - minC;

                if (sat > 0.15) {
                  let pool = null;
                  if (maxC === rv) {
                    if (gv > bv && gv > minC + 0.35 * sat)
                      pool = gv > minC + 0.7 * sat ? "yellow" : "orange";
                    else if (bv > gv && bv > minC + 0.6 * sat) pool = "violet";
                    else pool = "red";
                  } else if (maxC === gv) {
                    if (rv > bv && rv > minC + 0.7 * sat) pool = "yellow";
                    else if (bv > rv && bv > minC + 0.6 * sat) pool = "cyan";
                    else pool = "green";
                  } else {
                    if (gv > rv && gv > minC + 0.6 * sat) pool = "cyan";
                    else if (rv > gv && rv > minC + 0.6 * sat) pool = "violet";
                    else pool = "blue";
                  }
                  if (pool) pools[pool].push({ x, y });
                }
              }
            }
          }

          // --- 2. MAP TOPOGRAPHY TO THE 3D CUBE ---
          if (faces.length > 0) {
            faces.forEach((face) => {
              let minX = Infinity,
                maxX = -Infinity,
                minY = Infinity,
                maxY = -Infinity;
              face.keypoints.forEach((kp) => {
                if (kp.x < minX) minX = kp.x;
                if (kp.x > maxX) maxX = kp.x;
                if (kp.y < minY) minY = kp.y;
                if (kp.y > maxY) maxY = kp.y;
              });

              const faceWidth = maxX - minX;
              const faceHeight = maxY - minY;

              // Define the 3D Cube Dimensions (Exactly like portrait3d)
              const boxX = maxX + faceWidth * 0.15;
              const boxY = maxY - faceHeight * 0.2;
              const bW = faceWidth * 0.6;
              const bH = faceHeight * 0.6;
              const depth = canvas.width * 0.05;

              // Fill the box with pure black glass to create the canvas
              ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
              ctx.fillRect(boxX, boxY, bW, bH);

              // Draw the topographical lines ONLY if they fall inside the box constraints
              ctx.lineWidth = 1.0;
              ctx.lineCap = "round";

              const drawConstrainedPool = (points, colorHex) => {
                ctx.strokeStyle = colorHex;
                points.forEach((p1, i) => {
                  // MAPPING CALCULATION: Shrink the global coordinates down into the box coordinates
                  const mappedX = boxX + (p1.x / w) * bW;
                  const mappedY = boxY + (p1.y / h) * bH;

                  // Only draw if the point is actually inside the box geometry
                  if (
                    mappedX > boxX &&
                    mappedX < boxX + bW &&
                    mappedY > boxY &&
                    mappedY < boxY + bH
                  ) {
                    const lookAhead = Math.min(points.length, i + 25);
                    for (let j = i + 1; j < lookAhead; j++) {
                      const p2 = points[j];
                      const m2X = boxX + (p2.x / w) * bW;
                      const m2Y = boxY + (p2.y / h) * bH;

                      if (Math.hypot(p2.x - p1.x, p2.y - p1.y) < maxDist) {
                        ctx.beginPath();
                        ctx.moveTo(mappedX, mappedY);
                        ctx.lineTo(m2X, m2Y);
                        ctx.stroke();
                      }
                    }
                  }
                });
              };

              // Paint the Topography into the cube!
              drawConstrainedPool(pools.red, "#ff0000");
              drawConstrainedPool(pools.orange, "#ff7700");
              drawConstrainedPool(pools.yellow, "#ffee00");
              drawConstrainedPool(pools.green, "#00ff00");
              drawConstrainedPool(pools.cyan, "#00ffff");
              drawConstrainedPool(pools.blue, "#0044ff");
              drawConstrainedPool(pools.violet, "#aa00ff");

              // Draw the 3D Orange Cube Structure around the projection
              ctx.strokeStyle = "#7b00ff";
              ctx.lineWidth = 2.0;

              ctx.strokeRect(boxX + depth, boxY - depth, bW, bH);
              ctx.strokeRect(boxX, boxY, bW, bH);

              ctx.beginPath();
              ctx.moveTo(boxX, boxY);
              ctx.lineTo(boxX + depth, boxY - depth);
              ctx.moveTo(boxX + bW, boxY);
              ctx.lineTo(boxX + bW + depth, boxY - depth);
              ctx.moveTo(boxX, boxY + bH);
              ctx.lineTo(boxX + depth, boxY + bH - depth);
              ctx.moveTo(boxX + bW, boxY + bH);
              ctx.lineTo(boxX + bW + depth, boxY + bH - depth);
              ctx.stroke();
            });
          }
        });
        baseTensor.dispose();
      }

      ////// Bottom of try block for new kits
    } catch (err) {
      console.error("Tensor Execution Fault:", err);
    } finally {
      setIsDetecting(false);

      if (frameRef.current && canvasRef.current && !imageError) {
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = canvasRef.current.width;
        tempCanvas.height = canvasRef.current.height;
        const tempCtx = srgb2d(tempCanvas);
        tempCtx.drawImage(frameRef.current, 0, 0);
        tempCtx.drawImage(canvasRef.current, 0, 0);
        setMergedImgUrl(tempCanvas.toDataURL("image/png"));
      }

      onProcessed?.(canvasRef.current.toDataURL("image/png"));
    }
  };

  useEffect(() => {
    setMergedImgUrl(null);
    drawCanvas();
  }, [activeKit, frameReady]);

  return (
    <div
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        height: "100%",
      }}
    >
      {/* 1. Base Original Image */}
      {/* 1. Base Original Image (Fed by the Secure Blob) */}
      {secureBlobUrl && (
        <img
          ref={imgRef}
          src={secureBlobUrl}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: "8px",
            display: imageError ? "none" : "block",
          }}
        />
      )}

      {/* {imageError && (
        <div
          style={{
            padding: "20px",
            background: "#ffebee",
            color: "red",
            borderRadius: "8px",
            height: "100%",
            display: "flex",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          ⚠️ Image blocked by CORS Cache. Open DevTools, check "Disable cache",
          and refresh.
        </div>
      )} */}

      {/* 2. Tensor Canvas - Rendered invisibly behind the merged image to save resources once complete */}
      {!imageError && (
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            borderRadius: "8px",
            opacity: mergedImgUrl ? 0 : 1, // Hide the live canvas once the native image takes over
          }}
        />
      )}

      {/* 3. NEW: The Merged Native Image Overlay. 
          Because this is a standard <img> tag sitting perfectly on top, 
          holding it down on an iPhone will trigger the native "Save Photo" menu! */}
      {mergedImgUrl && !isDetecting && !imageError && (
        <img
          src={mergedImgUrl}
          alt="Merged Wireframe"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            borderRadius: "8px",
            zIndex: 10,
            pointerEvents: "auto",
          }}
        />
      )}

      {/* Loading Overlay */}
      {isDetecting && !imageError && (
        <div
          style={{
            position: "absolute",
            top: 15,
            left: 15,
            background: "white",
            color: "blue",
            padding: "6px 12px",
            borderRadius: "4px",
            fontSize: "13px",
            fontFamily: "monospace",
            fontWeight: "bold",
            border: "1px solid blue",
            zIndex: 20,
          }}
        >
          COMPUTING Ocancerz ...
        </div>
      )}
    </div>
  );
};

export default BubbleDisplayImg;
